import React, { useEffect, useRef, useState, useCallback, useMemo } from 'react';
import { createPortal } from 'react-dom';
import axios from 'axios';
import './NegociacaoWizard.css';
import './NegociacaoVeiculosResumo.css';
import './ChecklistMobileTabs.css';
import API_CONFIG from './config/api';
import { formatarDataInteligente } from './utils/formatarData';
import { classificarPromissorias } from './utils/promissoriasNegociacao';
import { imprimirImagem } from './utils/imprimirImagem';

const DocumentoScanner = React.lazy(() => import('./DocumentoScanner'));

const DOCUMENTOS_IMPRESSAO = [
  { id: 'folha-rosto', nome: 'Folha de rosto' },
  { id: 'checklist', nome: 'Checklist da negociação' },
  { id: 'termo-imagem-voz', nome: 'Termo de uso de imagem e voz' },
  { id: 'avaliacao-entrega', nome: 'Avaliação de entrega do veículo' },
  { id: 'avaliacao-painel', nome: 'Avaliação do painel de instrumentos' }
];

const ResumoVeiculosNegociacao = ({ saidas, entradas }) => {
  const renderVeiculos = (veiculos, entrada) => veiculos.map((veiculo, index) => (
    <div className="neg-veiculo-resumo-item" key={`${entrada ? 'entrada' : 'saida'}-${veiculo.placa}-${index}`}>
      <span className="neg-veiculo-direcao" role="img" aria-label={entrada ? 'Entrada na troca' : 'Saída: veículo vendido'} title={entrada ? 'Entrada na troca' : 'Saída: veículo vendido'}>
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
          <path d={entrada ? 'M4 12h16m-6-6 6 6-6 6' : 'M20 12H4m6-6-6 6 6 6'} />
        </svg>
      </span>
      <span className="neg-veiculo-resumo-nome">
        {veiculo.nome}
        {veiculo.placa && <span className="neg-veiculo-placa-texto" aria-label={`Placa ${veiculo.placa}`}> · {veiculo.placa}</span>}
      </span>
    </div>
  ));

  if (!saidas.length && !entradas.length) return <div className="neg-veiculos-resumo-vazio">Sem veículo vendido</div>;

  return (
    <div className="neg-veiculos-resumo">
      {renderVeiculos(saidas, false)}
      {renderVeiculos(entradas, true)}
    </div>
  );
};

const IconePromissoriaNegociacao = ({ futura = false, titulo }) => (
  <span role="img" aria-label={titulo} title={titulo} data-promissoria-periodo={futura ? 'posteriores' : 'cinza'} style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: '30px', height: '30px', borderRadius: '8px', background: futura ? '#f0fdf4' : '#f1f5f9', color: futura ? '#15803d' : '#64748b', border: `1px solid ${futura ? '#86efac' : '#cbd5e1'}`, flexShrink: 0 }}>
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M14 3H6a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V9z" />
      <path d="M14 3v6h6" />
      {futura ? (
        <text x="12" y="18" textAnchor="middle" fill="currentColor" stroke="none" fontFamily="Arial, sans-serif" fontSize="12" fontWeight="700">$</text>
      ) : (
        <path d="M8 7h2M8 12h8M8 16v2m3-2v2m2-2v2m3-2v2" />
      )}
    </svg>
  </span>
);

const formatarDataCurtaEnvio = (valor) => {
  if (!valor) return 'xx/xx/xx';
  const d = new Date(valor);
  if (isNaN(d.getTime())) return 'xx/xx/xx';
  return d.toLocaleDateString('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: '2-digit'
  });
};

const obterDataCalendarioISO = (valor) => {
  if (!valor) return '';
  if (typeof valor === 'string') {
    const match = valor.match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (match) return `${match[1]}-${match[2]}-${match[3]}`;
  }
  const data = new Date(valor);
  if (Number.isNaN(data.getTime())) return '';
  return `${data.getFullYear()}-${String(data.getMonth() + 1).padStart(2, '0')}-${String(data.getDate()).padStart(2, '0')}`;
};

const obterMesCalendario = (valor) => obterDataCalendarioISO(valor).slice(0, 7);

const obterTimestampCalendario = (valor) => {
  const iso = obterDataCalendarioISO(valor);
  if (!iso) return 0;
  const [ano, mes, dia] = iso.split('-').map(Number);
  return Date.UTC(ano, mes - 1, dia);
};

const RASCUNHO_KEY = 'negociacaoRascunho_v1';

const VENDEDOR_TODOS = 'TODOS';

const obterMesAtualFiltro = () => {
  const hoje = new Date();
  return `${hoje.getFullYear()}-${String(hoje.getMonth() + 1).padStart(2, '0')}`;
};

const formatarMoeda = (raw) => {
  const digits = raw.replace(/\D/g, '');
  if (!digits) return '';
  const num = parseInt(digits, 10);
  const reais = Math.floor(num / 100);
  const centavos = num % 100;
  return `R$ ${reais.toLocaleString('pt-BR')},${String(centavos).padStart(2, '0')}`;
};

const parseMoeda = (str) => {
  if (!str) return 0;
  const digits = str.replace(/\D/g, '');
  return parseInt(digits || '0', 10) / 100;
};

const exibirMoeda = (val) =>
  val.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });

const formatarDataCurta = (valor) => {
  if (!valor) return '—';
  const iso = obterDataCalendarioISO(valor);
  if (!iso) return '—';
  const [ano, mes, dia] = iso.split('-').map(Number);
  return new Date(ano, mes - 1, dia).toLocaleDateString('pt-BR', {
    day: '2-digit',
    month: 'short',
    year: 'numeric'
  });
};

const getStatusVisual = (status) => {
  const normalized = `${status || ''}`.trim().toLowerCase();

  if (normalized === 'pronto') {
    return {
      label: 'Pronta',
      background: 'linear-gradient(135deg, #dcfce7 0%, #bbf7d0 100%)',
      color: '#166534',
      border: '#86efac'
    };
  }

  if (normalized === 'pendente') {
    return {
      label: 'Pendente',
      background: 'linear-gradient(135deg, #fef3c7 0%, #fde68a 100%)',
      color: '#92400e',
      border: '#fcd34d'
    };
  }

  if (normalized === 'cancelada') {
    return {
      label: 'Cancelada',
      background: 'linear-gradient(135deg, #fee2e2 0%, #fecaca 100%)',
      color: '#b91c1c',
      border: '#fca5a5'
    };
  }

  return {
    label: status ? String(status) : 'Sem status',
    background: 'linear-gradient(135deg, #e2e8f0 0%, #cbd5e1 100%)',
    color: '#334155',
    border: '#cbd5e1'
  };
};

const estadoInicial = {
  vendedor: '',
  loja: '',
  data: '',
  origemVenda: '',
  origemVendaDetalhe: '',
  participantes: [],
  nome: '',
  cpf: '',
  rg: '',
  profissao: '',
  estadoCivil: '',
  cep: '',
  rua: '',
  numero: '',
  bairro: '',
  cidadeEstado: '',
  telefone: ''
};

const veiculoVendidoVazio = (id) => ({
  id,
  marca: '', modelo: '', ano: '', cor: '', placa: '', km: '',
  temManual: '-', temChaveReserva: '-',
  valorVenda: ''
});

const subFormVazio = () => ({
  marca: '', modelo: '', ano: '', cor: '', placa: '', km: '',
  temManual: '-', temChaveReserva: '-',
  _busca: '', _isManual: false, _mostrarOpcoes: false, _precoVenda: ''
});

const veiculoTrocaVazio = (id) => ({
  id,
  entrada_id: crypto.randomUUID(),
  placa: '', modelo: '', cor: '', km: '',
  anoFabricacao: '', anoModelo: '',
  renavam: '', chassis: '',
  temManual: '-', temChaveReserva: '-',
  valorQuitacao: '', instituicaoQuitacao: '', valorDebitos: '',
  valorTroca: ''
});

const produtosAgregadosPadrao = [
  {
    tipo: 'transferencia_propriedade',
    nome: 'Transferência de propriedade',
    valor: 'R$ 950,00',
    exigeVeiculo: false
  },
  {
    tipo: 'vistoria_transferencia',
    nome: 'Vistoria de transferência',
    valor: 'R$ 120,00',
    exigeVeiculo: true
  },
  {
    tipo: 'outros',
    nome: 'Outros',
    valor: '',
    exigeVeiculo: false
  }
];

const produtoAgregadoVazio = (id) => ({
  id,
  tipo: 'transferencia_propriedade',
  nome: 'Transferência de propriedade',
  valor: 'R$ 950,00',
  veiculo_tipo: '',
  veiculo_placa: '',
  veiculo_label: ''
});

const subFormTrocaVazio = () => ({
  placa: '', modelo: '', cor: '', km: '',
  anoFabricacao: '', anoModelo: '',
  renavam: '', chassis: '',
  temManual: '-', temChaveReserva: '-',
  valorQuitacao: '', instituicaoQuitacao: '', valorDebitos: ''
});

const sections = [
  { title: 'Cliente', description: 'Comece pelo CPF. Buscamos os dados disponíveis para você.' },
  { title: 'Veículo de venda', description: 'Selecione no estoque ou consulte pela placa.' },
  { title: 'Veículo de entrada', description: 'Há um veículo entrando na negociação? Adicione aqui. Se não, continue.' },
  { title: 'Adicionais', description: 'Inclua os serviços e produtos combinados com o cliente. Esta etapa é opcional.' },
  { title: 'Acerto financeiro', description: 'Organize os recebimentos e pagamentos e confira o saldo.' },
  { title: 'Revisão', description: 'Confira o que foi combinado. Você pode voltar e ajustar qualquer informação.' }
];
const partesCliente = [
  { title: 'Quem está negociando?', description: 'Consulte o CPF e complete apenas o que faltar.' },
  { title: 'Onde o cliente mora?', description: 'Use o CEP para preencher o endereço e confira o número.' },
  { title: 'Sobre esta negociação', description: 'Defina o responsável, a loja e como o cliente chegou até você.' }
];


const hojeLocalISO = () => {
  const d = new Date();
  const offset = d.getTimezoneOffset();
  return new Date(d.getTime() - offset * 60000).toISOString().slice(0, 10);
};

const ITEM_CNH_RG = 'CNH / RG';
const ITEM_CNH_RG_ANTIGO = 'CNH / RG (Frente e verso)';
const ITEM_RECIBO_TRANSFERENCIA = 'Recibo de transferência';
const ITEM_TERMO_USO_IMAGEM_VOZ = 'Termo de uso de imagem e voz';
const ITEM_AVALIACAO_ENTREGA = 'Avaliação de entrega';
const ITEM_AVALIACAO_GASTOS_PREVISTOS = 'Avaliação de gastos previstos';
const CATEGORIA_ACERTO_FINANCEIRO = 'ACERTO FINANCEIRO';
const ABAS_CHECKLIST_MOBILE = [
  { id: 'cliente', rotulo: 'Cliente', titulo: 'Documentos do cliente', vazio: 'Nenhum documento do cliente nesta negociação.' },
  { id: 'entrada', rotulo: 'Entrada', titulo: 'Veículo entrando', vazio: 'Nenhum veículo entrando nesta negociação.' },
  { id: 'saida', rotulo: 'Saída', titulo: 'Veículo saindo', vazio: 'Nenhum veículo saindo nesta negociação.' },
  { id: 'financeiro', rotulo: 'Financeiro', titulo: 'Acerto financeiro', vazio: 'Nenhum item de acerto financeiro nesta negociação.' }
];

const abaDoItemChecklist = (item) => {
  const categoria = `${item.categoria || ''}`.trim();
  if (item.tipo_cat === 'entrando' || categoria.startsWith('->')) return 'entrada';
  if (item.tipo_cat === 'saindo' || categoria.startsWith('<-')) return 'saida';
  if (categoria.toUpperCase() === 'DOCUMENTOS DO CLIENTE') return 'cliente';
  return 'financeiro';
};

const normalizarTextoChecklist = (valor = '') => `${valor || ''}`
  .normalize('NFD')
  .replace(/[\u0300-\u036f]/g, '')
  .toLowerCase()
  .trim();

const isItemChecklistOcultoNaVisualizacao = (item = {}) => {
  const nome = normalizarTextoChecklist(item.nome);
  return nome === 'previsao de gastos' || nome === 'avaliacao de gastos previstos';
};

const filtrarChecklistVisivel = (items = []) =>
  (items || []).filter((item) => !isItemChecklistOcultoNaVisualizacao(item));

const isItemAcertoDinamico = (id = '') => /^acerto_(recebimento|pagamento)_\d+_/i.test(`${id || ''}`);

const mesmoItemChecklist = (item = {}, itemId = '', itemMeta = null) => {
  const normalizarId = valor => normalizarTextoChecklist(valor).replace(/[^a-z0-9]+/g, '_');
  if (normalizarId(item?.id) === normalizarId(itemId) || `${item?._id || ''}` === `${itemId || ''}`) {
    return true;
  }
  if (isItemAcertoDinamico(itemId) || isItemAcertoDinamico(item?.id)) {
    if (normalizarId(itemId) === normalizarId(item?.id)) return true;
    return false;
  }
  if (!itemMeta) return false;

  const mesmoNome = normalizarTextoChecklist(item.nome) === normalizarTextoChecklist(itemMeta.nome);
  const mesmaCategoria = normalizarTextoChecklist(item.categoria) === normalizarTextoChecklist(itemMeta.categoria);
  const placaItem = `${item.veiculo_placa || ''}`.trim().toUpperCase();
  const placaMeta = `${itemMeta.veiculo_placa || ''}`.trim().toUpperCase();

  return mesmoNome && mesmaCategoria && (!placaMeta || placaItem === placaMeta);
};

const documentacaoEstaAtiva = (neg = {}) => neg?.documentacao_iniciada === true;

const formatarOrigemVenda = (origem = '', detalhe = '') => {
  const base = `${origem || ''}`.trim();
  const extra = `${detalhe || ''}`.trim();
  return base === 'Web' && extra ? `Web > ${extra}` : base;
};

const obterValorRecebimentoAcerto = (recebimento = {}) => {
  const tipo = `${recebimento?.tipo || ''}`.trim().toLowerCase();
  if (tipo === 'cartão de crédito') {
    return parseMoeda(recebimento?.liquido_cartao || recebimento?.valor || '');
  }
  return parseMoeda(recebimento?.valor || '');
};

const calcularSaldoFinanceiroNegociacao = (neg = {}) => {
  const totalVendidos = (neg.veiculos_vendidos || []).reduce((s, v) => s + parseMoeda(v.valorVenda), 0);
  const totalTrocas = (neg.veiculos_troca || []).reduce((s, t) => s + parseMoeda(t.valorTroca) - parseMoeda(t.valorQuitacao) - parseMoeda(t.valorDebitos), 0);
  const totalProdutosAgregados = (neg.produtos_agregados || []).reduce((s, p) => s + parseMoeda(p.valor), 0);
  const saldo = totalVendidos + totalProdutosAgregados - totalTrocas;
  const totalPago = (neg.pagamentos || []).reduce((s, p) => s + parseMoeda(p.valor), 0);
  const totalRecebido = (neg.recebimentos || []).reduce((s, r) => s + obterValorRecebimentoAcerto(r), 0);
  const saldoFinal = saldo - totalRecebido + totalPago;

  return {
    totalVendidos,
    totalTrocas,
    totalProdutosAgregados,
    totalPago,
    totalRecebido,
    saldoFinal
  };
};

const formatarTipoComprovante = (tipo = '') => {
  const mapa = {
    'cartão de crédito': 'CARTÃO DE CRÉDITO',
    'cartão de debito': 'CARTÃO DE DÉBITO',
    'cartão de débito': 'CARTÃO DE DÉBITO',
    'nota promissória': 'NOTA PROMISSÓRIA'
  };

  const chave = `${tipo || ''}`.trim().toLowerCase();
  return mapa[chave] || `${tipo || ''}`.trim().toUpperCase();
};

const isChecklistItemCompleto = (item) => {
  if (!item) return false;
  if (item.concluido_manualmente === true) return true;

  if (item.checagem_automatica === 'acerto_financeiro') {
    return Math.abs(Number(item.saldo_final || 0)) < 0.005;
  }

  if (item.nome === ITEM_CNH_RG) {
    return !!(item.arquivo_frente_url && item.arquivo_verso_url);
  }

  if (item.nome === ITEM_CNH_RG_ANTIGO) {
    return !!(item.arquivo_frente_url && item.arquivo_verso_url) || !!(item.arquivo_url && item.arquivo_verso_url);
  }

  if (item.nome === ITEM_RECIBO_TRANSFERENCIA) {
    if (item.tipo_recibo === 'Digital') return true;
    if (item.tipo_recibo === 'Físico') return !!item.arquivo_url;
    return false;
  }

  return !!item.arquivo_url;
};

const calcularProgressoChecklist = (items) => {
  const total = (items || []).length;
  const anexados = (items || []).filter(isChecklistItemCompleto).length;
  const completo = total > 0 && anexados >= total;
  const percentual = total > 0 ? Math.round((anexados / total) * 100) : 0;
  return { total, anexados, completo, percentual };
};

const obterResumoChecklist = (neg) => {
  const base = mesclarChecklist(neg?.checklist, gerarChecklist(neg || {}));
  const { total, anexados, completo, percentual } = calcularProgressoChecklist(filtrarChecklistVisivel(base));
  return { total, anexados, completo, percentual };
};

// ─── Gerador de Checklist ────────────────────────────────────────────────────
const gerarChecklist = (neg) => {
  const items = [];
  const add = (categoria, nome, extra = {}) =>
    items.push({ id: `${categoria}_${nome}`.replace(/\s+/g, '_'), categoria, nome, arquivo_url: null, arquivo_nome: null, ...extra });

  ['Contrato de compra e venda', ITEM_CNH_RG, 'Comprovante de endereço', 'Foto segurando documento', ITEM_TERMO_USO_IMAGEM_VOZ]
    .forEach(n => add('DOCUMENTOS DO CLIENTE', n));

  (neg.veiculos_vendidos || []).forEach(v => {
    const cat = `<- VEICULO SAINDO DO ESTOQUE: ${v.marca || ''} ${v.modelo || ''} ${v.ano || ''} • ${v.placa || ''}`.trim();
    ['DUAL do veículo', 'Foto KM no painel', 'Vistoria cautelar', ITEM_AVALIACAO_ENTREGA, 'Avaliação do painel de instrumentos']
      .forEach(n => add(cat, n, { tipo_cat: 'saindo', veiculo_placa: v.placa, id: `saindo_${v.placa}_${n}`.replace(/\s+/g,'_') }));
  });

  (neg.veiculos_troca || []).forEach(t => {
    const cat = `-> VEICULO ENTRANDO NO ESTOQUE: ${t.modelo || ''} ${t.anoFabricacao || ''} • ${t.placa || ''}`.trim();
    [
      { nome: 'DUAL do veículo' },
      { nome: ITEM_RECIBO_TRANSFERENCIA, tipo_recibo: '' },
      { nome: 'Foto KM no painel' },
      { nome: 'Vistoria cautelar' },
      { nome: ITEM_AVALIACAO_GASTOS_PREVISTOS },
      { nome: 'Pesquisa de débitos' },
      { nome: 'Procuração do proprietário' },
    ].forEach(({ nome, tipo_recibo }) => add(cat, nome, { tipo_cat: 'entrando', veiculo_placa: t.placa, id: `entrando_${t.placa}_${nome}`.replace(/\s+/g,'_'), ...(tipo_recibo !== undefined ? { tipo_recibo } : {}) }));
  });

  const { saldoFinal } = calcularSaldoFinanceiroNegociacao(neg);
  const saldoFinalZerado = Math.abs(saldoFinal) < 0.005;
  add(
    CATEGORIA_ACERTO_FINANCEIRO,
    saldoFinalZerado
      ? 'Acerto financeiro zerado'
      : `Checagem automática: acerto pendente (${exibirMoeda(Math.abs(saldoFinal))} ${saldoFinal > 0 ? 'a receber' : 'a pagar'})`,
    {
      id: 'acerto_financeiro_checagem_saldo',
      checagem_automatica: 'acerto_financeiro',
      saldo_final: saldoFinal
    }
  );

  const lancamentosAcerto = [
    ...(neg.recebimentos || []).map((lanc, idx) => ({ ...lanc, _origem: 'recebimento', _idx: idx })),
    ...(neg.pagamentos || []).map((lanc, idx) => ({ ...lanc, _origem: 'pagamento', _idx: idx }))
  ];

  lancamentosAcerto
    .filter((lanc) => {
      if (lanc._origem === 'recebimento') {
        return obterValorRecebimentoAcerto(lanc) > 0;
      }
      return parseMoeda(lanc.valor) > 0;
    })
    .forEach((lanc) => {
      const valorNumerico = lanc._origem === 'recebimento'
        ? obterValorRecebimentoAcerto(lanc)
        : parseMoeda(lanc.valor);
      const valorLancamento = exibirMoeda(valorNumerico);
      const tipoNormalizado = `${lanc.tipo || ''}`.trim().toLowerCase();

      if (tipoNormalizado === 'financiamento') {
        const banco = `${lanc.banco || ''}`.trim();
        add(
          CATEGORIA_ACERTO_FINANCEIRO,
          `C.E.T FINANCIAMENTO ${banco ? `Banco ${banco}` : 'Banco não informado'} ${valorLancamento}`,
          {
            id: `acerto_${lanc._origem}_${lanc._idx}_financiamento`
          }
        );
        return;
      }

      add(
        CATEGORIA_ACERTO_FINANCEIRO,
        `Comprovante ${formatarTipoComprovante(lanc.tipo)} ${valorLancamento}`,
        {
          id: `acerto_${lanc._origem}_${lanc._idx}_${formatarTipoComprovante(lanc.tipo).replace(/\s+/g, '_')}`
        }
      );
    });

  return items;
};

// ─── Mesclar checklist salvo com novo (preserva uploads) ────────────────────
const mesclarChecklist = (salvo, gerado) => {
  const mapa = {};
  const chaveItem = valor => normalizarTextoChecklist(valor).replace(/[^a-z0-9]+/g, '_');
  (salvo || []).forEach(i => { if (i?.id) mapa[chaveItem(i.id)] = i; });
  return gerado.map(item => mapa[chaveItem(item.id)] ? {
    ...item,
    concluido_manualmente: mapa[chaveItem(item.id)].concluido_manualmente === true,
    confirmacao_manual: mapa[chaveItem(item.id)].confirmacao_manual,
    arquivo_url: mapa[chaveItem(item.id)].arquivo_url,
    arquivo_nome: mapa[chaveItem(item.id)].arquivo_nome,
    arquivo_frente_url: mapa[chaveItem(item.id)].arquivo_frente_url || (item.nome === ITEM_CNH_RG ? (mapa[chaveItem(item.id)].arquivo_url || null) : null),
    arquivo_frente_nome: mapa[chaveItem(item.id)].arquivo_frente_nome || (item.nome === ITEM_CNH_RG ? (mapa[chaveItem(item.id)].arquivo_nome || null) : null),
    arquivo_verso_url: mapa[chaveItem(item.id)].arquivo_verso_url || null,
    arquivo_verso_nome: mapa[chaveItem(item.id)].arquivo_verso_nome || null,
    tipo_recibo: mapa[chaveItem(item.id)].tipo_recibo ?? item.tipo_recibo,
    documento_gerado_id: mapa[chaveItem(item.id)].documento_gerado_id ?? null,
    documento_gerado_tipo: mapa[chaveItem(item.id)].documento_gerado_tipo ?? null,
    documento_gerado_em: mapa[chaveItem(item.id)].documento_gerado_em ?? null
  } : (() => {
    const idAntigo = item.id.replace(ITEM_CNH_RG.replace(/\s+/g, '_'), ITEM_CNH_RG_ANTIGO.replace(/\s+/g, '_'));
    const itemAntigo = mapa[chaveItem(idAntigo)];
    if (item.nome === ITEM_CNH_RG && itemAntigo) {
      return {
        ...item,
        concluido_manualmente: itemAntigo.concluido_manualmente === true,
        confirmacao_manual: itemAntigo.confirmacao_manual,
        arquivo_url: itemAntigo.arquivo_url,
        arquivo_nome: itemAntigo.arquivo_nome,
        arquivo_frente_url: itemAntigo.arquivo_frente_url || itemAntigo.arquivo_url || null,
        arquivo_frente_nome: itemAntigo.arquivo_frente_nome || itemAntigo.arquivo_nome || null,
        arquivo_verso_url: itemAntigo.arquivo_verso_url || null,
        arquivo_verso_nome: itemAntigo.arquivo_verso_nome || null,
        tipo_recibo: itemAntigo.tipo_recibo ?? item.tipo_recibo,
        documento_gerado_id: itemAntigo.documento_gerado_id ?? null,
        documento_gerado_tipo: itemAntigo.documento_gerado_tipo ?? null,
        documento_gerado_em: itemAntigo.documento_gerado_em ?? null,
        documentos_gerados: itemAntigo.documentos_gerados || []
      };
    }
    return item;
  })());
};

// ─── ChecklistModal ───────────────────────────────────────────────────────────
const ChecklistModal = ({ neg, onClose, onChecklistUpdated }) => {
  const [confirmandoItem, setConfirmandoItem] = useState('');
  const [confirmacaoManualModal, setConfirmacaoManualModal] = useState(null);
  const confirmarItemManual = async (item, observacao = '') => {
    const concluido = !item.concluido_manualmente;
    if (!concluido && !window.confirm(`Remover a confirmação manual de "${item.nome}"? Anexos existentes continuam válidos.`)) return;
    if (concluido && !`${observacao}`.trim()) return;
    setConfirmandoItem(item.id);
    try {
      const r = await axios.post(`${API_CONFIG.API_URL}/negociacoes/${neg._id}/checklist/confirmar-manual`, { item_id: item.id, concluido, confirmado: true, observacao: `${observacao}`.trim() }, { headers: { 'x-auth-token': localStorage.getItem('token') } });
      if (!r.data.success) throw new Error('Confirmação não salva.');
      setItems(atual => atual.map(i => i.id === item.id ? { ...i, concluido_manualmente: r.data.item.concluido_manualmente, confirmacao_manual: r.data.item.confirmacao_manual } : i));
      setConfirmacaoManualModal(null);
    } catch (e) { alert(e.response?.data?.message || e.message); }
    finally { setConfirmandoItem(''); }
  };
  const [isChecklistMobile, setIsChecklistMobile] = useState(() => {
    if (typeof window === 'undefined') return false;
    return window.innerWidth <= 768;
  });
  const [items, setItems] = useState(() => mesclarChecklist(neg.checklist, gerarChecklist(neg)));
  const [salvando, setSalvando] = useState(false);
  const [gerandoDocumentoId, setGerandoDocumentoId] = useState(null);
  const [excluindoProcuracaoId, setExcluindoProcuracaoId] = useState('');
  const [painelProcuracoesItem, setPainelProcuracoesItem] = useState(null);
  const [versoesProcuracoes, setVersoesProcuracoes] = useState([]);
  const [carregandoProcuracoes, setCarregandoProcuracoes] = useState(false);
  const [erroProcuracoes, setErroProcuracoes] = useState('');
  const [uploadProgress, setUploadProgress] = useState({});
  const [arquivoPreview, setArquivoPreview] = useState(null);
  const [documentoScanner, setDocumentoScanner] = useState(null);
  const [menuAnexo, setMenuAnexo] = useState(null);
  const menuAnexoRef = useRef(null);
  const acionadorMenuAnexoRef = useRef(null);
  const [buscandoCepOutroProprietario, setBuscandoCepOutroProprietario] = useState(false);
  const [categoriasChecklistExpandidas, setCategoriasChecklistExpandidas] = useState({});
  const [abaChecklistMobile, setAbaChecklistMobile] = useState('cliente');
  const conteudoChecklistRef = useRef(null);
  const [procuracaoModal, setProcuracaoModal] = useState({
    aberto: false,
    item: null,
    veiculoPlaca: '',
    proprietarioTipo: 'cliente',
    outroProprietario: {
      nome: '',
      cpf: '',
      rg: '',
      endereco: '',
      bairro: '',
      cep: '',
      cidadeEstado: ''
    }
  });
  const token = localStorage.getItem('token');
  const veiculoPrincipal = (neg.veiculos_vendidos || [])[0];
  const resumoVeiculo = veiculoPrincipal
    ? [veiculoPrincipal.marca, veiculoPrincipal.modelo, veiculoPrincipal.ano, veiculoPrincipal.placa].filter(Boolean).join(' · ')
    : 'Sem veículo vendido';
  const ITEM_AVALIACAO_PAINEL = 'Avaliação do painel de instrumentos';
  const ITENS_GERACAO_VENDA = new Set([ITEM_AVALIACAO_ENTREGA, ITEM_AVALIACAO_GASTOS_PREVISTOS]);
  const ITEM_GERACAO_PROCURACAO = 'Procuração do proprietário';

  useEffect(() => {
    const handleResizeChecklist = () => setIsChecklistMobile(window.innerWidth <= 768);
    handleResizeChecklist();
    window.addEventListener('resize', handleResizeChecklist);
    return () => window.removeEventListener('resize', handleResizeChecklist);
  }, []);

  const obterOutroProprietarioVazio = () => ({
    nome: '',
    cpf: '',
    rg: '',
    cep: '',
    endereco: '',
    bairro: '',
    cidadeEstado: ''
  });

  useEffect(() => {
    if (!menuAnexo) return;
    menuAnexoRef.current?.querySelector('button:not(:disabled)')?.focus();
    const fecharFora = e => {
      if (!menuAnexoRef.current?.contains(e.target)) setMenuAnexo(null);
    };
    const fechar = () => setMenuAnexo(null);
    const teclado = e => {
      if (e.key === 'Escape') {
        e.preventDefault();
        setMenuAnexo(null);
        acionadorMenuAnexoRef.current?.focus();
      }
    };
    document.addEventListener('pointerdown', fecharFora);
    document.addEventListener('keydown', teclado);
    window.addEventListener('resize', fechar);
    window.addEventListener('scroll', fechar, true);
    return () => {
      document.removeEventListener('pointerdown', fecharFora);
      document.removeEventListener('keydown', teclado);
      window.removeEventListener('resize', fechar);
      window.removeEventListener('scroll', fechar, true);
    };
  }, [menuAnexo]);

  const abrirMenuAnexo = (e, item, campoUrl = 'arquivo_url', campoNome = 'arquivo_nome') => {
    if (!item[campoUrl]) return;
    e.preventDefault();
    e.stopPropagation();
    acionadorMenuAnexoRef.current = e.currentTarget;
    setMenuAnexo({
      itemId: item.id, campoUrl, campoNome,
      x: Math.max(8, Math.min(e.clientX, window.innerWidth - 220)),
      y: Math.max(8, Math.min(e.clientY, window.innerHeight - 64))
    });
  };

  useEffect(() => {
    setItems(mesclarChecklist(neg.checklist, gerarChecklist(neg)));
  }, [neg._id, neg.checklist, neg.recebimentos, neg.pagamentos, neg.veiculos_vendidos, neg.veiculos_troca]);

  useEffect(() => {
    if (isChecklistMobile && conteudoChecklistRef.current) conteudoChecklistRef.current.scrollTop = 0;
  }, [abaChecklistMobile, isChecklistMobile]);

  const opcoesVeiculoProcuracao = useMemo(() => {
    return (neg.veiculos_troca || []).map((veiculo) => {
      const label = [veiculo.modelo, veiculo.anoFabricacao || veiculo.anoModelo, veiculo.placa]
        .filter(Boolean)
        .join(' • ');
      return {
        placa: veiculo.placa || '',
        label: label || (veiculo.placa || 'Veículo sem placa')
      };
    });
  }, [neg.veiculos_troca]);

  const salvarChecklist = useCallback(async (lista) => {
    try {
      await axios.patch(`${API_CONFIG.API_URL}/negociacoes/${neg._id}/checklist`, { checklist: lista }, { headers: { 'x-auth-token': token } });
    } catch (e) {}
  }, [neg._id, token]);

  // O endpoint de upload cria o item ausente atomicamente. Não salvar o checklist
  // inteiro em um efeito: uma resposta atrasada poderia sobrescrever novos anexos.

  const handleUpload = async (itemId, file, opcoes = {}) => {
    if (documentacaoEstaAtiva(neg)) {
      alert('A documentação já foi iniciada. O checklist e os anexos estão bloqueados.');
      return false;
    }
    if (!file) return false;
    if (file.size > 20 * 1024 * 1024) {
      alert('O arquivo deve ter no máximo 20 MB.');
      return false;
    }
    const {
      campoUrl = 'arquivo_url',
      campoNome = 'arquivo_nome',
      progressKey = itemId,
      itemMeta = null
    } = opcoes;
    const form = new FormData();
    form.append('arquivo', file);
    form.append('item_id', itemId);
    if (itemMeta?.nome) form.append('item_nome', itemMeta.nome);
    if (itemMeta?.categoria) form.append('item_categoria', itemMeta.categoria);
    if (itemMeta?.veiculo_placa) form.append('item_veiculo_placa', itemMeta.veiculo_placa);
    form.append('campo_url', campoUrl);
    form.append('campo_nome', campoNome);
    setSalvando(true);
    setUploadProgress(prev => ({ ...prev, [progressKey]: 0 }));
    let sucesso = false;
    try {
      const res = await axios.post(`${API_CONFIG.API_URL}/negociacoes/${neg._id}/checklist/upload`, form, {
        headers: { 'x-auth-token': token },
        onUploadProgress: (evt) => {
          if (!evt.total) return;
          const pctUpload = Math.max(0, Math.min(100, Math.round((evt.loaded / evt.total) * 100)));
          setUploadProgress(prev => ({ ...prev, [progressKey]: pctUpload }));
        }
      });
      if (!res.data.success || !res.data.arquivo_url) throw new Error('O servidor não confirmou o armazenamento do anexo.');
      if (res.data.success) {
        setUploadProgress(prev => ({ ...prev, [progressKey]: 100 }));
        setItems(prev => {
          return prev.map(i => {
            if (!mesmoItemChecklist(i, itemId, itemMeta)) return i;
            return {
              ...i,
              // A resposta atualiza o anexo, nunca a identidade da linha escolhida.
              [campoUrl]: res.data.arquivo_url,
              [campoNome]: res.data.arquivo_nome
            };
          });
        });
        // A resposta do upload já contém o item persistido. Não recarregar a
        // negociação imediatamente: uma resposta atrasada poderia substituir
        // o estado local e fazer o anexo recém-enviado desaparecer da tela.
        sucesso = true;
      }
    } catch (e) {
      const msg = typeof e.response?.data === 'string'
        ? e.response.data
        : (e.response?.data?.message || e.message);
      const codigo = e.response?.data?.code;
      alert(`${msg || 'Não foi possível anexar o arquivo. Tente novamente.'}${codigo ? `\nCódigo: ${codigo}` : ''}`);
    }
    setTimeout(() => {
      setUploadProgress(prev => {
        const next = { ...prev };
        delete next[progressKey];
        return next;
      });
    }, 400);
    setSalvando(false);
    return sucesso;
  };

  const handleRemover = async (itemId, campoUrl = 'arquivo_url', campoNome = 'arquivo_nome') => {
    if (documentacaoEstaAtiva(neg)) {
      alert('A documentação já foi iniciada. A exclusão de anexos está bloqueada.');
      return;
    }
    try {
      await axios.post(`${API_CONFIG.API_URL}/negociacoes/${neg._id}/checklist/remover-anexo`, {
        item_id: itemId,
        campo_url: campoUrl,
        campo_nome: campoNome
      }, {
        headers: { 'x-auth-token': token }
      });

      setItems(prev => prev.map(i => i.id === itemId ? { ...i, [campoUrl]: null, [campoNome]: null } : i));
      if (onChecklistUpdated) await onChecklistUpdated();
    } catch (e) {
      const msg = typeof e.response?.data === 'string'
        ? e.response.data
        : (e.response?.data?.message || e.message);
      alert(msg || 'Nao foi possivel remover o arquivo. Tente novamente.');
    }
  };

  const handleTipoRecibo = (itemId, tipo) => {
    setItems(prev => {
      const next = prev.map(i => {
        if (i.id !== itemId) return i;
        if (tipo === 'Digital') {
          return { ...i, tipo_recibo: tipo, arquivo_url: null, arquivo_nome: null };
        }
        return { ...i, tipo_recibo: tipo };
      });
      salvarChecklist(next);
      return next;
    });
  };

  const renderBotaoScanner = (item, opcoes = {}) => {
    if (!isChecklistMobile || documentacaoEstaAtiva(neg) || item[opcoes.campoUrl || 'arquivo_url']) return null;
    return <button type="button" title="Fotografar, ajustar os cantos e anexar em PDF" aria-label={`Escanear ${opcoes.titulo || item.nome || 'documento'}`} disabled={salvando} onClick={() => setDocumentoScanner({ item, opcoes })} style={{ display: 'inline-flex', alignItems: 'center', gap: 5, padding: '5px 8px', background: '#fff', color: '#1e40af', border: '1px solid #bfdbfe', borderRadius: 6, fontSize: '0.75rem', fontWeight: 600, cursor: salvando ? 'wait' : 'pointer', whiteSpace: 'nowrap' }}>
      <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M4 7V4h4m8 0h4v3M4 17v3h4m8 0h4v-3M8 8h8v8H8z" /></svg>
      Escanear
    </button>;
  };

  const renderAnexoMenu = (item) => (
    <div style={{ position: 'relative', flexShrink: 0, display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 6 }}>
      <input
        id={`checklist-anexo-${neg._id}-${item.id}`}
        type="file"
        accept="image/*,.heic,.heif,.pdf,.doc,.docx"
        style={{ display: 'none' }}
        onChange={e => {
          if (e.target.files[0]) handleUpload(item.id, e.target.files[0], { itemMeta: item });
          e.target.value = '';
        }}
      />
      {item.arquivo_url && !isImg(item.arquivo_url) ? (
        <a
          href={construirArquivoUrl(item.arquivo_url)}
          target="_blank"
          rel="noopener noreferrer"
          onContextMenu={e => abrirMenuAnexo(e, item)}
          style={{ padding: '5px 10px', background: '#eff6ff', color: '#1e40af', border: '1px solid #bfdbfe', borderRadius: '6px', cursor: 'pointer', fontSize: '0.75rem', fontWeight: '600', whiteSpace: 'nowrap', display: 'flex', alignItems: 'center', gap: '6px', textDecoration: 'none' }}
        >
          Visualizar
        </a>
      ) : (
        <button
          type="button"
          disabled={uploadProgress[item.id] != null}
          onContextMenu={e => abrirMenuAnexo(e, item)}
          onClick={() => {
            if (item.arquivo_url) {
              setArquivoPreview({
                itemId: item.id,
                url: construirArquivoUrl(item.arquivo_url),
                nome: item.arquivo_nome || 'Arquivo',
                isImage: true
              });
              return;
            }
            if (documentacaoEstaAtiva(neg)) {
              alert('A documentação já foi iniciada. Não é possível adicionar novos anexos.');
              return;
            }
            const input = document.getElementById(`checklist-anexo-${neg._id}-${item.id}`);
            if (input) input.click();
          }}
          style={{ padding: '5px 10px', background: item.arquivo_url ? '#eff6ff' : '#ffffff', color: '#1e40af', border: '1px solid #bfdbfe', borderRadius: '6px', cursor: uploadProgress[item.id] != null ? 'default' : 'pointer', fontSize: '0.75rem', fontWeight: '600', whiteSpace: 'nowrap', display: 'flex', alignItems: 'center', gap: '6px', opacity: uploadProgress[item.id] != null ? 0.7 : 1 }}
        >
          {item.arquivo_url ? 'Visualizar' : '📎 Anexar'}
        </button>
      )}
      {renderBotaoScanner(item)}
    </div>
  );

  const renderAnexoCNH = (item) => {
    const renderBotao = (lado) => {
      const isFrente = lado === 'frente';
      const campoUrl = isFrente ? 'arquivo_frente_url' : 'arquivo_verso_url';
      const campoNome = isFrente ? 'arquivo_frente_nome' : 'arquivo_verso_nome';
      const progressKey = `${item.id}:${lado}`;
      const inputId = `checklist-anexo-${neg._id}-${item.id}-${lado}`;
      const arquivoExiste = !!item[campoUrl];

      return (
        <div style={{ position: 'relative', flexShrink: 0, display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 6 }}>
          <input
            id={inputId}
            type="file"
            accept="image/*,.heic,.heif,.pdf,.doc,.docx"
            style={{ display: 'none' }}
            onChange={e => {
              if (e.target.files[0]) {
                handleUpload(item.id, e.target.files[0], { campoUrl, campoNome, progressKey, itemMeta: item });
              }
              e.target.value = '';
            }}
          />
          {arquivoExiste && !isImg(item[campoUrl]) ? (
            <a
              href={construirArquivoUrl(item[campoUrl])}
              target="_blank"
              rel="noopener noreferrer"
              onContextMenu={e => abrirMenuAnexo(e, item, campoUrl, campoNome)}
              style={{ padding: '5px 10px', background: '#eff6ff', color: '#1e40af', border: '1px solid #bfdbfe', borderRadius: '6px', cursor: 'pointer', fontSize: '0.75rem', fontWeight: '600', whiteSpace: 'nowrap', display: 'flex', alignItems: 'center', gap: '6px', textDecoration: 'none' }}
            >
              Visualizar {isFrente ? 'frente' : 'verso'}
            </a>
          ) : (
            <button
              type="button"
              disabled={uploadProgress[progressKey] != null}
              onContextMenu={e => abrirMenuAnexo(e, item, campoUrl, campoNome)}
              onClick={() => {
                if (arquivoExiste) {
                  setArquivoPreview({
                    itemId: item.id,
                    campoUrl,
                    campoNome,
                    url: construirArquivoUrl(item[campoUrl]),
                    nome: item[campoNome] || `CNH/RG ${isFrente ? 'frente' : 'verso'}`,
                    isImage: true
                  });
                  return;
                }
                const input = document.getElementById(inputId);
                if (input) input.click();
              }}
              style={{ padding: '5px 10px', background: arquivoExiste ? '#eff6ff' : '#ffffff', color: '#1e40af', border: '1px solid #bfdbfe', borderRadius: '6px', cursor: uploadProgress[progressKey] != null ? 'default' : 'pointer', fontSize: '0.75rem', fontWeight: '600', whiteSpace: 'nowrap', display: 'flex', alignItems: 'center', gap: '6px', opacity: uploadProgress[progressKey] != null ? 0.7 : 1 }}
            >
              {arquivoExiste ? `Visualizar ${isFrente ? 'frente' : 'verso'}` : `📎 Anexar ${isFrente ? 'frente' : 'verso'}`}
            </button>
          )}
          {renderBotaoScanner(item, { campoUrl, campoNome, progressKey, titulo: `${item.nome || 'Documento'} - ${lado}` })}
        </div>
      );
    };

    return (
      <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
        {renderBotao('frente')}
        {renderBotao('verso')}
      </div>
    );
  };

  // Agrupar por categoria
  const itemsVisiveis = filtrarChecklistVisivel(items);
  const grupos = {};
  itemsVisiveis.forEach(i => { if (!grupos[i.categoria]) grupos[i.categoria] = []; grupos[i.categoria].push(i); });
  const abasChecklist = ABAS_CHECKLIST_MOBILE.map(aba => {
    const itens = itemsVisiveis.filter(item => abaDoItemChecklist(item) === aba.id);
    return { ...aba, itens, progresso: calcularProgressoChecklist(itens) };
  });
  const abaAtualChecklist = abasChecklist.find(aba => aba.id === abaChecklistMobile);
  const gruposChecklistRender = isChecklistMobile ? { '__aba_mobile__': abaAtualChecklist.itens } : grupos;

  const { total, anexados: feitos, percentual: pct } = calcularProgressoChecklist(itemsVisiveis);

  const isImg = (url) => url && /\.(jpg|jpeg|png|gif|webp|heic|heif)$/i.test(url);

  const construirArquivoUrl = (url) => {
    if (!url) return '';
    if (/^https?:\/\//i.test(url)) return url;
    const base = `${API_CONFIG.API_URL}`.replace(/\/api\/?$/, '');
    const normalized = url.startsWith('/') ? url : `/${url}`;
    if (normalized.startsWith('/bucket/') || normalized.startsWith('/arquivos/')) {
      return `${base}${normalized}`;
    }
    return `${base}${normalized}`;
  };

  const montarEnderecoResumo = (extraDados) => {
    if (neg.cliente?.rua) {
      return [neg.cliente.rua, neg.cliente.numero, neg.cliente.bairro, neg.cliente.cidadeEstado].filter(Boolean).join(', ');
    }
    if (extraDados?.enderecoCompleto) return extraDados.enderecoCompleto;
    return [extraDados?.endereco, extraDados?.numero, extraDados?.bairro].filter(Boolean).join(', ');
  };

  const abrirBlobParaDownload = (blob, nomeArquivo) => {
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = nomeArquivo;
    document.body.appendChild(link);
    link.click();
    link.remove();
    setTimeout(() => window.URL.revokeObjectURL(url), 1000);
  };

  const normalizarNomeArquivo = (valor = '') => `${valor}`
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-zA-Z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '');

  const montarNomeArquivoFallback = (tipoDocumento = 'documento') => {
    const cliente = normalizarNomeArquivo(neg?.cliente?.nome || '').toUpperCase() || 'CLIENTE';
    const tipo = normalizarNomeArquivo(tipoDocumento).toLowerCase() || 'documento';
    return `${cliente}_${tipo}.pdf`;
  };

  const carregarVersoesProcuracoes = async () => {
    const response = await axios.get(`${API_CONFIG.API_URL}/negociacoes/${neg._id}/checklist/documentos/procuracoes`, { headers: { 'x-auth-token': token } });
    setVersoesProcuracoes((response.data.itens || []).flatMap(item => (item.documentos_gerados || []).map((versao, indice) => ({ ...versao, numeroVersao: indice + 1 })))
      .sort((a, b) => new Date(b.gerado_em) - new Date(a.gerado_em)));
    const porItem = new Map((response.data.itens || []).map(item => [item.item_id, item.documentos_gerados || []]));
    setItems(atual => atual.map(item => porItem.has(item.id) ? { ...item, documentos_gerados: porItem.get(item.id) } : item));
  };

  const abrirPainelProcuracoes = async (item) => {
    setPainelProcuracoesItem(item);
    setVersoesProcuracoes([]);
    setErroProcuracoes('');
    setCarregandoProcuracoes(true);
    try {
      await carregarVersoesProcuracoes();
    } catch (error) {
      setErroProcuracoes('Não foi possível carregar as procurações. Feche e abra novamente para tentar.');
    } finally { setCarregandoProcuracoes(false); }
  };

  const excluirVersaoProcuracao = async (versao) => {
    if (!window.confirm('Excluir permanentemente esta versão da procuração?')) return;
    setExcluindoProcuracaoId(versao.id);
    try {
      await axios.delete(`${API_CONFIG.API_URL}/negociacoes/${neg._id}/checklist/documentos/procuracoes/${versao.id}`, { headers: { 'x-auth-token': token } });
      await carregarVersoesProcuracoes();
    } catch (error) {
      alert(error.response?.data?.message || 'Não foi possível excluir esta procuração.');
    } finally { setExcluindoProcuracaoId(''); }
  };

  const extrairNomeArquivoResposta = (headers, fallbackNome) => {
    const contentDisposition = headers?.['content-disposition'] || headers?.['Content-Disposition'] || '';
    if (!contentDisposition) return fallbackNome;

    const matchUtf8 = contentDisposition.match(/filename\*=UTF-8''([^;]+)/i);
    if (matchUtf8?.[1]) {
      try {
        return decodeURIComponent(matchUtf8[1].trim().replace(/"/g, ''));
      } catch (e) {
        return matchUtf8[1].trim().replace(/"/g, '');
      }
    }

    const matchSimples = contentDisposition.match(/filename=([^;]+)/i);
    if (matchSimples?.[1]) {
      return matchSimples[1].trim().replace(/"/g, '');
    }

    return fallbackNome;
  };

  const tratarRespostaDocumento = (response, popupDocumento, tipoDocumento) => {
    const blobPdf = new Blob([response.data], { type: 'application/pdf' });
    const nomeArquivo = extrairNomeArquivoResposta(response.headers, montarNomeArquivoFallback(tipoDocumento));
    const isMobile = /Android|iPhone|iPad|iPod/i.test(navigator.userAgent || '');

    if (isMobile) {
      // Garante arquivo local para compartilhar sem link temporario de blob.
      abrirBlobParaDownload(blobPdf, nomeArquivo);
    }

    abrirBlobEmNovaAba(blobPdf, popupDocumento);
  };

  const abrirBlobEmNovaAba = (blob, popupWindow = null) => {
    const url = window.URL.createObjectURL(blob);
    const targetWindow = popupWindow || window.open('', '_blank');

    if (!targetWindow) {
      window.URL.revokeObjectURL(url);
      throw new Error('Não foi possível abrir a nova aba. Verifique o bloqueador de pop-up.');
    }

    targetWindow.location.href = url;
    targetWindow.focus();
    setTimeout(() => window.URL.revokeObjectURL(url), 120000);
  };

  const abrirModalProcuracao = (item) => {
    const primeiraPlaca = opcoesVeiculoProcuracao[0]?.placa || '';
    setProcuracaoModal({
      aberto: true,
      item,
      veiculoPlaca: item?.veiculo_placa || primeiraPlaca,
      proprietarioTipo: 'cliente',
      outroProprietario: obterOutroProprietarioVazio()
    });
  };

  const buscarCepOutroProprietario = async (cepValor) => {
    const cepLimpo = `${cepValor || ''}`.replace(/\D/g, '').slice(0, 8);
    if (cepLimpo.length !== 8) return;
    setBuscandoCepOutroProprietario(true);
    try {
      const resposta = await fetch(`https://viacep.com.br/ws/${cepLimpo}/json/`);
      const dadosCep = await resposta.json();
      if (dadosCep?.erro) return;

      setProcuracaoModal((prev) => ({
        ...prev,
        outroProprietario: {
          ...prev.outroProprietario,
          cep: cepLimpo,
          endereco: dadosCep.logradouro || prev.outroProprietario.endereco || '',
          bairro: dadosCep.bairro || prev.outroProprietario.bairro || '',
          cidadeEstado: [dadosCep.localidade, dadosCep.uf].filter(Boolean).join(' / ') || prev.outroProprietario.cidadeEstado || ''
        }
      }));
    } catch (e) {
    } finally {
      setBuscandoCepOutroProprietario(false);
    }
  };

  const gerarDocumentoChecklist = async (item, payloadExtra = null) => {
    let popupDocumento = null;
    setGerandoDocumentoId(item.id);
    try {
      popupDocumento = window.open('', '_blank');
      const itemRecibo = items.find((checkItem) => checkItem.nome === ITEM_RECIBO_TRANSFERENCIA);

      if (item.nome === ITEM_TERMO_USO_IMAGEM_VOZ) {
        const response = await axios.post(`${API_CONFIG.API_URL}/negociacoes/${neg._id}/checklist/documentos/termo-imagem-voz`, {}, {
          headers: { 'x-auth-token': token },
          responseType: 'blob'
        });
        tratarRespostaDocumento(response, popupDocumento, 'termo_uso_imagem_voz');
        return;
      }

      if (item.nome === ITEM_GERACAO_PROCURACAO) {
        const response = await axios.post(`${API_CONFIG.API_URL}/negociacoes/${neg._id}/checklist/documentos/procuracao`, { ...(payloadExtra || {}), itemId: item.id }, {
          headers: { 'x-auth-token': token },
          responseType: 'blob'
        });
        tratarRespostaDocumento(response, popupDocumento, 'procuracao');
        await carregarVersoesProcuracoes();
        return;
      }

      if (item.nome === ITEM_AVALIACAO_PAINEL) {
        const response = await axios.post(`${API_CONFIG.API_URL}/negociacoes/${neg._id}/checklist/documentos/avaliacao-painel`, {}, {
          headers: { 'x-auth-token': token },
          responseType: 'blob'
        });
        tratarRespostaDocumento(response, popupDocumento, 'avaliacao_painel');
        return;
      }

      if (item.nome === ITEM_AVALIACAO_ENTREGA) {
        const response = await axios.post(`${API_CONFIG.API_URL}/negociacoes/${neg._id}/checklist/documentos/avaliacao-entrega`, {}, {
          headers: { 'x-auth-token': token },
          responseType: 'blob'
        });
        tratarRespostaDocumento(response, popupDocumento, 'avaliacao_entrega');
        return;
      }

      if (item.nome === ITEM_AVALIACAO_GASTOS_PREVISTOS) {
        const response = await axios.post(`${API_CONFIG.API_URL}/negociacoes/${neg._id}/checklist/documentos/avaliacao-gastos-previstos`, {}, {
          headers: { 'x-auth-token': token },
          responseType: 'blob'
        });
        tratarRespostaDocumento(response, popupDocumento, 'avaliacao_gastos_previstos');
        return;
      }

      const response = await axios.post(`${API_CONFIG.API_URL}/negociacoes/${neg._id}/checklist/documentos/venda`, {
        tipoRecibo: itemRecibo?.tipo_recibo || ''
      }, {
        headers: { 'x-auth-token': token },
        responseType: 'blob'
      });
      tratarRespostaDocumento(response, popupDocumento, 'documentos_venda');
    } catch (error) {
      if (popupDocumento && !popupDocumento.closed) popupDocumento.close();
      let mensagem = 'Erro ao gerar documento';
      if (error.response?.data instanceof Blob) {
        try {
          const texto = await error.response.data.text();
          const json = JSON.parse(texto);
          mensagem = json.erro || mensagem;
        } catch {
          mensagem = error.message || mensagem;
        }
      } else {
        mensagem = error.response?.data?.erro || error.message || mensagem;
      }
      alert(mensagem);
    } finally {
      setGerandoDocumentoId(null);
    }
  };

  const confirmarAberturaProcuracao = async () => {
    const { item, veiculoPlaca, proprietarioTipo, outroProprietario } = procuracaoModal;
    if (!item) return;

    if (!veiculoPlaca) {
      alert('Selecione o veículo da procuração.');
      return;
    }

    if (proprietarioTipo === 'outro') {
      const obrigatorios = ['nome', 'cpf', 'rg', 'cep', 'endereco', 'bairro', 'cidadeEstado'];
      const faltando = obrigatorios.filter((campo) => !`${outroProprietario[campo] || ''}`.trim());
      if (faltando.length > 0) {
        alert('Preencha todos os campos do outro proprietário.');
        return;
      }
    }

    const payload = {
      veiculoTrocaPlaca: veiculoPlaca,
      proprietarioTipo,
      proprietarioAtual: proprietarioTipo === 'outro' ? outroProprietario : undefined
    };

    setProcuracaoModal((prev) => ({ ...prev, aberto: false }));
    await gerarDocumentoChecklist(item, payload);
  };

  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 4000, background: '#fff', display: 'flex', flexDirection: 'column', height: '100dvh', maxHeight: '100dvh', overflow: 'hidden' }}>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: isChecklistMobile ? '10px 12px' : '16px 24px', borderBottom: '1px solid #e2e8f0', flexShrink: 0, gap: '10px' }}>
        <div>
          <h2 style={{ margin: 0, fontSize: isChecklistMobile ? '0.95rem' : '1.12rem', fontWeight: '800', color: '#1e293b', lineHeight: 1.25 }}>
            Checklist — {neg.cliente?.nome || 'Negociação'} - {neg.cliente?.cpf || 'CPF'} - {neg.cliente?.telefone || neg.cliente?.celular || 'Telefone'}
          </h2>
          <p style={{ margin: '2px 0 0', fontSize: '0.78rem', color: '#94a3b8' }}>{feitos} de {total} documentos anexados</p>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: isChecklistMobile ? '6px' : '12px', flexShrink: 0 }}>
          {salvando && <span style={{ fontSize: '0.78rem', color: '#94a3b8' }}>Salvando...</span>}
          <button onClick={onClose} style={{ background: 'none', border: 'none', fontSize: '1.5rem', cursor: 'pointer', color: '#94a3b8' }}>×</button>
        </div>
      </div>

      {/* Progress bar */}
      {documentacaoEstaAtiva(neg) && (
        <div style={{ margin: isChecklistMobile ? '0 12px 8px' : '0 24px 12px', background: '#fef2f2', border: '1px solid #fca5a5', padding: isChecklistMobile ? '9px 10px' : '12px 16px', borderRadius: '8px', color: '#991b1b', fontWeight: '600', fontSize: '0.86rem', display: 'flex', alignItems: 'center', gap: '8px', flexShrink: 0 }}>
          <span>🔒 A documentação já foi iniciada para esta negociação. O checklist, os anexos e a exclusão estão bloqueados.</span>
        </div>
      )}
      <div style={{ padding: isChecklistMobile ? '0 12px' : '0 24px', paddingTop: isChecklistMobile ? '8px' : '10px', flexShrink: 0 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.72rem', color: '#64748b', marginBottom: '4px' }}>
          <span>Progresso geral</span><span style={{ fontWeight: '700', color: pct === 100 ? '#16a34a' : '#475569' }}>{pct}%</span>
        </div>
        <div style={{ height: '8px', background: '#e2e8f0', borderRadius: '99px', overflow: 'hidden', marginBottom: '16px' }}>
          <div style={{ height: '100%', width: `${pct}%`, background: pct === 100 ? '#16a34a' : '#3b82f6', borderRadius: '99px', transition: 'width 0.4s' }} />
        </div>
      </div>

      {isChecklistMobile && <div className="checklist-mobile-abas" role="tablist" aria-label="Etapas do checklist">
        {abasChecklist.map((aba, indice) => <button
          key={aba.id}
          type="button"
          role="tab"
          id={`checklist-aba-${neg._id}-${aba.id}`}
          aria-label={aba.titulo}
          aria-selected={abaChecklistMobile === aba.id}
          aria-controls={`checklist-painel-${neg._id}`}
          tabIndex={abaChecklistMobile === aba.id ? 0 : -1}
          onClick={() => setAbaChecklistMobile(aba.id)}
          onKeyDown={e => {
            const proximo = e.key === 'ArrowRight' ? (indice + 1) % abasChecklist.length
              : e.key === 'ArrowLeft' ? (indice + abasChecklist.length - 1) % abasChecklist.length
                : e.key === 'Home' ? 0 : e.key === 'End' ? abasChecklist.length - 1 : null;
            if (proximo === null) return;
            e.preventDefault();
            setAbaChecklistMobile(abasChecklist[proximo].id);
            document.getElementById(`checklist-aba-${neg._id}-${abasChecklist[proximo].id}`)?.focus();
          }}
        ><span className="checklist-mobile-aba-nome">{aba.rotulo}</span><span className="checklist-mobile-aba-progresso">{aba.progresso.anexados}/{aba.progresso.total}</span></button>)}
      </div>}

      {/* Conteúdo */}
      <div ref={conteudoChecklistRef} role={isChecklistMobile ? 'tabpanel' : undefined} id={isChecklistMobile ? `checklist-painel-${neg._id}` : undefined} aria-labelledby={isChecklistMobile ? `checklist-aba-${neg._id}-${abaChecklistMobile}` : undefined} tabIndex={isChecklistMobile ? 0 : undefined} style={{ flex: '1 1 auto', minHeight: 0, overflowY: 'auto', WebkitOverflowScrolling: 'touch', overscrollBehavior: 'contain', padding: isChecklistMobile ? '0 12px calc(18px + env(safe-area-inset-bottom))' : '0 24px 24px', display: 'flex', flexDirection: 'column', gap: isChecklistMobile ? '8px' : '12px' }}>
        {isChecklistMobile && <h3 className="checklist-mobile-aba-titulo">{abaAtualChecklist.titulo}</h3>}
        {isChecklistMobile && !abaAtualChecklist.itens.length && <p className="checklist-mobile-aba-vazia">{abaAtualChecklist.vazio}</p>}
        {Object.entries(gruposChecklistRender).map(([cat, catItems]) => {
          const isAbaChecklistMobile = isChecklistMobile && cat === '__aba_mobile__';
          const isOpen = isAbaChecklistMobile || !!categoriasChecklistExpandidas[cat];
          return (
            <div key={cat} style={{ flexShrink: 0, border: isAbaChecklistMobile ? 'none' : '1px solid #cbd5e1', borderRadius: isAbaChecklistMobile ? 0 : '10px', overflow: isAbaChecklistMobile ? 'visible' : 'hidden', background: '#fff', boxShadow: isAbaChecklistMobile ? 'none' : '0 1px 3px rgba(0,0,0,0.05)' }}>
              <>
              {!isAbaChecklistMobile && (() => {
                const catBase = cat.replace(/^<-\s*/, '').replace(/^->\s*/, '');
                const isSaindo = cat.startsWith('<-');
                const isEntrando = cat.startsWith('->');
                const icone = isEntrando ? (
                  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                    <path d="M5 12h14" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" />
                    <path d="M13 6l6 6-6 6" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                ) : isSaindo ? (
                  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                    <path d="M5 12h14" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" />
                    <path d="M11 6l-6 6 6 6" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                ) : catBase === 'DOCUMENTOS DO CLIENTE' ? (
                  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                    <path d="M8 7h8M8 11h8M8 15h5" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" />
                    <path d="M7 3h8l5 5v13H7a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2Z" stroke="currentColor" strokeWidth="1.8" strokeLinejoin="round" />
                  </svg>
                ) : catBase === CATEGORIA_ACERTO_FINANCEIRO ? (
                  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                    <path d="M12 3v18M6 8h8a3 3 0 1 1 0 6H9a3 3 0 1 0 0 6h9" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                ) : (
                  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                    <path d="M4 7h16M4 12h16M4 17h10" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
                  </svg>
                );
                const bg = isEntrando ? '#eff6ff' : isSaindo ? '#ecfdf5' : '#f8fafc';
                const color = isEntrando ? '#2563eb' : isSaindo ? '#16a34a' : '#64748b';
                let tituloCategoria = catBase;
                let detalheCategoria = '';
                if ((isEntrando || isSaindo) && catBase.includes(':')) {
                  const idx = catBase.indexOf(':');
                  tituloCategoria = catBase.slice(0, idx + 1);
                  detalheCategoria = catBase.slice(idx + 1).trim();
                }
                return (
                  <div
                    onClick={() => setCategoriasChecklistExpandidas(prev => {
                      const nextIsOpen = !prev[cat];
                      return nextIsOpen ? { [cat]: true } : {};
                    })}
                    style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      padding: '12px 16px',
                      background: '#f8fafc',
                      cursor: 'pointer',
                      userSelect: 'none',
                      borderBottom: isOpen ? '1px solid #e2e8f0' : 'none'
                    }}
                  >
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                      <span style={{ fontSize: '0.85rem' }}>{isOpen ? '📂' : '📁'}</span>
                      <span style={{
                        display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                        width: '24px', height: '24px', borderRadius: '999px',
                        background: bg,
                        color,
                        fontSize: '0.95rem', fontWeight: '800', flexShrink: 0
                      }}>
                        {icone}
                      </span>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: detalheCategoria ? '2px' : 0 }}>
                        <div style={{ fontSize: '0.8rem', fontWeight: '700', color: '#475569', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                          {tituloCategoria}
                        </div>
                        {detalheCategoria && (
                          <div style={{ fontSize: '0.8rem', fontWeight: '700', color: '#0f172a', textTransform: 'uppercase', letterSpacing: '0.02em' }}>
                            {detalheCategoria}
                          </div>
                        )}
                      </div>
                    </div>
                    <span style={{ fontSize: '0.85rem', color: '#64748b', fontWeight: '700', transition: 'transform 0.2s' }}>{isOpen ? '▼' : '▶'}</span>
                  </div>
                );
              })()}

              <div style={{
                maxHeight: isAbaChecklistMobile ? 'none' : isOpen ? '2000px' : '0px',
                opacity: isOpen ? 1 : 0,
                overflow: isAbaChecklistMobile ? 'visible' : 'hidden',
                transition: isAbaChecklistMobile ? 'none' : 'max-height 0.35s ease, opacity 0.3s ease, padding 0.35s ease',
                padding: isAbaChecklistMobile ? 0 : isOpen ? '12px' : '0 12px',
                display: 'flex',
                flexDirection: 'column',
                gap: isAbaChecklistMobile ? '10px' : '8px',
                background: '#fff'
              }}>
                {catItems.map((item, index) => (
                    <div key={item.id} style={{ display: 'flex', alignItems: isChecklistMobile ? 'stretch' : 'center', flexDirection: isChecklistMobile ? 'column' : 'row', gap: isChecklistMobile ? '10px' : '14px', padding: isChecklistMobile ? '12px' : '12px 16px', background: isChecklistItemCompleto(item) ? '#eef2ff' : index % 2 === 0 ? '#ffffff' : '#f8fafc', border: '1px solid #cbd5e1', borderRadius: '10px', boxShadow: '0 1px 2px rgba(15, 23, 42, 0.04)' }}>
                  {/* Checkbox indicator */}
                  <div style={{ display: 'flex', alignItems: 'center', gap: '10px', minWidth: 0 }}>
                    <button type="button" title={item.concluido_manualmente ? 'Remover confirmação manual' : 'Confirmar item manualmente'} aria-label={`Confirmar manualmente: ${item.nome}`} aria-pressed={isChecklistItemCompleto(item)} disabled={documentacaoEstaAtiva(neg) || Boolean(confirmandoItem)} onClick={() => item.concluido_manualmente ? confirmarItemManual(item) : setConfirmacaoManualModal({ item, observacao: '' })} style={{ width: '24px', height: '24px', border: 0, padding: 0, cursor: 'pointer', borderRadius: '50%', background: isChecklistItemCompleto(item) ? '#2563eb' : '#e2e8f0', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                      {isChecklistItemCompleto(item) && <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg>}
                    </button>
                    {isChecklistMobile && (
                      <span style={{ minWidth: 0, color: '#64748b', fontSize: '0.68rem', fontWeight: '800', letterSpacing: '0.04em', textTransform: 'uppercase', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {`${item.categoria || ''}`.replace(/^<-\s*/, '').replace(/^->\s*/, '')}
                      </span>
                    )}
                  </div>

                  {/* Nome + tipo recibo */}
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: '0.94rem', fontWeight: '600', color: '#0f172a' }}>{item.nome}</div>
                    {item.concluido_manualmente && <small style={{ color: '#64748b', display: 'block' }}>Confirmado manualmente{item.confirmacao_manual?.nome ? ` por ${item.confirmacao_manual.nome}` : ''}{item.confirmacao_manual?.observacao ? <span style={{ display: 'block', marginTop: 3, color: '#475569' }}>Observação: {item.confirmacao_manual.observacao}</span> : null}</small>}
                    {item.nome === ITEM_RECIBO_TRANSFERENCIA && (
                      <div style={{ display: 'flex', gap: '12px', marginTop: '6px' }}>
                        {['Físico', 'Digital'].map(op => (
                          <label key={op} style={{ display: 'flex', alignItems: 'center', gap: '4px', fontSize: '0.76rem', color: '#64748b', cursor: 'pointer' }}>
                            <input type="radio" name={`recibo_${item.id}`} checked={item.tipo_recibo === op} onChange={() => handleTipoRecibo(item.id, op)} />
                            {op}
                          </label>
                        ))}
                      </div>
                    )}
                  </div>

                  {/* Ações */}
                  <div style={{ display: 'flex', gap: '6px', flexShrink: 0, flexWrap: 'wrap', justifyContent: isChecklistMobile ? 'flex-start' : 'flex-end' }}>
                    {item.checagem_automatica === 'acerto_financeiro' ? (
                      <span style={{ padding: '5px 10px', background: isChecklistItemCompleto(item) ? '#ecfdf5' : '#fff7ed', color: isChecklistItemCompleto(item) ? '#166534' : '#9a3412', border: `1px solid ${isChecklistItemCompleto(item) ? '#bbf7d0' : '#fed7aa'}`, borderRadius: '6px', fontSize: '0.75rem', fontWeight: '600', whiteSpace: 'nowrap' }}>
                        {item.concluido_manualmente ? 'Confirmado manualmente' : isChecklistItemCompleto(item) ? 'Saldo zerado' : 'Ajustar acerto financeiro'}
                      </span>
                    ) : item.nome === ITEM_TERMO_USO_IMAGEM_VOZ ? (
                      <>
                        <button
                          type="button"
                          onClick={() => gerarDocumentoChecklist(item)}
                          disabled={gerandoDocumentoId === item.id}
                          style={{ padding: '5px 10px', background: '#1e40af', color: '#fff', border: '1px solid #1d4ed8', borderRadius: '6px', cursor: 'pointer', fontSize: '0.75rem', fontWeight: '600', whiteSpace: 'nowrap', opacity: gerandoDocumentoId === item.id ? 0.7 : 1, display: 'inline-flex', alignItems: 'center', gap: '6px' }}
                        >
                          {gerandoDocumentoId === item.id ? (
                            <>
                              <svg width="12" height="12" viewBox="0 0 50 50" aria-hidden="true">
                                <circle cx="25" cy="25" r="20" fill="none" stroke="rgba(255,255,255,0.3)" strokeWidth="6" />
                                <path d="M25 5a20 20 0 0 1 20 20" fill="none" stroke="#fff" strokeWidth="6" strokeLinecap="round">
                                  <animateTransform attributeName="transform" type="rotate" from="0 25 25" to="360 25 25" dur="0.8s" repeatCount="indefinite" />
                                </path>
                              </svg>
                              Abrindo...
                            </>
                          ) : (
                            <>
                              Abrir
                              <span aria-hidden="true">↗</span>
                            </>
                          )}
                        </button>
                        {renderAnexoMenu(item)}
                      </>
                    ) : ITENS_GERACAO_VENDA.has(item.nome) || item.nome === ITEM_GERACAO_PROCURACAO || item.nome === ITEM_AVALIACAO_PAINEL ? (
                      <>
                        <button
                          type="button"
                          onClick={() => {
                            if (item.nome === ITEM_GERACAO_PROCURACAO) {
                              abrirPainelProcuracoes(item);
                              return;
                            }
                            gerarDocumentoChecklist(item);
                          }}
                          disabled={gerandoDocumentoId === item.id}
                          style={{ padding: '5px 10px', background: '#1e40af', color: '#fff', border: '1px solid #1d4ed8', borderRadius: '6px', cursor: 'pointer', fontSize: '0.75rem', fontWeight: '600', whiteSpace: 'nowrap', opacity: gerandoDocumentoId === item.id ? 0.7 : 1, display: 'inline-flex', alignItems: 'center', gap: '6px' }}
                        >
                          {gerandoDocumentoId === item.id ? (
                            <>
                              <svg width="12" height="12" viewBox="0 0 50 50" aria-hidden="true">
                                <circle cx="25" cy="25" r="20" fill="none" stroke="rgba(255,255,255,0.3)" strokeWidth="6" />
                                <path d="M25 5a20 20 0 0 1 20 20" fill="none" stroke="#fff" strokeWidth="6" strokeLinecap="round">
                                  <animateTransform attributeName="transform" type="rotate" from="0 25 25" to="360 25 25" dur="0.8s" repeatCount="indefinite" />
                                </path>
                              </svg>
                              Abrindo...
                            </>
                          ) : (
                            <>
                              Abrir
                              <span aria-hidden="true">↗</span>
                            </>
                          )}
                        </button>
                        {renderAnexoMenu(item)}
                      </>
                    ) : (
                      <>
                        {item.nome === ITEM_CNH_RG ? (
                          renderAnexoCNH(item)
                        ) : item.nome.startsWith('Comprovante') ? (
                          renderAnexoMenu(item)
                        ) : item.nome === ITEM_RECIBO_TRANSFERENCIA && item.tipo_recibo === 'Digital' ? (
                          <span style={{ padding: '5px 10px', background: '#ecfdf5', color: '#166534', border: '1px solid #bbf7d0', borderRadius: '6px', fontSize: '0.75rem', fontWeight: '600', whiteSpace: 'nowrap' }}>
                            Concluído (Digital)
                          </span>
                        ) : (
                          renderAnexoMenu(item)
                        )}
                      </>
                    )}

                    {Object.keys(uploadProgress).some((k) => k === item.id || k.startsWith(`${item.id}:`)) && (
                      <div style={{ minWidth: '110px', maxWidth: '130px' }}>
                        <div style={{ fontSize: '0.7rem', color: '#64748b', marginBottom: '3px', textAlign: 'right' }}>
                          {Math.max(...Object.entries(uploadProgress)
                            .filter(([k]) => k === item.id || k.startsWith(`${item.id}:`))
                            .map(([, v]) => v))}%
                        </div>
                        <div style={{ height: '6px', borderRadius: '999px', background: '#dbeafe', overflow: 'hidden' }}>
                          <div style={{ width: `${Math.max(...Object.entries(uploadProgress)
                            .filter(([k]) => k === item.id || k.startsWith(`${item.id}:`))
                            .map(([, v]) => v))}%`, height: '100%', background: '#2563eb', transition: 'width 0.15s linear' }} />
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              ))}
              </div>
              </>
            </div>
          );
        })}
      </div>

      {confirmacaoManualModal && (
        <div style={{ position: 'fixed', inset: 0, zIndex: 4250, background: 'rgba(15, 23, 42, 0.62)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
          <form onSubmit={e => { e.preventDefault(); confirmarItemManual(confirmacaoManualModal.item, confirmacaoManualModal.observacao); }} style={{ width: 'min(520px, 100%)', background: '#fff', borderRadius: 12, padding: 22, boxShadow: '0 20px 60px rgba(15,23,42,.3)' }}>
            <h3 style={{ margin: '0 0 6px', color: '#0f172a' }}>Concluir item manualmente</h3>
            <p style={{ margin: '0 0 16px', color: '#64748b', fontSize: 14 }}>{confirmacaoManualModal.item.nome}</p>
            <label style={{ display: 'block', color: '#334155', fontSize: 13, fontWeight: 700 }}>
              Observação da conclusão
              <textarea autoFocus required rows={4} maxLength={1000} value={confirmacaoManualModal.observacao} onChange={e => setConfirmacaoManualModal(atual => ({ ...atual, observacao: e.target.value }))} placeholder="Informe por que este item está sendo considerado concluído" style={{ display: 'block', width: '100%', boxSizing: 'border-box', marginTop: 7, padding: 11, border: '1px solid #cbd5e1', borderRadius: 8, resize: 'vertical', font: 'inherit' }} />
            </label>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 18 }}>
              <button type="button" className="btn-secondary" disabled={Boolean(confirmandoItem)} onClick={() => setConfirmacaoManualModal(null)}>Cancelar</button>
              <button type="submit" className="btn-primary" disabled={Boolean(confirmandoItem) || !confirmacaoManualModal.observacao.trim()}>{confirmandoItem ? 'Salvando…' : 'Confirmar conclusão'}</button>
            </div>
          </form>
        </div>
      )}

      {painelProcuracoesItem && (
        <div style={{ position: 'fixed', inset: 0, zIndex: 4100, background: 'rgba(15, 23, 42, 0.6)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
          <div role="dialog" aria-modal="true" aria-labelledby="titulo-procuracoes" style={{ width: 'min(760px, 100%)', maxHeight: '88vh', overflowY: 'auto', background: '#fff', borderRadius: 12, padding: 20 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12 }}>
              <h3 id="titulo-procuracoes" style={{ margin: 0 }}>Procurações da negociação</h3>
              <button type="button" aria-label="Fechar procurações" disabled={Boolean(gerandoDocumentoId) || Boolean(excluindoProcuracaoId) || carregandoProcuracoes} onClick={() => setPainelProcuracoesItem(null)} className="btn-secondary">Fechar</button>
            </div>
            <p style={{ color: '#64748b', fontSize: 14 }}>{neg.cliente?.nome || 'Negociação'} — todas as procurações geradas, de todos os veículos desta negociação.</p>
            <button type="button" className="btn-primary" disabled={Boolean(gerandoDocumentoId)} onClick={() => abrirModalProcuracao(painelProcuracoesItem)}>{gerandoDocumentoId ? 'Gerando procuração…' : 'Gerar nova procuração'}</button>
            <h4 style={{ marginBottom: 10 }}>Procurações já geradas ({versoesProcuracoes.length})</h4>
            {carregandoProcuracoes ? <p role="status">Carregando procurações…</p> : erroProcuracoes ? <p role="alert">{erroProcuracoes}</p> : versoesProcuracoes.length === 0 ? <p>Nenhuma procuração gerada para esta negociação.</p> : (
              <div style={{ display: 'grid', gap: 10 }}>
                {versoesProcuracoes.map(versao => (
                  <div key={versao.id} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 10, padding: 12, border: '1px solid #dbeafe', borderRadius: 8, background: '#f8fafc', fontSize: 13 }}>
                    <a href={construirArquivoUrl(versao.arquivo_url)} target="_blank" rel="noopener noreferrer" style={{ flex: 1, minWidth: 180, padding: 0, color: '#1d4ed8', cursor: 'pointer', textAlign: 'left', fontWeight: 700, overflowWrap: 'anywhere', textDecoration: 'none' }}>
                      Versão {versao.numeroVersao} · {new Date(versao.gerado_em).toLocaleString('pt-BR')}
                      <span style={{ display: 'block', color: '#64748b', fontWeight: 400 }}>{versao.dados?.proprietario?.nome || 'Proprietário'} · CPF {versao.dados?.proprietario?.cpf || '-'} · RG {versao.dados?.proprietario?.rg || '-'}</span>
                      <span style={{ display: 'block', color: '#64748b', fontWeight: 400 }}>{[versao.dados?.veiculo?.modelo, versao.dados?.veiculo?.ano, versao.dados?.veiculo?.cor, versao.dados?.veiculo?.placa].filter(Boolean).join(' · ') || 'Veículo não informado'} · por {versao.gerado_por || 'Usuário'}</span>
                    </a>
                    {!documentacaoEstaAtiva(neg) && <button type="button" disabled={Boolean(excluindoProcuracaoId)} onClick={() => excluirVersaoProcuracao(versao)} title="Excluir esta versão" style={{ border: '1px solid #fecaca', background: '#fff', color: '#dc2626', borderRadius: 6, cursor: 'pointer', padding: '6px 10px' }}>{excluindoProcuracaoId === versao.id ? 'Excluindo…' : 'Excluir'}</button>}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {documentoScanner && <React.Suspense fallback={<div role="status" style={{ position: 'fixed', inset: 0, zIndex: 6500, background: '#f8fafc', display: 'grid', placeContent: 'center', gap: 16, textAlign: 'center' }}>Preparando scanner…<button type="button" onClick={() => setDocumentoScanner(null)}>Cancelar</button></div>}>
        <DocumentoScanner
          titulo={documentoScanner.opcoes.titulo || documentoScanner.item.nome || 'Documento'}
          onFechar={() => setDocumentoScanner(null)}
          onSalvar={file => handleUpload(documentoScanner.item.id, file, { ...documentoScanner.opcoes, itemMeta: documentoScanner.item })}
        />
      </React.Suspense>}

      {menuAnexo && createPortal(
        <div
          ref={menuAnexoRef}
          role="menu"
          aria-label="Ações do anexo"
          onClick={e => e.stopPropagation()}
          onContextMenu={e => e.preventDefault()}
          style={{ position: 'fixed', left: menuAnexo.x, top: menuAnexo.y, zIndex: 6000, width: 'min(212px, calc(100vw - 16px))', padding: 5, background: '#fff', border: '1px solid #e2e8f0', borderRadius: 9, boxShadow: '0 8px 24px rgba(15, 23, 42, 0.18)' }}
        >
          <button
            type="button"
            role="menuitem"
            disabled={documentacaoEstaAtiva(neg)}
            title={documentacaoEstaAtiva(neg) ? 'A documentação já foi iniciada. Não é possível excluir anexos.' : 'Excluir este anexo'}
            onClick={async () => {
              const alvo = menuAnexo;
              setMenuAnexo(null);
              if (!window.confirm('Deseja excluir este arquivo do checklist?')) return;
              await handleRemover(alvo.itemId, alvo.campoUrl, alvo.campoNome);
              acionadorMenuAnexoRef.current?.focus();
            }}
            style={{ width: '100%', display: 'flex', alignItems: 'center', gap: 9, padding: '10px 12px', border: 0, borderRadius: 5, background: '#fef2f2', color: '#b91c1c', fontSize: '0.84rem', fontWeight: 600, cursor: documentacaoEstaAtiva(neg) ? 'not-allowed' : 'pointer', opacity: documentacaoEstaAtiva(neg) ? 0.5 : 1 }}
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M3 6h18M9 6V3h6v3M5 6l1 15h12l1-15M10 10v7M14 10v7" /></svg>
            Excluir anexo
          </button>
        </div>, document.body
      )}

      {arquivoPreview && (
        <div
          style={{ position: 'fixed', inset: 0, zIndex: 4200, background: 'rgba(15, 23, 42, 0.68)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '20px' }}
          onClick={() => setArquivoPreview(null)}
        >
          <div
            role="dialog"
            aria-modal="true"
            aria-label={arquivoPreview.nome}
            style={{ width: 'min(980px, 100%)', height: 'min(88vh, 860px)', background: '#fff', borderRadius: '12px', border: '1px solid #e2e8f0', overflow: 'hidden', display: 'flex', flexDirection: 'column' }}
            onClick={e => e.stopPropagation()}
          >
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, flexShrink: 0, padding: '12px 16px', borderBottom: '1px solid #e2e8f0' }}>
              <div style={{ minWidth: 0, fontSize: '0.86rem', fontWeight: '700', color: '#1e293b', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {arquivoPreview.nome}
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexShrink: 0 }}>
                {arquivoPreview.isImage && <button
                  type="button"
                  title="Imprimir imagem"
                  aria-label="Imprimir imagem"
                  onClick={() => imprimirImagem(arquivoPreview)}
                  style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', padding: 8, background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: 7, cursor: 'pointer', color: '#1d4ed8' }}
                >
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M6 9V3h12v6M6 18H4a2 2 0 0 1-2-2v-5a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v5a2 2 0 0 1-2 2h-2M6 14h12v7H6zM18 12h.01" /></svg>
                </button>}
                {arquivoPreview.itemId && !documentacaoEstaAtiva(neg) && (
                  <button
                    type="button"
                    onClick={() => {
                      if (!window.confirm('Deseja excluir este arquivo do checklist?')) return;
                      handleRemover(arquivoPreview.itemId, arquivoPreview.campoUrl || 'arquivo_url', arquivoPreview.campoNome || 'arquivo_nome');
                      setArquivoPreview(null);
                    }}
                    style={{ padding: '6px 10px', background: '#fef2f2', border: '1px solid #fecaca', borderRadius: '6px', cursor: 'pointer', color: '#dc2626', fontSize: '0.76rem', fontWeight: '700' }}
                  >
                    Excluir arquivo
                  </button>
                )}
                <button type="button" aria-label="Fechar visualização" onClick={() => setArquivoPreview(null)} style={{ border: 'none', background: 'none', cursor: 'pointer', color: '#64748b', fontSize: '1.4rem', lineHeight: 1 }}>×</button>
              </div>
            </div>
            <div style={{ flex: 1, minHeight: 0, overflow: 'auto', overscrollBehavior: 'contain', background: '#f8fafc', padding: '10px' }}>
              {arquivoPreview.isImage ? (
                <img src={arquivoPreview.url} alt={arquivoPreview.nome} style={{ display: 'block', maxWidth: '100%', width: 'auto', height: 'auto', margin: '0 auto' }} />
              ) : (
                <div style={{ display: 'grid', justifyItems: 'center', gap: 12, padding: 24, textAlign: 'center', color: '#475569' }}>
                  <span>Este documento será aberto em uma nova aba.</span>
                  <a href={arquivoPreview.url} target="_blank" rel="noopener noreferrer" className="btn-primary" style={{ textDecoration: 'none' }}>Abrir documento</a>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {procuracaoModal.aberto && (
        <div style={{ position: 'fixed', inset: 0, zIndex: 4300, background: 'rgba(15, 23, 42, 0.6)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '20px' }}>
          <div style={{ width: 'min(620px, 100%)', maxHeight: '88vh', overflowY: 'auto', background: '#fff', borderRadius: '12px', border: '1px solid #e2e8f0', padding: '18px' }}>
            <div style={{ fontSize: '1rem', fontWeight: '800', color: '#0f172a', marginBottom: '14px' }}>Confirmar dados da procuração</div>

            <div style={{ display: 'grid', gap: '12px' }}>
              <div>
                <label style={{ display: 'block', fontSize: '0.78rem', fontWeight: '700', color: '#334155', marginBottom: '6px' }}>Veículo</label>
                <select
                  value={procuracaoModal.veiculoPlaca}
                  disabled
                  onChange={(e) => setProcuracaoModal((prev) => ({ ...prev, veiculoPlaca: e.target.value }))}
                  style={{ width: '100%', padding: '10px 12px', border: '1px solid #cbd5e1', borderRadius: '8px', background: '#f8fafc', color: '#0f172a' }}
                >
                  {opcoesVeiculoProcuracao.map((opcao) => (
                    <option key={opcao.placa || opcao.label} value={opcao.placa}>{opcao.label}</option>
                  ))}
                </select>
              </div>

              <div>
                <label style={{ display: 'block', fontSize: '0.78rem', fontWeight: '700', color: '#334155', marginBottom: '6px' }}>Proprietário atual</label>
                <select
                  value={procuracaoModal.proprietarioTipo}
                  onChange={(e) => {
                    const tipo = e.target.value;
                    setProcuracaoModal((prev) => ({
                      ...prev,
                      proprietarioTipo: tipo,
                      outroProprietario: tipo === 'outro' ? obterOutroProprietarioVazio() : prev.outroProprietario
                    }));
                  }}
                  style={{ width: '100%', padding: '10px 12px', border: '1px solid #cbd5e1', borderRadius: '8px', background: '#fff', color: '#0f172a' }}
                >
                  <option value="cliente">{`Cliente da negociação: ${neg?.cliente?.nome || 'Não informado'}`}</option>
                  <option value="outro">Outro proprietário</option>
                </select>
              </div>

              {procuracaoModal.proprietarioTipo === 'outro' && (
                <>
                  {[
                    ['nome', 'Nome completo'],
                    ['cpf', 'CPF'],
                    ['rg', 'RG']
                  ].map(([campo, rotulo]) => (
                    <div key={campo}>
                      <label style={{ display: 'block', fontSize: '0.78rem', fontWeight: '700', color: '#334155', marginBottom: '6px' }}>{rotulo}</label>
                      <input
                        value={procuracaoModal.outroProprietario[campo] || ''}
                        onChange={(e) => setProcuracaoModal((prev) => ({
                          ...prev,
                          outroProprietario: { ...prev.outroProprietario, [campo]: e.target.value }
                        }))}
                        style={{ width: '100%', padding: '10px 12px', border: '1px solid #cbd5e1', borderRadius: '8px', background: '#fff', color: '#0f172a' }}
                      />
                    </div>
                  ))}

                  <div>
                    <label style={{ display: 'block', fontSize: '0.78rem', fontWeight: '700', color: '#334155', marginBottom: '6px' }}>CEP</label>
                    <div style={{ display: 'flex', gap: '8px' }}>
                      <input
                        value={procuracaoModal.outroProprietario.cep || ''}
                        onChange={(e) => {
                          const cepLimpo = e.target.value.replace(/\D/g, '').slice(0, 8);
                          setProcuracaoModal((prev) => ({
                            ...prev,
                            outroProprietario: { ...prev.outroProprietario, cep: cepLimpo }
                          }));
                          if (cepLimpo.length === 8) buscarCepOutroProprietario(cepLimpo);
                        }}
                        onBlur={() => buscarCepOutroProprietario(procuracaoModal.outroProprietario.cep)}
                        style={{ flex: 1, padding: '10px 12px', border: '1px solid #cbd5e1', borderRadius: '8px', background: '#fff', color: '#0f172a' }}
                      />
                      <button
                        type="button"
                        onClick={() => buscarCepOutroProprietario(procuracaoModal.outroProprietario.cep)}
                        disabled={buscandoCepOutroProprietario || `${procuracaoModal.outroProprietario.cep || ''}`.replace(/\D/g, '').length !== 8}
                        style={{ padding: '0 14px', background: '#1e40af', color: '#fff', border: '1px solid #1d4ed8', borderRadius: '8px', fontWeight: '700', cursor: 'pointer', opacity: buscandoCepOutroProprietario ? 0.7 : 1 }}
                      >
                        {buscandoCepOutroProprietario ? '...' : 'Buscar CEP'}
                      </button>
                    </div>
                  </div>

                  {[
                    ['endereco', 'Endereço'],
                    ['bairro', 'Bairro'],
                    ['cidadeEstado', 'Cidade/UF']
                  ].map(([campo, rotulo]) => (
                    <div key={campo}>
                      <label style={{ display: 'block', fontSize: '0.78rem', fontWeight: '700', color: '#334155', marginBottom: '6px' }}>{rotulo}</label>
                      <input
                        value={procuracaoModal.outroProprietario[campo] || ''}
                        onChange={(e) => setProcuracaoModal((prev) => ({
                          ...prev,
                          outroProprietario: { ...prev.outroProprietario, [campo]: e.target.value }
                        }))}
                        style={{ width: '100%', padding: '10px 12px', border: '1px solid #cbd5e1', borderRadius: '8px', background: '#fff', color: '#0f172a' }}
                      />
                    </div>
                  ))}
                </>
              )}
            </div>

            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px', marginTop: '16px' }}>
              <button
                type="button"
                onClick={() => setProcuracaoModal((prev) => ({ ...prev, aberto: false }))}
                style={{ padding: '8px 12px', background: '#fff', color: '#334155', border: '1px solid #cbd5e1', borderRadius: '8px', fontWeight: '700', cursor: 'pointer' }}
              >
                Cancelar
              </button>
              <button
                type="button"
                onClick={confirmarAberturaProcuracao}
                style={{ padding: '8px 12px', background: '#1e40af', color: '#fff', border: '1px solid #1d4ed8', borderRadius: '8px', fontWeight: '700', cursor: 'pointer' }}
              >
                Confirmar e abrir
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// ─── Negociacoes ──────────────────────────────────────────────────────────────
const Negociacoes = ({ onToggleSidebar, negociacaoInicialId, onNegociacaoInicialAberta }) => {
  const [isMobile, setIsMobile] = useState(() => {
    if (typeof window === 'undefined') return false;
    return window.innerWidth <= 768;
  });
  const [modalAberto, setModalAberto] = useState(false);
  const [currentSection, setCurrentSection] = useState(0);
  const [parteCliente, setParteCliente] = useState(0);
  const [clienteManual, setClienteManual] = useState(false);
  const [estadoRascunho, setEstadoRascunho] = useState('');
  const tituloEtapaRef = useRef(null);
  const conteudoEtapaRef = useRef(null);
  const [formData, setFormData] = useState(estadoInicial);
  const [erros, setErros] = useState({});
  const [carregando, setCarregando] = useState(false);
  const [blockSubmit, setBlockSubmit] = useState(false);
  const [pesquisaRealizada, setPesquisaRealizada] = useState(false);
  const [mensagemCpf, setMensagemCpf] = useState('');
  const consultaCpfRef = useRef(0);
  const consultaPlacaRef = useRef({ venda: 0, troca: 0 });
  const [consultaPlacaStatus, setConsultaPlacaStatus] = useState({});

  const [clienteQuery, setClienteQuery] = useState('');
  const [cepBuscando, setCepBuscando] = useState(false);
  const [cepQuery, setCepQuery] = useState('');
  const [enderecoManual, setEnderecoManual] = useState(false);

  // veículos vendidos (lista de cards)
  const [veiculosVendidos, setVeiculosVendidos] = useState([]);
  const [veiculos, setVeiculos] = useState([]);

  // sub-modal veículo
  const [subModalAberto, setSubModalAberto] = useState(false);
  const [subModalForm, setSubModalForm] = useState(subFormVazio());
  const [subModalEditId, setSubModalEditId] = useState(null);

  // veículos na troca
  const [trocas, setTrocas] = useState([]);
  const [subModalTrocaAberto, setSubModalTrocaAberto] = useState(false);
  const [subModalTrocaForm, setSubModalTrocaForm] = useState(subFormTrocaVazio());
  const [subModalTrocaEditId, setSubModalTrocaEditId] = useState(null);

  // produtos agregados
  const [produtosAgregados, setProdutosAgregados] = useState([]);

  // acerto financeiro
  const [pagamentos, setPagamentos] = useState([]);
  const [recebimentos, setRecebimentos] = useState([]);
  const [promissoriaAberta, setPromissoriaAberta] = useState(null);

  const proximoVencimentoPromissoria = (primeiroVencimento, indice) => {
    const [ano, mes, dia] = String(primeiroVencimento || hojeLocalISO()).split('-').map(Number);
    const destino = new Date(ano, mes - 1 + indice, 1);
    const ultimoDia = new Date(destino.getFullYear(), destino.getMonth() + 1, 0).getDate();
    return `${destino.getFullYear()}-${String(destino.getMonth() + 1).padStart(2, '0')}-${String(Math.min(dia, ultimoDia)).padStart(2, '0')}`;
  };

  const abrirConfiguracaoPromissoria = (recebimento) => {
    const principal = parseMoeda(recebimento.valor);
    const existente = recebimento.promissoria || {};
    const quantidade = Number(existente.quantidade_parcelas) || 1;
    const primeiroVencimento = existente.parcelas?.[0]?.vencimento || hojeLocalISO();
    const base = existente.parcelas?.length ? existente.parcelas : [];
    setPromissoriaAberta({ id: recebimento.id, principal, primeiro_vencimento: primeiroVencimento, quantidade_parcelas: quantidade, parcelas: base });
  };
  const recalcularPromissoria = (config) => {
    const qtd = Math.max(1, Math.min(120, Number(config.quantidade_parcelas) || 1));
    const principal = Number(config.principal) || 0;
    const antigas = config.parcelas || [];
    const primeiroVencimento = config.primeiro_vencimento || antigas[0]?.vencimento || hojeLocalISO();
    const parcelas = Array.from({ length: qtd }, (_, i) => ({
      numero: i + 1,
      vencimento: antigas[i]?.vencimento || proximoVencimentoPromissoria(primeiroVencimento, i),
      valor: principal > 0 ? exibirMoeda(principal / qtd) : ''
    }));
    return { ...config, quantidade_parcelas: qtd, primeiro_vencimento: primeiroVencimento, parcelas };
  };
  const alterarVencimentoPromissoria = (indice, vencimento) => {
    setPromissoriaAberta(atual => {
      if (!atual) return atual;
      const parcelas = indice === 0
        ? atual.parcelas.map((parcela, i) => ({ ...parcela, vencimento: proximoVencimentoPromissoria(vencimento, i) }))
        : atual.parcelas.map((parcela, i) => i === indice ? { ...parcela, vencimento } : parcela);
      return { ...atual, primeiro_vencimento: indice === 0 ? vencimento : atual.primeiro_vencimento, parcelas };
    });
  };
  const salvarPromissoria = () => {
    if (!promissoriaAberta) return;
    const config = promissoriaAberta;
    if (!config.parcelas?.length) return;
    setRecebimentos(prev => prev.map(r => r.id === config.id ? { ...r, promissoria: { quantidade_parcelas: config.quantidade_parcelas, parcelas: config.parcelas } } : r));
    setPromissoriaAberta(null);
  };

  // lista de negociações
  const [negociacoes, setNegociacoes] = useState([]);
  const [selectedNegociacao, setSelectedNegociacao] = useState(null);
  const [filtroBuscaNeg, setFiltroBuscaNeg] = useState('');
  const [filtroLojaNeg, setFiltroLojaNeg] = useState('');
  const [filtroStatusChecklist, setFiltroStatusChecklist] = useState('');
  const [mesFiltroNeg, setMesFiltroNeg] = useState(() => obterMesAtualFiltro());
  const [sortNeg, setSortNeg] = useState('data_desc');
  const [filtrosMobileAbertos, setFiltrosMobileAbertos] = useState(false);
  const [relatorioModalAberto, setRelatorioModalAberto] = useState(false);
  const [relatorioVendedor, setRelatorioVendedor] = useState('');
  const [relatorioMes, setRelatorioMes] = useState('');
  const [relatorioGerando, setRelatorioGerando] = useState(false);
  const [relatorioErro, setRelatorioErro] = useState('');
  const [relatorioFormato, setRelatorioFormato] = useState('pdf');
  const [relatorioColunas, setRelatorioColunas] = useState({
    valorFinanciado: false,
    bancoFinanciado: false,
    retornoPercentual: false,
    retornoLiquido: false,
    retornoCincoPorcento: false
  });
  const [checklistModalNeg, setChecklistModalNeg] = useState(null);
  const [menuAcaoNegociacaoId, setMenuAcaoNegociacaoId] = useState(null);
  const [impressaoModalNeg, setImpressaoModalNeg] = useState(null);
  const [documentosImpressao, setDocumentosImpressao] = useState([]);
  const [gerandoImpressao, setGerandoImpressao] = useState(false);
  const [erroImpressao, setErroImpressao] = useState('');
  const gerandoImpressaoRef = useRef(false);
  const [usuariosLoja, setUsuariosLoja] = useState([]);
  const [bancosLoja, setBancosLoja] = useState([]);
  const [participanteBusca, setParticipanteBusca] = useState('');
  const [participantesAbertos, setParticipantesAbertos] = useState(false);
  const rascunhoIdRef = useRef(null);
  const rascunhoOriginalRef = useRef(null);
  const salvarDebounceRef = useRef(null);
  const filaSalvamentoRef = useRef(Promise.resolve());
  const finalizandoRef = useRef(false);
  const usuarioLogado = (() => {
    try {
      return JSON.parse(localStorage.getItem('user') || 'null');
    } catch {
      return null;
    }
  })();
  const podeExcluirNegociacao = ['super_admin', 'admin_loja', 'Gerente', 'gerente', 'Analista', 'analista'].includes(usuarioLogado?.tipo || usuarioLogado?.cargo || '');

  const gruposVendedores = useMemo(() => {
    const gruposMap = new Map();

    const normalizarTituloCargo = (cargoRaw) => {
      const cargoBase = `${cargoRaw || ''}`.trim();
      if (!cargoBase) return 'Sem cargo definido';
      const lower = cargoBase.toLowerCase();
      if (lower === 'super_admin') return 'Super Admin';
      if (lower === 'admin_loja') return 'Admin Loja';
      if (lower === 'gerente') return 'Gerente';
      if (lower === 'analista') return 'Analista';
      if (lower === 'vendedor' || lower === 'vendedora') return 'Vendedor(a)';
      return cargoBase.charAt(0).toUpperCase() + cargoBase.slice(1);
    };

    const ordemCargo = (cargo) => {
      const c = cargo.toLowerCase();
      if (c.includes('super admin')) return 1;
      if (c.includes('admin loja')) return 2;
      if (c.includes('gerente')) return 3;
      if (c.includes('analista')) return 4;
      if (c.includes('vendedor')) return 5;
      if (c.includes('sem cargo')) return 99;
      return 50;
    };

    (usuariosLoja || []).forEach((u) => {
      const nome = `${u?.nome || ''}`.trim();
      if (!nome) return;
      const cargo = normalizarTituloCargo(u?.cargo || u?.tipo);

      if (!gruposMap.has(cargo)) {
        gruposMap.set(cargo, []);
      }

      gruposMap.get(cargo).push({
        id: u?._id || `${cargo}-${nome}`,
        nome
      });
    });

    return Array.from(gruposMap.entries())
      .map(([cargo, usuarios]) => ({
        cargo,
        usuarios: usuarios.sort((a, b) => a.nome.localeCompare(b.nome, 'pt-BR'))
      }))
      .sort((a, b) => {
        const porOrdem = ordemCargo(a.cargo) - ordemCargo(b.cargo);
        if (porOrdem !== 0) return porOrdem;
        return a.cargo.localeCompare(b.cargo, 'pt-BR');
      });
  }, [usuariosLoja]);

  const vendedorExisteNaLista = useMemo(() => {
    const atual = `${formData.vendedor || ''}`.trim();
    if (!atual) return true;
    return gruposVendedores.some((grupo) =>
      grupo.usuarios.some((u) => u.nome === atual)
    );
  }, [formData.vendedor, gruposVendedores]);

  const veiculosParaProdutosAgregados = useMemo(() => {
    const vendidos = veiculosVendidos.map((v) => {
      const label = [v.marca, v.modelo, v.ano].filter(Boolean).join(' ');
      return {
        tipo: 'saida',
        placa: v.placa || '',
        label: `${label || 'Veículo saindo'}${v.placa ? ` — ${v.placa}` : ''}`,
        key: `saida:${v.placa || v.id}`
      };
    });

    const entradas = trocas.map((t) => {
      const ano = t.anoFabricacao && t.anoModelo ? `${t.anoFabricacao}/${t.anoModelo}` : t.anoFabricacao || t.anoModelo || '';
      const label = [t.modelo, ano].filter(Boolean).join(' ');
      return {
        tipo: 'entrada',
        placa: t.placa || '',
        label: `${label || 'Veículo entrando'}${t.placa ? ` — ${t.placa}` : ''}`,
        key: `entrada:${t.placa || t.id}`
      };
    });

    return [...vendidos, ...entradas];
  }, [trocas, veiculosVendidos]);

  const gruposParticipantesDisponiveis = useMemo(() => {
    const busca = participanteBusca.trim().toLowerCase();
    const participantesAtuais = new Set(
      (Array.isArray(formData.participantes) ? formData.participantes : [])
        .map((nome) => `${nome || ''}`.trim())
        .filter(Boolean)
    );

    return gruposVendedores
      .map((grupo) => {
        const cargoMatch = grupo.cargo.toLowerCase().includes(busca);
        const usuarios = grupo.usuarios.filter((usuario) => {
          if (participantesAtuais.has(usuario.nome)) return false;
          if (!busca) return true;
          return cargoMatch || usuario.nome.toLowerCase().includes(busca);
        });

        return { ...grupo, usuarios };
      })
      .filter((grupo) => grupo.usuarios.length > 0);
  }, [formData.participantes, gruposVendedores, participanteBusca]);

  const participantesDisponiveisFlat = useMemo(
    () => gruposParticipantesDisponiveis.flatMap((grupo) =>
      grupo.usuarios.map((usuario) => ({ ...usuario, cargo: grupo.cargo }))
    ),
    [gruposParticipantesDisponiveis]
  );



  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    if (!menuAcaoNegociacaoId) return;

    const handleOutsideMenuClick = (event) => {
      const target = event.target;
      if (target instanceof Element && target.closest('[data-negociacao-menu="true"]')) return;
      setMenuAcaoNegociacaoId(null);
    };

    document.addEventListener('mousedown', handleOutsideMenuClick);
    document.addEventListener('touchstart', handleOutsideMenuClick);

    return () => {
      document.removeEventListener('mousedown', handleOutsideMenuClick);
      document.removeEventListener('touchstart', handleOutsideMenuClick);
    };
  }, [menuAcaoNegociacaoId]);

  useEffect(() => {
    setBlockSubmit(false);
    if (conteudoEtapaRef.current) conteudoEtapaRef.current.scrollTop = 0;
    tituloEtapaRef.current?.focus();
    document.querySelector('.nw-steps [aria-current="step"]')?.scrollIntoView?.({ block: 'nearest', inline: 'center' });
  }, [currentSection, parteCliente, modalAberto]);

  const limparRascunho = () => {
    try { localStorage.removeItem(RASCUNHO_KEY); } catch (e) {}
  };

  const carregarNegociacoes = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API_CONFIG.API_URL}/negociacoes`, {
        headers: { 'x-auth-token': token }
      });
      if (res.data.success) {
        const lista = Array.isArray(res.data.data) ? res.data.data : [];
        setNegociacoes(lista);
        setChecklistModalNeg((atual) => {
          if (!atual?._id) return atual;
          return lista.find((neg) => neg._id === atual._id) || atual;
        });
      }
    } catch (e) {}
  };

  const excluirNegociacao = async (neg, e) => {
    e?.stopPropagation();
    if (!window.confirm(`Excluir esta negociação de ${neg.cliente?.nome || 'cliente'}?`)) return;
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API_CONFIG.API_URL}/negociacoes/${neg._id}`, {
        headers: { 'x-auth-token': token }
      });
      await carregarNegociacoes();
    } catch (err) {
      console.error('Erro ao excluir negociação:', err);
      alert('Não foi possível excluir a negociação.');
    }
  };

  const salvarRascunhoDireto = (dados) => {
    const tarefa = filaSalvamentoRef.current.then(async () => {
      setEstadoRascunho('Salvando…');
      const token = localStorage.getItem('token');
      const currentId = rascunhoIdRef.current;
      const config = { headers: { 'x-auth-token': token } };
      const res = currentId
        ? await axios.put(`${API_CONFIG.API_URL}/negociacoes/${currentId}`, dados, config)
        : await axios.post(`${API_CONFIG.API_URL}/negociacoes`, dados, config);
      if (!res.data.success || !res.data.data?._id) throw new Error('Não foi possível salvar a negociação.');
      rascunhoIdRef.current = res.data.data._id;
      atualizarNegociacaoLocal(res.data.data);
      rascunhoOriginalRef.current = JSON.stringify(dados);
      setEstadoRascunho('Rascunho salvo');
      return res.data.data;
    });
    filaSalvamentoRef.current = tarefa.catch(() => { setEstadoRascunho('Não foi possível salvar. Tente novamente ao sair.'); });
    return tarefa;
  };

  const atualizarNegociacaoLocal = salva => {
    setNegociacoes(lista => {
      const anterior = lista.find(n => n._id === salva._id);
      const atualizada = { ...anterior, ...salva };
      // POST/PUT devolvem o ID do autor; a listagem inicial devolve o nome populado.
      if (anterior?.criado_por?._id === salva.criado_por) atualizada.criado_por = anterior.criado_por;
      return anterior ? lista.map(n => n._id === salva._id ? atualizada : n) : [atualizada, ...lista];
    });
    setChecklistModalNeg(atual => atual?._id === salva._id ? { ...atual, ...salva } : atual);
  };

  const montarRascunhoAtual = () => {
    const tipo = tipoNegociacao();
    const { vendedor, loja, data, origemVenda, origemVendaDetalhe, participantes, ...clienteData } = formData;
    return {
      status: 'pendente',
      etapa_atual: Math.min(currentSection, 4),
      tipo: tipo || null,
      data: data || null,
      vendedor_responsavel: vendedor || '',
      loja_unidade: loja || '',
      origem_venda: origemVenda || '',
      origem_venda_detalhe: ['Web', 'Outros'].includes(origemVenda) ? (origemVendaDetalhe || '') : '',
      participantes: Array.isArray(participantes) ? participantes : [],
      cliente: clienteData,
      veiculos_vendidos: veiculosVendidos.map(({ id, ...v }) => v),
      veiculos_troca: trocas.map(({ id, ...t }) => t),
      produtos_agregados: produtosAgregados.map(({ id, ...p }) => p),
      pagamentos: pagamentos.map(({ id, ...p }) => p),
      recebimentos: recebimentos.map(({ id, ...r }) => r)
    };
  };

  const rascunhoNaoMudou = (dados) => rascunhoOriginalRef.current === JSON.stringify(dados);

  const abrirRascunho = (neg) => {
    consultaCpfRef.current += 1;
    setCarregando(false);
    setMensagemCpf('');
    setMenuAcaoNegociacaoId(null);
    setFormData({
      ...estadoInicial,
      ...(neg.cliente || {}),
      vendedor: neg.vendedor_responsavel || '',
      loja: neg.loja_unidade || '',
      data: neg.data ? neg.data.split('T')[0] : '',
      origemVenda: neg.origem_venda || '',
      origemVendaDetalhe: neg.origem_venda_detalhe || '',
      participantes: Array.isArray(neg.participantes) ? neg.participantes : []
    });
    setCepQuery((neg.cliente?.cep || '').replace(/\D/g, '').slice(0, 8));
    setErros({});
    setCurrentSection(Math.min(Math.max(Number(neg.etapa_atual) || 0, 0), 4));
    setParteCliente(0);
    setParticipantesAbertos(false);
    setParticipanteBusca('');
    setClienteManual(false);
    setPesquisaRealizada(!!(neg.cliente?.cpf));
    setClienteQuery('');
    setBlockSubmit(false);
    setVeiculosVendidos((neg.veiculos_vendidos || []).map((v, i) => ({
      ...veiculoVendidoVazio(Date.now() + i), ...v
    })));
    setTrocas((neg.veiculos_troca || []).map((t, i) => ({
      ...veiculoTrocaVazio(Date.now() + i), ...t, entrada_id: t.entrada_id || `${neg._id}-${i}`
    })));
    setProdutosAgregados((neg.produtos_agregados || []).map((p, i) => ({
      ...produtoAgregadoVazio(Date.now() + i), ...p
    })));
    setPagamentos((neg.pagamentos || []).map((p, i) => ({ ...p, id: Date.now() + i })));
    setRecebimentos((neg.recebimentos || []).map((r, i) => ({ ...r, financiamento_chave: r.financiamento_chave || `legado-${i}`, id: Date.now() + i })));
    rascunhoIdRef.current = neg._id;
    setSubModalAberto(false);
    setSubModalTrocaAberto(false);
    carregarVeiculos();
    setSelectedNegociacao(neg);
    rascunhoOriginalRef.current = JSON.stringify({
      status: neg.status || 'pendente',
      etapa_atual: neg.etapa_atual || 0,
      tipo: neg.tipo || null,
      data: neg.data || null,
      vendedor_responsavel: neg.vendedor_responsavel || '',
      loja_unidade: neg.loja_unidade || '',
      origem_venda: neg.origem_venda || '',
      origem_venda_detalhe: neg.origem_venda_detalhe || '',
      participantes: Array.isArray(neg.participantes) ? neg.participantes : [],
      cliente: neg.cliente || {},
      veiculos_vendidos: (neg.veiculos_vendidos || []).map(v => ({ ...v })),
      veiculos_troca: (neg.veiculos_troca || []).map(t => ({ ...t })),
      produtos_agregados: (neg.produtos_agregados || []).map(p => ({ ...p })),
      pagamentos: (neg.pagamentos || []).map(p => ({ ...p })),
      recebimentos: (neg.recebimentos || []).map(r => ({ ...r }))
    });
    setModalAberto(true);
  };

  // eslint-disable-next-line
  useEffect(() => { carregarNegociacoes(); }, []);

  useEffect(() => {
    if (!negociacaoInicialId) return;
    setFiltroBuscaNeg(String(negociacaoInicialId));
    setMesFiltroNeg('');
    setFiltroLojaNeg('');
    setFiltroStatusChecklist('');
    onNegociacaoInicialAberta?.();
  }, [negociacaoInicialId, onNegociacaoInicialAberta]);

  useEffect(() => {
    if (!modalAberto || finalizandoRef.current) return;
    const dados = montarRascunhoAtual();
    if (rascunhoIdRef.current && rascunhoNaoMudou(dados)) return;
    if (salvarDebounceRef.current) clearTimeout(salvarDebounceRef.current);
    salvarDebounceRef.current = setTimeout(async () => {
      try {
        await salvarRascunhoDireto(dados);
        rascunhoOriginalRef.current = JSON.stringify(dados);
      } catch (e) { console.error('Não foi possível salvar o rascunho:', e); }
      // Os financiamentos são sincronizados pelo servidor no próprio salvamento.
    }, 800);
    return () => { if (salvarDebounceRef.current) clearTimeout(salvarDebounceRef.current); };
  // eslint-disable-next-line
  }, [modalAberto, formData, veiculosVendidos, trocas, produtosAgregados, pagamentos, recebimentos, currentSection]);

  const carregarVeiculos = async () => {
    try {
      const token = localStorage.getItem('token');
      const [resV, resU, resB] = await Promise.all([
        axios.get(`${API_CONFIG.API_URL}/veiculos`, { headers: { 'x-auth-token': token } }),
        axios.get(`${API_CONFIG.API_URL}/usuarios`, { headers: { 'x-auth-token': token } }),
        axios.get(`${API_CONFIG.API_URL}/bancos`, { headers: { 'x-auth-token': token } })
      ]);
      if (resV.data.success) setVeiculos(resV.data.data);
      if (resU.data.success) setUsuariosLoja(resU.data.data || []);
      if (resB.data.success) setBancosLoja(resB.data.data || []);
    } catch (e) {}
  };

  // --- veículos vendidos ---
  const removerVeiculoVendido = (id) => {
    const veiculoRemovido = veiculosVendidos.find(v => v.id === id);
    setVeiculosVendidos(prev => prev.filter(v => v.id !== id));
    if (veiculoRemovido?.placa) {
      setProdutosAgregados(prev => prev.map(p =>
        p.veiculo_tipo === 'saida' && p.veiculo_placa === veiculoRemovido.placa
          ? { ...p, veiculo_tipo: '', veiculo_placa: '', veiculo_label: '' }
          : p
      ));
    }
  };

  const handleVeiculoVendidoValor = (id, valor) => {
    setVeiculosVendidos(prev => prev.map(v => v.id === id ? { ...v, valorVenda: valor } : v));
  };

  const filtrarVeiculosEstoque = (busca) => {
    if (!busca || !veiculos.length) return veiculos;
    const termos = busca.toLowerCase().trim().split(/\s+/).filter(t => t.length > 0);
    return veiculos.filter(v => {
      const texto = [v.marca, v.modelo, v.ano_modelo, v.ano_fabricacao, v.placa, v.cor].join(' ').toLowerCase();
      return termos.every(t => texto.includes(t));
    });
  };

  const limparConsultaPlaca = (tipo) => {
    consultaPlacaRef.current[tipo] += 1;
    setConsultaPlacaStatus(prev => ({ ...prev, [tipo]: {} }));
  };

  const buscarDadosPorPlaca = async (tipo) => {
    const formulario = tipo === 'venda' ? subModalForm : subModalTrocaForm;
    const atualizar = tipo === 'venda' ? setSubModalForm : setSubModalTrocaForm;
    const placa = formulario.placa.toUpperCase().replace(/[\s-]/g, '');
    if (!/^[A-Z]{3}\d[A-Z\d]\d{2}$/.test(placa)) {
      setConsultaPlacaStatus(prev => ({ ...prev, [tipo]: { mensagem: 'Informe uma placa válida, como ABC1234 ou ABC1D23.' } }));
      return;
    }
    const consulta = ++consultaPlacaRef.current[tipo];
    setConsultaPlacaStatus(prev => ({ ...prev, [tipo]: { carregando: true, mensagem: 'Consultando placa…' } }));
    try {
      const resposta = await axios.post(`${API_CONFIG.API_URL}/negociacoes/consultar-placa`, { placa }, {
        headers: { 'x-auth-token': localStorage.getItem('token') }, timeout: 65000
      });
      if (consulta !== consultaPlacaRef.current[tipo]) return;
      if (!resposta.data.success) throw new Error('Não foi possível consultar a placa. Preencha manualmente.');
      const dados = resposta.data.data;
      const campos = tipo === 'venda' ? {
        marca: dados.marca, modelo: dados.modelo, cor: dados.cor,
        ano: [dados.anoFabricacao, dados.anoModelo].filter(Boolean).join('/')
      } : {
        modelo: dados.marca && !dados.modelo?.toUpperCase().startsWith(dados.marca.toUpperCase())
          ? [dados.marca, dados.modelo].filter(Boolean).join(' ') : dados.modelo,
        cor: dados.cor, anoFabricacao: dados.anoFabricacao, anoModelo: dados.anoModelo,
        renavam: dados.renavam, chassis: dados.chassis
      };
      atualizar(prev => {
        if (prev.placa.toUpperCase().replace(/[\s-]/g, '') !== placa) return prev;
        // Preservar campos editados enquanto a consulta estava em andamento.
        const preenchidos = Object.fromEntries(Object.entries(campos).filter(([campo, valor]) => valor && prev[campo] === formulario[campo]));
        return { ...prev, ...preenchidos };
      });
      setConsultaPlacaStatus(prev => ({ ...prev, [tipo]: { mensagem: 'Dados encontrados. Confira e complete os campos restantes.' } }));
    } catch (erro) {
      if (consulta !== consultaPlacaRef.current[tipo]) return;
      setConsultaPlacaStatus(prev => ({ ...prev, [tipo]: { mensagem: erro.response?.data?.message || 'Não foi possível consultar a placa. Tente novamente ou preencha manualmente.' } }));
    }
  };

  // --- sub-modal ---
  const abrirSubModalNovo = () => {
    limparConsultaPlaca('venda');
    setSubModalForm(subFormVazio());
    setSubModalEditId(null);
    setSubModalAberto(true);
  };

  const abrirSubModalEditar = (v) => {
    limparConsultaPlaca('venda');
    setSubModalForm({
      marca: v.marca, modelo: v.modelo, ano: v.ano, cor: v.cor, placa: v.placa, km: v.km,
      temManual: v.temManual, temChaveReserva: v.temChaveReserva,
      _busca: '', _isManual: true, _mostrarOpcoes: false
    });
    setSubModalEditId(v.id);
    setSubModalAberto(true);
  };

  const fecharSubModal = () => { limparConsultaPlaca('venda'); setSubModalAberto(false); };

  const handleSubChange = (campo, valor) => {
    if (campo === 'placa') limparConsultaPlaca('venda');
    setSubModalForm(prev => ({ ...prev, [campo]: valor }));
  };

  const handleSelecionarEstoqueSubModal = (veiculo) => {
    limparConsultaPlaca('venda');
    const anoExibicao = veiculo.ano_fabricacao && veiculo.ano_modelo
      ? `${veiculo.ano_fabricacao}/${veiculo.ano_modelo}`
      : veiculo.ano_modelo || veiculo.ano_fabricacao || '';
    setSubModalForm(prev => ({
      ...prev,
      marca: veiculo.marca || '',
      modelo: veiculo.modelo || '',
      ano: anoExibicao,
      cor: veiculo.cor || '',
      placa: veiculo.placa || '',
      km: veiculo.quilometragem ? String(veiculo.quilometragem) : '',
      _precoVenda: veiculo.preco_venda != null ? formatarMoeda(String(Math.round(veiculo.preco_venda * 100))) : '',
      _busca: `${veiculo.marca} ${veiculo.modelo} - ${anoExibicao}`,
      _mostrarOpcoes: false
    }));
  };

  const salvarSubModal = () => {
    limparConsultaPlaca('venda');
    const campos = {
      marca: subModalForm.marca, modelo: subModalForm.modelo, ano: subModalForm.ano,
      cor: subModalForm.cor, placa: subModalForm.placa, km: subModalForm.km,
      temManual: subModalForm.temManual, temChaveReserva: subModalForm.temChaveReserva
    };
    if (subModalEditId) {
      setVeiculosVendidos(prev => prev.map(v =>
        v.id === subModalEditId ? { ...v, ...campos } : v
      ));
    } else {
      setVeiculosVendidos(prev => [...prev, {
        ...veiculoVendidoVazio(Date.now()),
        ...campos,
        valorVenda: subModalForm._precoVenda || ''
      }]);
    }
    setSubModalAberto(false);
  };

  const subModalValido = () =>
    subModalForm.marca.trim() && subModalForm.modelo.trim() && subModalForm.ano.trim() &&
    subModalForm.cor.trim() && subModalForm.placa.trim() && subModalForm.km.trim() &&
    subModalForm.temManual !== '-' && subModalForm.temChaveReserva !== '-';

  // --- veículos na troca ---
  const removerTroca = (id) => {
    const veiculoRemovido = trocas.find(t => t.id === id);
    setTrocas(prev => prev.filter(t => t.id !== id));
    if (veiculoRemovido?.placa) {
      setProdutosAgregados(prev => prev.map(p =>
        p.veiculo_tipo === 'entrada' && p.veiculo_placa === veiculoRemovido.placa
          ? { ...p, veiculo_tipo: '', veiculo_placa: '', veiculo_label: '' }
          : p
      ));
    }
  };

  const handleTrocaValor = (id, valor) => {
    setTrocas(prev => prev.map(t => t.id === id ? { ...t, valorTroca: valor } : t));
  };

  const abrirSubModalTrocaNovo = () => {
    limparConsultaPlaca('troca');
    setSubModalTrocaForm(subFormTrocaVazio());
    setSubModalTrocaEditId(null);
    setSubModalTrocaAberto(true);
  };

  const abrirSubModalTrocaEditar = (t) => {
    limparConsultaPlaca('troca');
    setSubModalTrocaForm({
      placa: t.placa, modelo: t.modelo, cor: t.cor, km: t.km,
      anoFabricacao: t.anoFabricacao, anoModelo: t.anoModelo,
      renavam: t.renavam, chassis: t.chassis,
      temManual: t.temManual, temChaveReserva: t.temChaveReserva,
      valorQuitacao: t.valorQuitacao, instituicaoQuitacao: t.instituicaoQuitacao || '', valorDebitos: t.valorDebitos
    });
    setSubModalTrocaEditId(t.id);
    setSubModalTrocaAberto(true);
  };

  const fecharSubModalTroca = () => { limparConsultaPlaca('troca'); setSubModalTrocaAberto(false); };

  const handleSubTrocaChange = (campo, valor) => {
    if (campo === 'placa') limparConsultaPlaca('troca');
    setSubModalTrocaForm(prev => ({ ...prev, [campo]: valor }));
  };

  const salvarSubModalTroca = () => {
    limparConsultaPlaca('troca');
    const campos = {
      placa: subModalTrocaForm.placa, modelo: subModalTrocaForm.modelo,
      cor: subModalTrocaForm.cor, km: subModalTrocaForm.km,
      anoFabricacao: subModalTrocaForm.anoFabricacao, anoModelo: subModalTrocaForm.anoModelo,
      renavam: subModalTrocaForm.renavam, chassis: subModalTrocaForm.chassis,
      temManual: subModalTrocaForm.temManual, temChaveReserva: subModalTrocaForm.temChaveReserva,
      valorQuitacao: subModalTrocaForm.valorQuitacao, instituicaoQuitacao: subModalTrocaForm.instituicaoQuitacao.trim(), valorDebitos: subModalTrocaForm.valorDebitos
    };
    if (subModalTrocaEditId) {
      setTrocas(prev => prev.map(t =>
        t.id === subModalTrocaEditId ? { ...t, ...campos } : t
      ));
    } else {
      setTrocas(prev => [...prev, { ...veiculoTrocaVazio(Date.now()), ...campos }]);
    }
    setSubModalTrocaAberto(false);
  };

  const subModalTrocaValido = () =>
    !!(subModalTrocaForm.placa.trim() && subModalTrocaForm.modelo.trim() &&
      subModalTrocaForm.anoFabricacao.trim() && subModalTrocaForm.anoModelo.trim() &&
      subModalTrocaForm.temManual !== '-' && subModalTrocaForm.temChaveReserva !== '-');

  const adicionarProdutoAgregado = () => {
    setProdutosAgregados(prev => [...prev, produtoAgregadoVazio(Date.now())]);
  };

  const removerProdutoAgregado = (id) => {
    setProdutosAgregados(prev => prev.filter(p => p.id !== id));
  };

  const atualizarProdutoAgregado = (id, campo, valor) => {
    setProdutosAgregados(prev => prev.map((produto) => {
      if (produto.id !== id) return produto;

      if (campo === 'tipo') {
        const preset = produtosAgregadosPadrao.find(p => p.tipo === valor) || produtosAgregadosPadrao[0];
        return {
          ...produto,
          tipo: preset.tipo,
          nome: preset.tipo === 'outros' ? '' : preset.nome,
          valor: preset.valor,
          veiculo_tipo: preset.exigeVeiculo ? produto.veiculo_tipo : '',
          veiculo_placa: preset.exigeVeiculo ? produto.veiculo_placa : '',
          veiculo_label: preset.exigeVeiculo ? produto.veiculo_label : ''
        };
      }

      if (campo === 'veiculo') {
        const [tipo, placa] = `${valor || ''}`.split(':');
        const veiculo = veiculosParaProdutosAgregados.find(v => v.tipo === tipo && v.placa === placa);
        return {
          ...produto,
          veiculo_tipo: veiculo?.tipo || '',
          veiculo_placa: veiculo?.placa || '',
          veiculo_label: veiculo?.label || ''
        };
      }

      return { ...produto, [campo]: valor };
    }));
  };

  const buscarDadosPorCPF = async (e) => {
    if (e) e.preventDefault();
    const cpf = formData.cpf.replace(/\D/g, '');
    if (cpf.length !== 11) {
      setMensagemCpf('Informe um CPF com 11 dígitos.');
      return;
    }
    const consulta = ++consultaCpfRef.current;
    setCarregando(true);
    setMensagemCpf('Consultando CPF…');
    const headers = { 'x-auth-token': localStorage.getItem('token') };
    try {
      const [api, local] = await Promise.allSettled([
        axios.post(`${API_CONFIG.API_URL}/negociacoes/consultar-cpf`, { cpf }, { headers, timeout: 35000 }),
        axios.get(`${API_CONFIG.API_URL}/fichas`, { headers, params: { search: cpf, limit: 1 }, timeout: 15000 })
      ]);
      if (consulta !== consultaCpfRef.current) return;
      const fichas = local.status === 'fulfilled' && Array.isArray(local.value.data.data) ? local.value.data.data : [];
      const cliente = fichas.find(f => (f.cliente?.cpf || '').replace(/\D/g, '') === cpf)?.cliente;
      const endereco = cliente?.endereco || {};
      const dadosLocais = cliente ? {
        nome: cliente.nome, rg: cliente.rg, profissao: cliente.profissao,
        telefone: cliente.celular || cliente.telefone, cep: endereco.cep,
        rua: endereco.rua, numero: endereco.numero, bairro: endereco.bairro,
        cidadeEstado: [endereco.cidade, endereco.estado].filter(Boolean).join(' / ')
      } : {};
      const dadosApi = api.status === 'fulfilled' && api.value.data.success ? api.value.data.data : {};
      const dados = Object.fromEntries(Object.entries({
        ...Object.fromEntries(Object.entries(dadosLocais).filter(([, valor]) => valor)),
        ...dadosApi
      }).filter(([, valor]) => valor !== undefined && valor !== null && valor !== ''));
      setFormData(prev => prev.cpf.replace(/\D/g, '') === cpf ? { ...prev, ...dados } : prev);
      if (dados.cep) setCepQuery(String(dados.cep).replace(/\D/g, '').slice(0, 8));
      setPesquisaRealizada(true);
      setMensagemCpf(api.status === 'fulfilled' && api.value.data.success
        ? 'Dados encontrados. Confira e complete os campos restantes.'
        : `${api.reason?.response?.data?.message || 'Não foi possível consultar o CPF. Preencha manualmente.'}${cliente ? ' Dados da ficha existente foram preenchidos.' : ''}`);
    } finally {
      if (consulta === consultaCpfRef.current) setCarregando(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value,
      ...(name === 'origemVenda' && !['Web', 'Outros'].includes(value) ? { origemVendaDetalhe: '' } : {})
    }));

    if (name === 'cpf') {
      consultaCpfRef.current += 1;
      setCarregando(false);
      setMensagemCpf('');
      setPesquisaRealizada(clienteManual);
      if (value.replace(/\D/g, '') !== formData.cpf.replace(/\D/g, '')) {
        setFormData(prev => ({ ...prev, ...Object.fromEntries(
          ['nome', 'rg', 'profissao', 'estadoCivil', 'cep', 'rua', 'numero', 'bairro', 'cidadeEstado', 'telefone'].map(campo => [campo, ''])
        ) }));
        setCepQuery('');
      }
    }

    if (erros[name]) {
      setErros(prev => ({ ...prev, [name]: false }));
    }
  };

  const adicionarParticipante = (valor) => {
    const nome = `${valor || ''}`.trim();
    if (!nome) return;

    setFormData(prev => {
      const atuais = Array.isArray(prev.participantes) ? prev.participantes : [];
      if (atuais.includes(nome)) return prev;
      return { ...prev, participantes: [...atuais, nome] };
    });
    setParticipanteBusca('');
  };

  const removerParticipante = (nome) => {
    setFormData(prev => ({
      ...prev,
      participantes: (Array.isArray(prev.participantes) ? prev.participantes : []).filter((participante) => participante !== nome)
    }));
  };

  const camposStep0Preenchidos = () =>
    pesquisaRealizada &&
    formData.vendedor.trim() !== '' &&
    formData.loja.trim() !== '' &&
    formData.cpf.trim() !== '' &&
    formData.nome.trim() !== '' &&
    formData.rg.trim() !== '' &&
    formData.profissao.trim() !== '' &&
    formData.estadoCivil.trim() !== '' &&
    formData.rua.trim() !== '' &&
    formData.cidadeEstado.trim() !== '' &&
    formData.telefone.trim() !== '';

  const camposStep1Preenchidos = () =>
    veiculosVendidos.every(v =>
      v.marca.trim() && v.modelo.trim() && v.ano.trim() &&
      v.cor.trim() && v.placa.trim() && v.km.trim() &&
      v.temManual !== '-' && v.temChaveReserva !== '-'
    );

  const tipoNegociacao = () => {
    const temSaindo = veiculosVendidos.length > 0;
    const temEntrando = trocas.length > 0;
    if (temSaindo && temEntrando) return 'troca';
    if (!temSaindo && temEntrando) return 'compra';
    if (temSaindo && !temEntrando) return 'venda';
    return null;
  };

  const produtosAgregadosValidos = () =>
    produtosAgregados.every((produto) => {
      if (produto.tipo === 'vistoria_transferencia' && !produto.veiculo_placa) return false;
      if (produto.tipo === 'outros' && (!`${produto.nome || ''}`.trim() || parseMoeda(produto.valor || '') <= 0)) return false;
      return true;
    });

  const camposParteCliente = [
    ['cpf', 'nome', 'rg', 'telefone', 'profissao', 'estadoCivil'],
    ['rua', 'cidadeEstado'],
    ['vendedor', 'loja']
  ];
  const proximoBloqueado = () => carregando;

  const validarSecao = (idx) => {
    const novosErros = {};
    if (idx === 0) {
      camposParteCliente[parteCliente].forEach(campo => {
        if (!`${formData[campo] || ''}`.trim()) novosErros[campo] = true;
      });
      if (!pesquisaRealizada) novosErros.cpf = true;
    }
    if (idx === 1 && !camposStep1Preenchidos()) novosErros.etapa = 'Complete os dados dos veículos adicionados.';
    if (idx === 2 && !tipoNegociacao()) novosErros.etapa = 'Adicione ao menos um veículo de venda ou de entrada para continuar.';
    if (idx === 3 && !produtosAgregadosValidos()) novosErros.etapa = 'Confira o veículo vinculado e os valores dos adicionais.';
    setErros(novosErros);
    return Object.keys(novosErros).length === 0;
  };

  const goToNextSection = () => {
    if (!validarSecao(currentSection)) return;
    if (currentSection === 0 && parteCliente < 2) setParteCliente(prev => prev + 1);
    else setCurrentSection(prev => Math.min(prev + 1, sections.length - 1));
  };

  const goToPreviousSection = () => {
    setErros({});
    if (currentSection === 0) setParteCliente(prev => Math.max(prev - 1, 0));
    else {
      if (currentSection === 1) setParteCliente(2);
      setCurrentSection(prev => Math.max(prev - 1, 0));
    }
  };

  const abrirModal = () => {
    consultaCpfRef.current += 1;
    setCarregando(false);
    setMensagemCpf('');
    setFormData({ ...estadoInicial, data: hojeLocalISO() });
    setErros({});
    setCurrentSection(0);
    setParteCliente(0);
    setParticipantesAbertos(false);
    setParticipanteBusca('');
    setClienteManual(false);
    setEstadoRascunho('');
    setPesquisaRealizada(false);
    setClienteQuery('');
    setBlockSubmit(false);
    setVeiculosVendidos([]);
    setTrocas([]);
    setProdutosAgregados([]);
    setPagamentos([]);
    setRecebimentos([]);
    setSubModalAberto(false);
    setSubModalTrocaAberto(false);
    limparRascunho();
    rascunhoIdRef.current = null;
    setCepQuery('');
    setEnderecoManual(false);
    setSelectedNegociacao(null);
    carregarVeiculos();
    setModalAberto(true);
  };

  const handleClose = async ({ descartar = false } = {}) => {
    clearTimeout(salvarDebounceRef.current);
    const estavaFinalizando = finalizandoRef.current;
    finalizandoRef.current = true;
    try {
      await filaSalvamentoRef.current;
      // Não perder a última edição quando o X é clicado antes dos 800 ms.
      const dados = montarRascunhoAtual();
      if (!descartar && !estavaFinalizando && !rascunhoNaoMudou(dados)) await salvarRascunhoDireto(dados);
    } catch (err) {
      finalizandoRef.current = estavaFinalizando;
      alert(err.response?.data?.message || 'Não foi possível salvar. A negociação continua aberta para tentar novamente.');
      return;
    }
    setModalAberto(false);
    consultaCpfRef.current += 1;
    setCarregando(false);
    setMensagemCpf('');
    setFormData({ ...estadoInicial, data: hojeLocalISO() });
    setErros({});
    setCurrentSection(0);
    setParteCliente(0);
    setParticipantesAbertos(false);
    setParticipanteBusca('');
    setClienteManual(false);
    setEstadoRascunho('');
    setPesquisaRealizada(false);
    setVeiculosVendidos([]);
    setTrocas([]);
    setProdutosAgregados([]);
    setPagamentos([]);
    setRecebimentos([]);
    setSubModalAberto(false);
    setSubModalTrocaAberto(false);
    rascunhoIdRef.current = null;
    rascunhoOriginalRef.current = null;
    setCepQuery('');
    setEnderecoManual(false);
    setSelectedNegociacao(null);
    finalizandoRef.current = estavaFinalizando;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (blockSubmit) return;
    if (currentSection !== sections.length - 1) return;
    if (finalizandoRef.current) return;
    if (!camposStep0Preenchidos()) {
      setCurrentSection(0); setParteCliente(0);
      setErros({ etapa: 'Confira os dados obrigatórios do cliente e da negociação antes de concluir.' });
      return;
    }
    if (!camposStep1Preenchidos() || !tipoNegociacao()) {
      setCurrentSection(1); setErros({ etapa: 'Confira os veículos antes de concluir.' }); return;
    }
    if (!produtosAgregadosValidos()) {
      setCurrentSection(3); setErros({ etapa: 'Confira os adicionais antes de concluir.' }); return;
    }
    finalizandoRef.current = true;
    clearTimeout(salvarDebounceRef.current);
    const dadosBase = montarRascunhoAtual();
    setCarregando(true);
    try {
      await filaSalvamentoRef.current;
      const dados = {
        ...dadosBase,
        status: 'pronto',
        etapa_atual: 4
      };
      await salvarRascunhoDireto(dados);
      await handleClose();
    } catch (err) {
      console.error('Erro ao concluir negociação:', err);
      alert(err.response?.data?.message || 'Não foi possível concluir a negociação. Tente novamente.');
    } finally {
      finalizandoRef.current = false;
      setCarregando(false);
    }
  };

  const section = sections[currentSection];

  const normalizarLoja = (valor) => `${valor || ''}`.trim().toLowerCase().replace(/\s+/g, '');

  const negociacoesFiltradas = negociacoes.filter((neg) => {
    if (!neg) return false;

    if (filtroStatusChecklist) {
      const resumoChecklist = obterResumoChecklist(neg);
      if (filtroStatusChecklist === 'completo' && !resumoChecklist.completo) return false;
      if (filtroStatusChecklist === 'pendencia' && resumoChecklist.completo) return false;
    }

    if (filtroLojaNeg) {
      const lojaNeg = normalizarLoja(neg.loja_unidade);
      const lojaFiltro = normalizarLoja(filtroLojaNeg);
      if (lojaNeg !== lojaFiltro) return false;
    }

    if (filtroBuscaNeg.trim()) {
      const termo = filtroBuscaNeg.trim().toLowerCase();
      const texto = [
        neg.cliente?.nome,
        neg.cliente?.cpf,
        neg.vendedor_responsavel,
        neg.loja_unidade,
        neg.numero,
        neg._id,
        ...(neg.veiculos_vendidos || []).map(v => [v.marca, v.modelo, v.placa, v.ano, v.anoFabricacao, v.anoModelo, v.cor].filter(Boolean).join(' ')),
        ...(neg.veiculos_troca || []).map(v => [v.marca, v.modelo, v.placa, v.ano, v.anoFabricacao, v.anoModelo, v.cor].filter(Boolean).join(' '))
      ].filter(Boolean).join(' ').toLowerCase();
      if (!texto.includes(termo)) return false;
    }

    if (mesFiltroNeg) {
      const mesNegociacao = obterMesCalendario(neg.data);
      if (!mesNegociacao) return false;
      if (mesNegociacao !== mesFiltroNeg) return false;
    }

    return true;
  });

  const negociacoesOrdenadas = [...negociacoesFiltradas].sort((a, b) => {
    const [field, dir] = sortNeg.split('_');
    let va;
    let vb;

    if (field === 'data') {
      va = obterTimestampCalendario(a.data);
      vb = obterTimestampCalendario(b.data);
    } else if (field === 'cliente') {
      va = (a.cliente?.nome || '').toLowerCase();
      vb = (b.cliente?.nome || '').toLowerCase();
    } else if (field === 'vendedor') {
      va = (a.vendedor_responsavel || '').toLowerCase();
      vb = (b.vendedor_responsavel || '').toLowerCase();
    } else if (field === 'tipo') {
      va = (a.tipo || '').toLowerCase();
      vb = (b.tipo || '').toLowerCase();
    } else if (field === 'status') {
      va = (a.status || '').toLowerCase();
      vb = (b.status || '').toLowerCase();
    } else {
      va = obterTimestampCalendario(a.data);
      vb = obterTimestampCalendario(b.data);
    }

    if (va < vb) return dir === 'desc' ? 1 : -1;
    if (va > vb) return dir === 'desc' ? -1 : 1;
    return 0;
  });

  const mesesDisponiveis = [];
  const mesesVistos = new Set();
  const mesAtualFiltro = obterMesAtualFiltro();
  const dataMesAtual = new Date(`${mesAtualFiltro}-01T00:00:00`);
  mesesVistos.add(mesAtualFiltro);
  mesesDisponiveis.push({
    valor: mesAtualFiltro,
    label: dataMesAtual.toLocaleDateString('pt-BR', { month: 'long', year: 'numeric' })
  });
  negociacoes.forEach((neg) => {
    const dataISO = obterDataCalendarioISO(neg.data);
    if (!dataISO) return;
    const valor = dataISO.slice(0, 7);
    if (mesesVistos.has(valor)) return;
    const [ano, mes] = valor.split('-').map(Number);
    mesesVistos.add(valor);
    mesesDisponiveis.push({
      valor,
      label: new Date(ano, mes - 1, 1).toLocaleDateString('pt-BR', { month: 'long', year: 'numeric' })
    });
  });
  mesesDisponiveis.sort((a, b) => b.valor.localeCompare(a.valor));

  const vendedoresRelatorio = Array.from(new Set(
    [
      ...negociacoes.map((neg) => neg?.vendedor_responsavel),
      ...usuariosLoja.map((usuario) => usuario?.nome)
    ]
      .map((nome) => `${nome || ''}`.trim())
      .filter(Boolean)
  )).sort((a, b) => a.localeCompare(b, 'pt-BR'));

  const abrirRelatorioVendedor = () => {
    const hoje = new Date();
    const mesAtual = `${hoje.getFullYear()}-${String(hoje.getMonth() + 1).padStart(2, '0')}`;
    setRelatorioMes(mesFiltroNeg || mesAtual);
    setRelatorioVendedor((atual) =>
      atual || (vendedoresRelatorio.length === 1 ? vendedoresRelatorio[0] : VENDEDOR_TODOS)
    );
    setRelatorioFormato('pdf');
    setRelatorioColunas({
      valorFinanciado: false,
      bancoFinanciado: false,
      retornoPercentual: false,
      retornoLiquido: false,
      retornoCincoPorcento: false
    });
    setRelatorioErro('');
    console.log('Abrindo modal de relatório');
    setRelatorioModalAberto(true);
  };

  const baixarRelatorioVendedor = async () => {
    if (!relatorioVendedor || !relatorioMes || relatorioGerando) return;

    setRelatorioGerando(true);
    setRelatorioErro('');
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_CONFIG.API_URL}/negociacoes/relatorios/vendedor`, {
        headers: { 'x-auth-token': token },
        params: {
          vendedor: relatorioVendedor,
          mes: relatorioMes,
          formato: relatorioFormato,
          valorFinanciado: relatorioFormato === 'xlsx' ? relatorioColunas.valorFinanciado : false,
          bancoFinanciado: relatorioFormato === 'xlsx' ? relatorioColunas.bancoFinanciado : false,
          retornoPercentual: relatorioFormato === 'xlsx' ? relatorioColunas.retornoPercentual : false,
          retornoLiquido: relatorioFormato === 'xlsx' ? relatorioColunas.retornoLiquido : false,
          retornoCincoPorcento: relatorioFormato === 'xlsx' ? relatorioColunas.retornoCincoPorcento : false
        },
        responseType: 'blob'
      });
      const contentDisposition = response.headers?.['content-disposition'] || '';
      const nomeHeader = contentDisposition.match(/filename="?([^";]+)"?/i)?.[1];
      const nomeBase = relatorioVendedor === VENDEDOR_TODOS
        ? 'todos'
        : relatorioVendedor
          .normalize('NFD')
          .replace(/[\u0300-\u036f]/g, '')
          .replace(/[^a-zA-Z0-9]+/g, '_')
          .replace(/^_+|_+$/g, '')
          .toLowerCase();
      const extensao = relatorioFormato === 'xlsx' ? 'xlsx' : 'pdf';
      const mimeType = relatorioFormato === 'xlsx'
        ? 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
        : 'application/pdf';
      const nomeArquivo = nomeHeader || `negociacoes_${nomeBase || 'vendedor'}_${relatorioMes}.${extensao}`;
      const url = window.URL.createObjectURL(new Blob([response.data], { type: mimeType }));
      const link = document.createElement('a');
      link.href = url;
      link.download = nomeArquivo;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.setTimeout(() => window.URL.revokeObjectURL(url), 1000);
      setRelatorioModalAberto(false);
    } catch (error) {
      let mensagem = 'Não foi possível gerar o relatório.';
      const dadosErro = error.response?.data;
      if (dadosErro instanceof Blob) {
        try {
          const jsonErro = JSON.parse(await dadosErro.text());
          mensagem = jsonErro.message || mensagem;
        } catch {}
      } else if (dadosErro?.message) {
        mensagem = dadosErro.message;
      }
      setRelatorioErro(mensagem);
    } finally {
      setRelatorioGerando(false);
    }
  };

  const imprimirNegociacao = async (neg, e) => {
    return imprimirFolhaRostoNegociacao(neg, e);
  };

  const imprimirFolhaRostoNegociacao = async (neg, e) => {
    e?.stopPropagation();
    setMenuAcaoNegociacaoId(null);

    const janelaImpressao = window.open('', '_blank');
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_CONFIG.API_URL}/negociacoes/${neg._id}/folha-rosto`, {
        headers: { 'x-auth-token': token },
        responseType: 'blob'
      });

      const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
      if (janelaImpressao) {
        janelaImpressao.location.href = url;
      } else {
        window.open(url, '_blank');
      }
      window.setTimeout(() => window.URL.revokeObjectURL(url), 120000);
    } catch (error) {
      if (janelaImpressao && !janelaImpressao.closed) janelaImpressao.close();
      let mensagem = 'Não foi possível gerar a folha de rosto.';
      const dadosErro = error.response?.data;
      if (dadosErro instanceof Blob) {
        try {
          const jsonErro = JSON.parse(await dadosErro.text());
          mensagem = jsonErro.message || mensagem;
        } catch {}
      } else if (dadosErro?.message) {
        mensagem = dadosErro.message;
      }
      alert(mensagem);
    }
  };


  const imprimirDocumentosSelecionados = async () => {
    if (!impressaoModalNeg || !documentosImpressao.length || gerandoImpressaoRef.current) return;
    gerandoImpressaoRef.current = true;
    setGerandoImpressao(true);
    setErroImpressao('');
    let janela;
    try {
      janela = window.open('', '_blank');
      const response = await axios.post(`${API_CONFIG.API_URL}/negociacoes/${impressaoModalNeg._id}/impressao/pdf`, {
        documentos: DOCUMENTOS_IMPRESSAO.filter(documento => documentosImpressao.includes(documento.id)).map(documento => documento.id)
      }, { headers: { 'x-auth-token': localStorage.getItem('token') }, responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
      if (janela && !janela.closed) janela.location.href = url;
      else {
        const link = document.createElement('a');
        link.href = url;
        link.download = 'documentos_selecionados.pdf';
        document.body.appendChild(link);
        link.click();
        link.remove();
      }
      window.setTimeout(() => window.URL.revokeObjectURL(url), 120000);
    } catch (error) {
      if (janela && !janela.closed) janela.close();
      let mensagem = 'Não foi possível gerar os documentos selecionados.';
      const dadosErro = error.response?.data;
      if (dadosErro instanceof Blob) {
        try { mensagem = JSON.parse(await dadosErro.text()).message || mensagem; } catch {}
      } else if (dadosErro?.message) mensagem = dadosErro.message;
      setErroImpressao(mensagem);
    } finally {
      gerandoImpressaoRef.current = false;
      setGerandoImpressao(false);
    }
  };

  const quantidadeFiltrosAtivos = [
    filtroLojaNeg,
    filtroStatusChecklist,
    mesFiltroNeg,
    sortNeg !== 'data_desc' ? sortNeg : ''
  ].filter(Boolean).length;

  return (
    <div className="content-section negociacoes-page">
      <div className="negociacoes-fixed-panel">
        <div className="page-header-mobile">
          <button className="mobile-menu-toggle" onClick={onToggleSidebar}>☰</button>
          <h1>
            Negociações
            <span className="negociacoes-titulo-contador">( {negociacoesFiltradas.length} )</span>
          </h1>
        </div>

        <div className="section-actions negociacoes-toolbar-actions" style={{ alignItems: 'center', flexDirection: isMobile ? 'column' : 'row' }}>
          <input
            className="negociacoes-busca"
            type="text"
            value={filtroBuscaNeg}
            onChange={e => setFiltroBuscaNeg(e.target.value)}
            placeholder="Pesquisar cliente, vendedor, CPF, placa ou veículo..."
            style={{ padding: '8px 12px', border: '1px solid #e2e8f0', borderRadius: '6px', fontSize: '0.85rem', color: '#334155', background: '#fff', minWidth: isMobile ? '100%' : '280px', width: isMobile ? '100%' : 'auto' }}
          />

          <button
            type="button"
            className={`negociacoes-filtros-toggle ${filtrosMobileAbertos ? 'open' : ''}`}
            onClick={() => setFiltrosMobileAbertos(prev => !prev)}
            aria-expanded={filtrosMobileAbertos}
            aria-controls="negociacoes-filtros-mobile"
          >
            <span className="negociacoes-filtros-toggle-label">
              Filtros
              {quantidadeFiltrosAtivos > 0 && <strong>{quantidadeFiltrosAtivos}</strong>}
            </span>
            <span className="negociacoes-filtros-toggle-chevron" aria-hidden="true">⌄</span>
          </button>

          <div
            id="negociacoes-filtros-mobile"
            className={`negociacoes-filtros-opcoes ${filtrosMobileAbertos ? 'open' : ''}`}
          >
            <select value={filtroLojaNeg} onChange={e => setFiltroLojaNeg(e.target.value)}>
              <option value=''>Todas as lojas</option>
              <option value='Loja 1'>Loja 1</option>
              <option value='Loja 3'>Loja 3</option>
            </select>
            <select value={filtroStatusChecklist} onChange={e => setFiltroStatusChecklist(e.target.value)}>
              <option value=''>Status checklist</option>
              <option value='pendencia'>Com pendência</option>
              <option value='completo'>Completo</option>
            </select>
            <select value={mesFiltroNeg} onChange={e => setMesFiltroNeg(e.target.value)} style={{ textTransform: 'capitalize' }}>
              <option value=''>Todos os meses</option>
              {mesesDisponiveis.map(o => <option key={o.valor} value={o.valor} style={{ textTransform: 'capitalize' }}>{o.label}</option>)}
            </select>
            <select value={sortNeg} onChange={e => setSortNeg(e.target.value)}>
              <option value='data_desc'>Data ↓</option>
              <option value='data_asc'>Data ↑</option>
              <option value='cliente_asc'>Cliente A→Z</option>
              <option value='cliente_desc'>Cliente Z→A</option>
              <option value='vendedor_asc'>Vendedor A→Z</option>
              <option value='tipo_asc'>Tipo</option>
              <option value='status_asc'>Status</option>
            </select>
          </div>
          <div className="negociacoes-toolbar-buttons">
            <button
              type="button"
              className="btn-secondary negociacoes-relatorio-button"
              onClick={abrirRelatorioVendedor}
              disabled={negociacoes.length === 0}
              title={negociacoes.length === 0 ? 'Nenhuma negociação disponível' : 'Exportar relatório mensal por vendedor ou de todos'}
            >
              <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                <polyline points="14 2 14 8 20 8" />
                <line x1="12" y1="18" x2="12" y2="12" />
                <polyline points="9 15 12 18 15 15" />
              </svg>
              Relatório
            </button>
            <button
              className="btn-primary negociacoes-nova-button"
              onClick={abrirModal}
              style={{ padding: '12px 24px', fontSize: '1rem', fontWeight: '600', display: 'flex', alignItems: 'center', gap: '8px', width: isMobile ? '100%' : 'auto', justifyContent: 'center' }}
            >
              Nova negociação
            </button>
          </div>
        </div>
      </div>

      {/* Lista de negociações */}
      <div className="negociacoes-scroll-area" style={{ marginTop: '1.5rem' }}>
        {negociacoesFiltradas.length === 0 ? (
          <div style={{ textAlign: 'center', color: '#aaa', padding: '3rem', border: '2px dashed #e2e8f0', borderRadius: '8px', fontSize: '0.9rem' }}>
            Nenhuma negociação encontrada.
          </div>
        ) : (
          <div className="negociacoes-cards" style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              {negociacoesOrdenadas.map(neg => {
                const descreverVeiculoLista = (veiculo = {}) => {
                  const ano = veiculo.ano || [veiculo.anoFabricacao, veiculo.anoModelo].filter(Boolean).join('/');
                  const nome = [veiculo.marca, veiculo.modelo, ano].filter(Boolean).join(' ').trim() || 'Veículo';
                  return { nome, placa: veiculo.placa || '' };
                };
                const veiculosSaindo = (neg.veiculos_vendidos || []).map(descreverVeiculoLista);
                const veiculosEntrando = (neg.veiculos_troca || []).map(descreverVeiculoLista);
                const vendedorNome = (neg.vendedor_responsavel || '').trim();
                const lojaNome = (neg.loja_unidade || '').trim();
                const linhaVendedorLoja = [vendedorNome || 'Sem vendedor', lojaNome || 'Sem loja'].join(' | ');
                const fichaCompleta = `${neg.status || ''}`.trim().toLowerCase() === 'pronto';
                const etapaFinalAtingida = Number(neg.etapa_atual || 0) >= 4;
                const checklistLiberado = fichaCompleta || etapaFinalAtingida;
                const resumoChecklist = obterResumoChecklist(neg);
                const promissoriasPeriodo = classificarPromissorias(neg);
                const statusChecklistCor = resumoChecklist.completo
                  ? { bg: '#dcfce7', fg: '#15803d', bd: '#86efac', icon: '✓', label: 'Completo' }
                  : { bg: '#fef3c7', fg: '#b45309', bd: '#fcd34d', icon: '⚠', label: 'Com pendência' };

                return (
                  <div
                    key={neg._id}
                    style={{
                      overflow: 'visible',
                      background: '#ffffff',
                      border: '1px solid #e5e7eb',
                      borderRadius: '12px',
                      padding: '14px 18px',
                      display: 'flex',
                      flexDirection: 'column',
                      gap: '0',
                      cursor: 'default',
                      transition: 'transform 0.16s ease, box-shadow 0.16s ease, border-color 0.16s ease',
                      boxShadow: '0 1px 2px rgba(15, 23, 42, 0.06)'
                    }}
                  >
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '12px' }}>
                      <div style={{ display: 'flex', alignItems: 'center', flexShrink: 0 }}>
                        {neg.documentacao_iniciada ? (
                          <div
                            title={`Liberado para documentação em ${formatarDataCurtaEnvio(neg.documentacao_enviada_em || neg.updatedAt || neg.createdAt)}`}
                            style={{
                              width: '22px',
                              height: '22px',
                              borderRadius: '999px',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              fontSize: '0.75rem',
                              background: '#eff6ff',
                              color: '#1e40af',
                              border: '1px solid #bfdbfe',
                              flexShrink: 0,
                              cursor: 'default'
                            }}
                          >
                            📄
                          </div>
                        ) : (
                          <div
                            title={`Checklist ${statusChecklistCor.label}: ${resumoChecklist.anexados}/${resumoChecklist.total} (${resumoChecklist.percentual}%)`}
                            style={{
                              width: '22px',
                              height: '22px',
                              borderRadius: '999px',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              fontSize: '0.85rem',
                              fontWeight: '800',
                              background: statusChecklistCor.bg,
                              color: statusChecklistCor.fg,
                              border: `1px solid ${statusChecklistCor.bd}`,
                              flexShrink: 0
                            }}
                          >
                            {statusChecklistCor.icon}
                          </div>
                        )}
                      </div>

                      <div style={{ minWidth: 0, flex: 1 }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', color: '#6b7280', fontSize: '0.82rem', lineHeight: 1.2, marginBottom: '6px', flexWrap: 'wrap' }}>
                          <span>{formatarDataCurta(neg.data)}</span>
                        </div>
                        <div style={{ fontWeight: '800', fontSize: '1.02rem', color: '#111827', lineHeight: 1.15, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                          {neg.cliente?.nome || '—'}
                        </div>
                        <ResumoVeiculosNegociacao saidas={veiculosSaindo} entradas={veiculosEntrando} />
                        <div style={{ marginTop: '4px', fontSize: '0.8rem', color: '#64748b', lineHeight: 1.25, whiteSpace: isMobile ? 'normal' : 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                          {linhaVendedorLoja}
                        </div>
                      </div>

                      <div data-negociacao-menu="true" style={{ display: 'flex', alignItems: 'center', gap: '10px', flexShrink: 0, position: 'relative', alignSelf: 'center' }}>
                        {(promissoriasPeriodo.noMes || promissoriasPeriodo.semPeriodo) && (
                          <IconePromissoriaNegociacao titulo={promissoriasPeriodo.noMes ? 'Promissória com vencimento no mês da negociação' : 'Possui nota promissória — confira a configuração dos vencimentos'} />
                        )}
                        {promissoriasPeriodo.posteriores && (
                          <IconePromissoriaNegociacao futura titulo="Promissória com vencimento após o mês da negociação" />
                        )}
                        <button
                          type="button"
                          aria-label="Mais opções"
                          onClick={e => {
                            e.stopPropagation();
                            setMenuAcaoNegociacaoId(prev => (prev === neg._id ? null : neg._id));
                          }}
                          style={{ border: 'none', background: 'transparent', padding: 0, cursor: 'pointer', color: '#2563eb', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                        >
                          <svg width="23" height="23" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                            <circle cx="12" cy="5" r="2" />
                            <circle cx="12" cy="12" r="2" />
                            <circle cx="12" cy="19" r="2" />
                          </svg>
                        </button>

                        {menuAcaoNegociacaoId === neg._id && (
                          <div
                            onClick={e => e.stopPropagation()}
                            style={{ position: 'absolute', top: '26px', right: 0, zIndex: 25, minWidth: '210px', background: '#fff', border: '1px solid #e2e8f0', borderRadius: '10px', boxShadow: '0 10px 22px rgba(15, 23, 42, 0.16)', padding: '6px' }}
                          >
                            <button
                              type="button"
                              onClick={() => {
                                if (neg.documentacao_iniciada) {
                                  alert('Esta negociação já iniciou a documentação e sua edição está desabilitada.');
                                  setMenuAcaoNegociacaoId(null);
                                  return;
                                }
                                abrirRascunho(neg);
                                setMenuAcaoNegociacaoId(null);
                              }}
                              style={{ width: '100%', textAlign: 'left', border: 'none', background: '#fff', color: '#1e293b', cursor: 'pointer', padding: '9px 10px', borderRadius: '7px', fontSize: '0.84rem', fontWeight: '600', display: 'flex', alignItems: 'center', gap: '8px' }}
                              onMouseEnter={e => { e.currentTarget.style.background = '#f8fafc'; }}
                              onMouseLeave={e => { e.currentTarget.style.background = '#fff'; }}
                            >
                              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" aria-hidden="true" style={{ flexShrink: 0, color: 'currentColor' }}>
                                <path d="M3 17.25V21h3.75L19.81 7.94l-3.75-3.75L3 17.25z" stroke="currentColor" strokeWidth="1.8" strokeLinejoin="round" />
                                <path d="M14.06 4.19l3.75 3.75" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
                              </svg>
                              <span><strong>Editar</strong></span>
                            </button>

                            {false && (<button
                              type="button"
                              onClick={(e) => imprimirFolhaRostoNegociacao(neg, e)}
                              style={{ width: '100%', textAlign: 'left', border: 'none', background: '#fff', color: '#1e293b', cursor: 'pointer', padding: '9px 10px', borderRadius: '7px', fontSize: '0.84rem', fontWeight: '600', display: 'flex', alignItems: 'center', gap: '8px' }}
                              onMouseEnter={e => { e.currentTarget.style.background = '#f8fafc'; }}
                              onMouseLeave={e => { e.currentTarget.style.background = '#fff'; }}
                            >
                              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" aria-hidden="true" style={{ flexShrink: 0, color: 'currentColor' }}>
                                <path d="M6 9V3h12v6" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
                                <path d="M6 18H4a2 2 0 0 1-2-2v-5a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v5a2 2 0 0 1-2 2h-2" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
                                <path d="M6 14h12v7H6z" stroke="currentColor" strokeWidth="1.8" strokeLinejoin="round" />
                              </svg>
                              <span><strong>Imprimir folha de rosto</strong></span>
                            </button>)}

                            <button
                              type="button"
                              onClick={(e) => { e.stopPropagation(); setChecklistModalNeg(neg); setMenuAcaoNegociacaoId(null); }}
                              style={{ width: '100%', textAlign: 'left', border: 'none', background: '#fff', color: '#1e293b', cursor: 'pointer', padding: '9px 10px', borderRadius: '7px', fontSize: '0.84rem', fontWeight: '600', display: 'flex', alignItems: 'center', gap: '8px' }}
                              onMouseEnter={e => { e.currentTarget.style.background = '#f8fafc'; }}
                              onMouseLeave={e => { e.currentTarget.style.background = '#fff'; }}
                            >
                              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" aria-hidden="true" style={{ flexShrink: 0, color: 'currentColor' }}>
                                <path d="M9 11l2 2 4-5" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
                                <path d="M7 3h7l5 5v13H7a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2Z" stroke="currentColor" strokeWidth="1.8" strokeLinejoin="round" />
                                <path d="M14 3v5h5" stroke="currentColor" strokeWidth="1.8" strokeLinejoin="round" />
                              </svg>
                              <span><strong>Checklist</strong></span>
                            </button>

                            <button type="button" onClick={(e) => { e.stopPropagation(); setDocumentosImpressao([]); setErroImpressao(''); setImpressaoModalNeg(neg); setMenuAcaoNegociacaoId(null); }} style={{ width: '100%', textAlign: 'left', border: 'none', background: '#fff', color: '#1e293b', cursor: 'pointer', padding: '9px 10px', borderRadius: '7px', fontSize: '0.84rem', fontWeight: '600', display: 'flex', alignItems: 'center', gap: '8px' }} onMouseEnter={e => { e.currentTarget.style.background = '#f8fafc'; }} onMouseLeave={e => { e.currentTarget.style.background = '#fff'; }}>
                              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" aria-hidden="true"><path d="M6 9V3h12v6" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" /><path d="M6 18H4a2 2 0 0 1-2-2v-5a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v5h-2" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" /><path d="M6 14h12v7H6z" stroke="currentColor" strokeWidth="1.8" strokeLinejoin="round" /></svg>
                              <span><strong>Imprimir</strong></span>
                            </button>

                            {!neg.documentacao_iniciada && (
                              <button
                                type="button"
                                onClick={async (e) => {
                                  e.stopPropagation();
                                  if (!resumoChecklist.completo) {
                                    const confirmar = window.confirm(
                                      `Esta negociação ainda possui ${Math.max(0, resumoChecklist.total - resumoChecklist.anexados)} pendência(s) no checklist.\n\nDeseja enviar mesmo assim para a Documentação? Isso ficará registrado no histórico do documento.`
                                    );
                                    if (!confirmar) {
                                      setMenuAcaoNegociacaoId(null);
                                      return;
                                    }
                                  }
                                  try {
                                    const token = localStorage.getItem('token');
                                    await axios.put(`${API_CONFIG.API_URL}/negociacoes/${neg._id}`, {
                                      ...neg,
                                      documentacao_iniciada: true,
                                      documentacao_coluna: 'Análise documental'
                                    }, { headers: { 'x-auth-token': token } });
                                    setMenuAcaoNegociacaoId(null);
                                    await carregarNegociacoes();
                                  } catch (err) {
                                    alert('Não foi possível iniciar a documentação.');
                                  }
                                }}
                                style={{ width: '100%', textAlign: 'left', border: 'none', background: 'rgb(255, 255, 255)', color: 'rgb(30, 41, 59)', cursor: 'pointer', padding: '9px 10px', borderRadius: '7px', fontSize: '0.84rem', fontWeight: '600', display: 'flex', alignItems: 'center', gap: '8px' }}
                                onMouseEnter={e => { e.currentTarget.style.background = '#f8fafc'; }}
                                onMouseLeave={e => { e.currentTarget.style.background = '#fff'; }}
                              >
                                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" aria-hidden="true" style={{ flexShrink: 0, color: 'currentcolor' }}>
                                  <path d="M6 2.75h8l4 4v14.5H6z" stroke="currentColor" strokeWidth="1.8" strokeLinejoin="round"></path>
                                  <path d="M14 2.75v4h4M9 11h6M9 15h6M9 19h3" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"></path>
                                </svg>
                                <span><strong>Iniciar documentação</strong>{!resumoChecklist.completo ? ' com pendência' : ''}</span>
                              </button>
                            )}



                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
          </div>
        )}
      </div>

      {relatorioModalAberto && (
        <div
          className="modal-overlay negociacoes-relatorio-overlay"
          onClick={() => { if (!relatorioGerando) setRelatorioModalAberto(false); }}
        >
          <div className="negociacoes-relatorio-modal" role="dialog" aria-modal="true" aria-labelledby="titulo-relatorio-vendedor" onClick={(e) => e.stopPropagation()}>
            <div className="negociacoes-relatorio-header">
              <div>
                <h2 id="titulo-relatorio-vendedor">Relatório por vendedor</h2>
                <p>Escolha o vendedor e o mês das negociações.</p>
              </div>
              <button
                type="button"
                className="negociacoes-relatorio-fechar"
                onClick={() => setRelatorioModalAberto(false)}
                disabled={relatorioGerando}
                aria-label="Fechar"
              >
                ×
              </button>
            </div>

            <div className="negociacoes-relatorio-campos">
              <label>
                <span>Vendedor</span>
                <select value={relatorioVendedor} onChange={(e) => { setRelatorioVendedor(e.target.value); setRelatorioErro(''); }}>
                  <option value="">Selecione o vendedor</option>
                  <option value={VENDEDOR_TODOS}>TODOS</option>
                  {vendedoresRelatorio.map((vendedor) => (
                    <option key={vendedor} value={vendedor}>{vendedor}</option>
                  ))}
                </select>
              </label>

              <label>
                <span>Formato</span>
                <select value={relatorioFormato} onChange={(e) => { setRelatorioFormato(e.target.value); setRelatorioErro(''); }}>
                  <option value="pdf">PDF</option>
                  <option value="xlsx">Planilha Excel</option>
                </select>
              </label>

              <label style={{ gridColumn: relatorioFormato === 'xlsx' ? 'span 2' : 'auto' }}>
                <span>Mês</span>
                <input
                  type="month"
                  value={relatorioMes}
                  onChange={(e) => { setRelatorioMes(e.target.value); setRelatorioErro(''); }}
                />
              </label>

              {relatorioFormato === 'xlsx' && (
                <div style={{ gridColumn: 'span 2', marginTop: '8px', borderTop: '1px solid #edf2f7', paddingTop: '14px' }}>
                  <span style={{ display: 'block', fontSize: '0.85rem', fontWeight: '600', color: '#1e3a5f', marginBottom: '10px' }}>
                    Colunas adicionais da planilha (opcional):
                  </span>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '12px 16px' }}>
                    <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', fontSize: '0.82rem', fontWeight: '500', color: '#475569', userSelect: 'none' }}>
                      <input
                        type="checkbox"
                        style={{ accentColor: '#1e3a5f', width: '16px', height: '16px', cursor: 'pointer' }}
                        checked={relatorioColunas.valorFinanciado}
                        onChange={(e) => setRelatorioColunas(prev => ({ ...prev, valorFinanciado: e.target.checked }))}
                      />
                      Valor Financiado
                    </label>
                    <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', fontSize: '0.82rem', fontWeight: '500', color: '#475569', userSelect: 'none' }}>
                      <input
                        type="checkbox"
                        style={{ accentColor: '#1e3a5f', width: '16px', height: '16px', cursor: 'pointer' }}
                        checked={relatorioColunas.bancoFinanciado}
                        onChange={(e) => setRelatorioColunas(prev => ({ ...prev, bancoFinanciado: e.target.checked }))}
                      />
                      Banco Financiado
                    </label>
                    <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', fontSize: '0.82rem', fontWeight: '500', color: '#475569', userSelect: 'none' }}>
                      <input
                        type="checkbox"
                        style={{ accentColor: '#1e3a5f', width: '16px', height: '16px', cursor: 'pointer' }}
                        checked={relatorioColunas.retornoPercentual}
                        onChange={(e) => setRelatorioColunas(prev => ({ ...prev, retornoPercentual: e.target.checked }))}
                      />
                      Retorno (%)
                    </label>
                    <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', fontSize: '0.82rem', fontWeight: '500', color: '#475569', userSelect: 'none' }}>
                      <input
                        type="checkbox"
                        style={{ accentColor: '#1e3a5f', width: '16px', height: '16px', cursor: 'pointer' }}
                        checked={relatorioColunas.retornoLiquido}
                        onChange={(e) => setRelatorioColunas(prev => ({ ...prev, retornoLiquido: e.target.checked }))}
                      />
                      Retorno Líquido
                    </label>
                    <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', fontSize: '0.82rem', fontWeight: '500', color: '#475569', userSelect: 'none' }}>
                      <input
                        type="checkbox"
                        style={{ accentColor: '#1e3a5f', width: '16px', height: '16px', cursor: 'pointer' }}
                        checked={relatorioColunas.retornoCincoPorcento}
                        onChange={(e) => setRelatorioColunas(prev => ({ ...prev, retornoCincoPorcento: e.target.checked }))}
                      />
                      Retorno 5%
                    </label>
                  </div>
                </div>
              )}
            </div>

            {relatorioErro && <div className="negociacoes-relatorio-erro" role="alert">{relatorioErro}</div>}

            <div className="negociacoes-relatorio-acoes">
              <button type="button" className="btn-secondary" onClick={() => setRelatorioModalAberto(false)} disabled={relatorioGerando}>
                Cancelar
              </button>
              <button
                type="button"
                className="btn-primary"
                onClick={baixarRelatorioVendedor}
                disabled={!relatorioVendedor || !relatorioMes || relatorioGerando}
              >
                {relatorioGerando ? 'Gerando...' : `Baixar ${relatorioFormato === 'xlsx' ? 'Planilha' : 'PDF'}`}
              </button>
            </div>
          </div>
        </div>
      )}

      {modalAberto && (
        <div className="modal-overlay-preencher nw-overlay" style={{ padding: 0 }}>
          <div className="modal-preencher-container nw-dialog" role="dialog" aria-modal="true" aria-labelledby="nw-title" style={{ width: '100%', height: '100%', maxWidth: '100%', maxHeight: '100%', borderRadius: 0 }} onClick={(e) => e.stopPropagation()}>
            <div className="modal-preencher-header">
              <div className="nw-brand"><span className="nw-brand-mark">V</span><div><strong>Nova negociação</strong></div></div>
              <span className="nw-save" role="status">{estadoRascunho}</span>
              {selectedNegociacao && podeExcluirNegociacao && (
                <button
                  type="button"
                  onClick={async (e) => { e.stopPropagation(); await excluirNegociacao(selectedNegociacao); handleClose({ descartar: true }); }}
                  style={{ marginRight: '8px', background: '#fef2f2', border: '1px solid #fecaca', borderRadius: '6px', padding: '6px 10px', cursor: 'pointer', color: '#dc2626', fontWeight: '700' }}
                  title="Excluir negociação"
                >
                  Excluir
                </button>
              )}
              <button type="button" className="modal-close-btn" aria-label="Salvar e fechar negociação" onClick={handleClose}>×</button>
            </div>

            <nav className="nw-steps" aria-label="Etapas da negociação">
              {sections.map((item, index) => <button type="button" key={item.title} aria-current={index === currentSection ? 'step' : undefined} disabled={index > currentSection} onClick={() => { setErros({}); setCurrentSection(index); if (index === 0) setParteCliente(0); }}>
                <span>{index < currentSection ? '✓' : index + 1}</span><strong>{item.title}</strong>
              </button>)}
            </nav>
            <form
              onSubmit={handleSubmit}
              onKeyPress={(e) => {
                if (e.key === 'Enter' && currentSection < sections.length - 1) e.preventDefault();
              }}
              className="preencher-form-new"
              style={{ maxHeight: 'none', flex: 1 }}
            >
              <div className="form-fields" ref={conteudoEtapaRef}>
                <div className={`nw-content ${currentSection === 0 ? 'nw-customer' : ''}`}>
                <div className="nw-heading"><span className="nw-eyebrow">ETAPA {currentSection + 1} DE {sections.length}</span><h2 id="nw-title" tabIndex={-1} ref={tituloEtapaRef}>{currentSection === 0 ? partesCliente[parteCliente].title : section.title}</h2><p>{currentSection === 0 ? partesCliente[parteCliente].description : section.description}</p></div>
                {Object.keys(erros).length > 0 && <p className="nw-error" role="alert">{erros.etapa || 'Complete os campos destacados para continuar.'}</p>}
                <div className="nw-card">


                {currentSection === 0 && (
                  <>
                    <div className="nw-parts" aria-label="Dados do cliente">
                      {['Identificação', 'Endereço', 'Negociação'].map((titulo, index) => <span key={titulo} className={index === parteCliente ? 'active' : ''}>{index + 1}. {titulo}</span>)}
                    </div>
                    {parteCliente === 0 && <>
                    <div className="field-row" style={{ alignItems: 'flex-end' }}>
                      <div className="field-group">
                        <label>CPF <span className="required">*</span></label>
                        <div style={{ display: 'flex', gap: '8px' }}>
                          <input
                            type="text"
                            name="cpf"
                            value={formData.cpf}
                            onChange={handleChange}
                            placeholder="000.000.000-00"
                            className={erros.cpf ? 'input-error' : ''}
                            autoComplete="off"
                          />
                          <button
                            type="button"
                            onClick={buscarDadosPorCPF}
                            aria-label="Consultar CPF"
                            disabled={carregando}
                            style={{ padding: '0 15px', background: '#3498db', color: 'white', border: 'none', borderRadius: '6px', cursor: 'pointer', fontSize: '1.2rem' }}
                          >
                            {carregando ? 'Buscando…' : 'Buscar dados'}
                          </button>
                        </div>
                        {erros.cpf && <span className="field-error">Campo obrigatório</span>}
                        {mensagemCpf && <span role="status" style={{ display: 'block', marginTop: 8 }}>{mensagemCpf}</span>}
                      </div>

                      {/* RG aparece ao lado do CPF após pesquisa */}
                      {pesquisaRealizada && (
                        <div className="field-group">
                          <label>RG <span className="required">*</span></label>
                          <input type="text" name="rg" value={formData.rg} onChange={handleChange} placeholder="00.000.000-0" className={erros.rg ? 'input-error' : ''} />
                          {erros.rg && <span className="field-error">Campo obrigatório</span>}
                        </div>
                      )}
                    </div>

                    {pesquisaRealizada && (
                      <>
                        {/* Nome + Telefone lado a lado */}
                        <div className="field-row">
                          <div className="field-group">
                            <label>Nome Completo <span className="required">*</span></label>
                            <input
                              type="text"
                              name="nome"
                              value={formData.nome}
                              onChange={handleChange}
                              placeholder="Nome completo do cliente"
                              className={erros.nome ? 'input-error' : ''}
                            />
                            {erros.nome && <span className="field-error">Campo obrigatório</span>}
                          </div>
                          <div className="field-group">
                            <label>Telefone <span className="required">*</span></label>
                            <input type="text" name="telefone" value={formData.telefone} onChange={handleChange} placeholder="(00) 00000-0000" className={erros.telefone ? 'input-error' : ''} />
                            {erros.telefone && <span className="field-error">Campo obrigatório</span>}
                          </div>
                        </div>

                        {/* Profissão + Estado Civil lado a lado */}
                        <div className="field-row">
                          <div className="field-group">
                            <label>Profissão <span className="required">*</span></label>
                            <input type="text" name="profissao" value={formData.profissao} onChange={handleChange} placeholder="Profissão" className={erros.profissao ? 'input-error' : ''} />
                            {erros.profissao && <span className="field-error">Campo obrigatório</span>}
                          </div>
                          <div className="field-group">
                            <label>Estado Civil <span className="required">*</span></label>
                            <select name="estadoCivil" value={formData.estadoCivil} onChange={handleChange} className={erros.estadoCivil ? 'input-error' : ''}>
                              <option value="">Selecione...</option>
                              <option>Solteiro(a)</option>
                              <option>Casado(a)</option>
                              <option>Diórciado(a)</option>
                              <option>Viúvo(a)</option>
                              <option>União Estável</option>
                              <option>Separado(a)</option>
                            </select>
                            {erros.estadoCivil && <span className="field-error">Campo obrigatório</span>}
                          </div>
                        </div>

                      </>
                    )}
                    {!pesquisaRealizada && <button type="button" className="nw-text-button" onClick={() => { setClienteManual(true); setPesquisaRealizada(true); }}>Prefiro preencher manualmente</button>}
                    </>}
                    {parteCliente === 1 && <>
                        {/* Endereço com busca por CEP */}
                        <div className="field-row field-row-full">
                          <div className="field-group">
                            <label>CEP</label>
                            <div style={{ display: 'flex', gap: '8px' }}>
                              <input type="text" value={cepQuery}
                                onChange={e => {
                                  const valorCep = e.target.value.replace(/\D/g, '').slice(0, 8);
                                  setCepQuery(valorCep);
                                  setFormData(prev => ({ ...prev, cep: valorCep }));
                                }}
                                placeholder="00000-000" maxLength={8} style={{ width: '140px' }}
                                onKeyPress={async (e) => {
                                  if (e.key === 'Enter') { e.preventDefault();
                                    if (cepQuery.length === 8) { setCepBuscando(true);
                                      try { const r = await fetch(`https://viacep.com.br/ws/${cepQuery}/json/`); const d = await r.json();
                                        if (!d.erro) { setFormData(prev => ({ ...prev, cep: cepQuery, rua: d.logradouro || '', bairro: d.bairro || '', cidadeEstado: [d.localidade, d.uf].filter(Boolean).join(' / ') })); setEnderecoManual(false); } else { setEnderecoManual(true); }
                                      } catch { setEnderecoManual(true); } setCepBuscando(false); } }
                                }} />
                              <button type="button"
                                onClick={async () => { if (cepQuery.length !== 8) return; setCepBuscando(true);
                                  try { const r = await fetch(`https://viacep.com.br/ws/${cepQuery}/json/`); const d = await r.json();
                                    if (!d.erro) { setFormData(prev => ({ ...prev, cep: cepQuery, rua: d.logradouro || '', bairro: d.bairro || '', cidadeEstado: [d.localidade, d.uf].filter(Boolean).join(' / ') })); setEnderecoManual(false); } else { setEnderecoManual(true); }
                                  } catch { setEnderecoManual(true); } setCepBuscando(false); }}
                                disabled={cepQuery.length !== 8 || cepBuscando}
                                style={{ padding: '0 14px', background: '#3498db', color: 'white', border: 'none', borderRadius: '6px', cursor: 'pointer', fontWeight: '600', opacity: cepQuery.length !== 8 ? 0.5 : 1 }}>
                                {cepBuscando ? '...' : '🔍'}
                              </button>
                            </div>
                          </div>
                        </div>

                        {/* Rua + Número */}
                        <div className="field-row">
                          <div className="field-group">
                            <label>Rua <span className="required">*</span></label>
                            <input type="text" name="rua" value={formData.rua} onChange={handleChange}
                              placeholder="Nome da rua / avenida"
                              className={erros.rua ? 'input-error' : ''} />
                            {erros.rua && <span className="field-error">Campo obrigatório</span>}
                          </div>
                          <div className="field-group">
                            <label>Número</label>
                            <input type="text" name="numero" value={formData.numero} onChange={handleChange} placeholder="Nº" />
                          </div>
                        </div>

                        {/* Bairro + Cidade/Estado */}
                        <div className="field-row">
                          <div className="field-group">
                            <label>Bairro</label>
                            <input type="text" name="bairro" value={formData.bairro} onChange={handleChange} placeholder="Bairro" />
                          </div>
                          <div className="field-group">
                            <label>Cidade / Estado <span className="required">*</span></label>
                            <input type="text" name="cidadeEstado" value={formData.cidadeEstado} onChange={handleChange}
                              placeholder="Cidade / UF"
                              className={erros.cidadeEstado ? 'input-error' : ''} />
                            {erros.cidadeEstado && <span className="field-error">Campo obrigatório</span>}
                          </div>
                        </div>
                    </>}
                    {parteCliente === 2 && <>
                    {/* Vendedor + Loja */}
                    <div className="field-row">
                      <div className="field-group">
                        <label>Vendedor responsável <span className="required">*</span></label>
                        <select name="vendedor" value={formData.vendedor} onChange={handleChange} className={erros.vendedor ? 'input-error' : ''}>
                          <option value="">Selecione...</option>
                          {!vendedorExisteNaLista && formData.vendedor && (
                            <option value={formData.vendedor}>{formData.vendedor} (atual)</option>
                          )}
                          {gruposVendedores.map((grupo) => (
                            <optgroup key={grupo.cargo} label={grupo.cargo}>
                              {grupo.usuarios.map((u, idx) => (
                                <option key={`${u.id}-${idx}`} value={u.nome}>{u.nome}</option>
                              ))}
                            </optgroup>
                          ))}
                        </select>
                        {erros.vendedor && <span className="field-error">Campo obrigatório</span>}
                      </div>
                      <div className="field-group">
                        <label>Loja <span className="required">*</span></label>
                        <select name="loja" value={formData.loja} onChange={handleChange} className={erros.loja ? 'input-error' : ''}>
                          <option value="">Selecione...</option>
                          <option>Loja 1</option>
                          <option>Loja 3</option>
                          <option>Feirão</option>
                        </select>
                        {erros.loja && <span className="field-error">Campo obrigatório</span>}
                      </div>
                    </div>

                    {/* Data da negociação */}
                    <div className="field-row">
                      <div className="field-group">
                        <label>Data da negociação</label>
                        <input type="date" name="data" value={formData.data || ''} onChange={handleChange} />
                      </div>
                      <div className="field-group">
                        <label>Origem da venda</label>
                        <select name="origemVenda" value={formData.origemVenda || ''} onChange={handleChange}>
                          <option value="">Selecione...</option>
                          <option value="Porta">Porta</option>
                          <option value="Web">Web</option>
                          <option value="Indicação">Indicação</option>
                          <option value="Carteira da loja">Carteira da loja</option>
                          <option value="Carteira do vendedor">Carteira do vendedor</option>
                          <option value="Outros">Outros</option>
                        </select>
                      </div>
                    </div>

                    <div className="field-row">
                      {['Web', 'Outros'].includes(formData.origemVenda) ? (
                        <div className="field-group">
                          <label>{formData.origemVenda === 'Web' ? 'Portal / origem web' : 'Detalhes'}</label>
                          <input
                            type="text"
                            name="origemVendaDetalhe"
                            value={formData.origemVendaDetalhe || ''}
                            onChange={handleChange}
                            placeholder={formData.origemVenda === 'Web' ? 'Ex.: Facebook, Instagram, OLX...' : 'Descreva a origem'}
                          />
                        </div>
                      ) : (
                        <div className="field-group" />
                      )}
                      <div className="field-group nw-participants">
                        <label>Participantes <span className="nw-optional">Opcional</span></label>
                        <div className="nw-participant-chips">
                          {(formData.participantes || []).map(nome => <span key={nome}>{nome}<button type="button" aria-label={`Remover participante ${nome}`} onClick={() => removerParticipante(nome)}>×</button></span>)}
                        </div>
                        <button type="button" className="nw-participant-trigger" aria-expanded={participantesAbertos} aria-controls="nw-participant-options" onClick={() => setParticipantesAbertos(prev => !prev)}>
                          <span>{formData.participantes?.length ? 'Adicionar mais pessoas' : 'Adicionar participantes'}</span><span aria-hidden="true">{participantesAbertos ? '−' : '+'}</span>
                        </button>
                        {participantesAbertos && <div className="nw-participant-picker" id="nw-participant-options">
                          <input type="search" aria-label="Buscar participantes por nome ou cargo" autoFocus value={participanteBusca} onChange={e => setParticipanteBusca(e.target.value)} placeholder="Buscar nome ou cargo" />
                          <div className="nw-participant-list">
                            {participantesDisponiveisFlat.map((p, i) => <button type="button" key={`${p.id}-${i}`} aria-label={`Adicionar participante ${p.nome}`} onClick={() => adicionarParticipante(p.nome)}>
                              <span className="nw-person-avatar" aria-hidden="true">{p.nome.trim().split(/\s+/).map(n => n[0]).slice(0, 2).join('')}</span>
                              <span className="nw-person-info"><strong>{p.nome}</strong><small>{p.cargo}</small></span><span className="nw-person-add" aria-hidden="true">+</span>
                            </button>)}
                            {!participantesDisponiveisFlat.length && <p role="status">{participanteBusca ? 'Nenhuma pessoa encontrada.' : 'Nenhum participante disponível para adicionar.'}</p>}
                          </div>
                          <button type="button" className="nw-text-button" onClick={() => setParticipantesAbertos(false)}>Concluir seleção</button>
                        </div>}
                      </div>
                    </div>

                    </>}
                  </>
                )}

                {currentSection === 1 && (
                  <>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
                      <button
                        type="button"
                        onClick={abrirSubModalNovo}
                        className="btn-primary"
                        style={{ padding: '8px 16px', fontSize: '0.85rem' }}
                      >
                        + Adicionar veículo vendido
                      </button>
                    </div>

                    {veiculosVendidos.length === 0 ? (
                      <div style={{ textAlign: 'center', padding: '3rem', border: '2px dashed #ddd', borderRadius: '8px', color: '#999' }}>
                        Nenhum veículo adicionado. Clique em "+ Adicionar veículo vendido".
                      </div>
                    ) : (
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                        {/* cabeçalho da tabela */}
                        <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 80px 90px 140px 90px', gap: '8px', padding: '6px 12px', background: '#f1f5f9', borderRadius: '6px', fontSize: '0.78rem', fontWeight: '600', color: '#64748b' }}>
                          <span>Veículo</span>
                          {!isMobile && <span>Ano</span>}
                          {!isMobile && <span>Cor</span>}
                          {!isMobile && <span>Valor de Venda</span>}
                          {!isMobile && <span></span>}
                        </div>
                        {veiculosVendidos.map((v) => (
                          <div key={v.id} style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 80px 90px 140px 90px', gap: '8px', alignItems: 'center', padding: '10px 12px', border: '1px solid #e2e8f0', borderRadius: '8px', background: '#fff' }}>
                            <div>
                              <div style={{ fontWeight: '600', color: '#1e293b', fontSize: '0.9rem' }}>{v.marca} {v.modelo}</div>
                              <div style={{ fontSize: '0.78rem', color: '#94a3b8' }}>Placa: {v.placa || '—'} | KM: {v.km || '—'}</div>
                            </div>
                            <span style={{ color: '#475569', fontSize: '0.88rem' }}>{isMobile ? `Ano: ${v.ano || '—'}` : (v.ano || '—')}</span>
                            <span style={{ color: '#475569', fontSize: '0.88rem' }}>{isMobile ? `Cor: ${v.cor || '—'}` : (v.cor || '—')}</span>
                            <input
                              type="text"
                              value={v.valorVenda}
                              onChange={(e) => handleVeiculoVendidoValor(v.id, formatarMoeda(e.target.value))}
                              placeholder="R$ 0,00"
                              style={{ padding: '6px 8px', border: '1px solid #cbd5e1', borderRadius: '6px', fontSize: '0.85rem', width: '100%' }}
                            />
                            <div style={{ display: 'flex', gap: '6px', justifyContent: isMobile ? 'flex-start' : 'flex-end' }}>
                              <button
                                type="button"
                                onClick={() => abrirSubModalEditar(v)}
                                style={{ background: '#e0f2fe', border: 'none', borderRadius: '6px', padding: '6px 10px', cursor: 'pointer', color: '#0284c7', fontSize: '0.85rem' }}
                                title="Editar"
                              >✏️</button>
                              <button
                                type="button"
                                onClick={() => removerVeiculoVendido(v.id)}
                                style={{ background: '#fee2e2', border: 'none', borderRadius: '6px', padding: '6px 10px', cursor: 'pointer', color: '#dc2626', fontSize: '0.85rem' }}
                                title="Remover"
                              >×</button>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </>
                )}

                {currentSection === 2 && (
                  <>
                    {/* aviso: nenhum veículo */}
                    {tipoNegociacao() === null && (
                      <div style={{ background: '#fef3c7', border: '1px solid #fcd34d', borderRadius: '8px', padding: '12px 16px', marginBottom: '1.25rem', color: '#92400e', fontSize: '0.88rem' }}>
                        ⚠️ Adicione pelo menos um veículo (saindo ou entrando no estoque) para avançar para o acerto financeiro.
                      </div>
                    )}
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
                      <button
                        type="button"
                        onClick={abrirSubModalTrocaNovo}
                        className="btn-primary"
                        style={{ padding: '8px 16px', fontSize: '0.85rem', marginLeft: 'auto' }}
                      >
                        + Adicionar veículo entrando no estoque
                      </button>
                    </div>

                    {trocas.length === 0 ? (
                      <div style={{ textAlign: 'center', padding: '3rem', border: '2px dashed #ddd', borderRadius: '8px', color: '#999' }}>
                        Nenhum veículo adicionado. Clique em "+ Adicionar veículo na troca".
                      </div>
                    ) : (
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                        <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 90px 90px 140px 90px', gap: '8px', padding: '6px 12px', background: '#f1f5f9', borderRadius: '6px', fontSize: '0.78rem', fontWeight: '600', color: '#64748b' }}>
                          <span>Veículo</span>
                          {!isMobile && <span>Placa</span>}
                          {!isMobile && <span>Ano</span>}
                          {!isMobile && <span>Valor da Troca</span>}
                          {!isMobile && <span></span>}
                        </div>
                        {trocas.map((t) => (
                          <div key={t.id} style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 90px 90px 140px 90px', gap: '8px', alignItems: 'center', padding: '10px 12px', border: '1px solid #e2e8f0', borderRadius: '8px', background: '#fff' }}>
                            <div>
                              <div style={{ fontWeight: '600', color: '#1e293b', fontSize: '0.9rem' }}>{t.modelo || '—'}</div>
                              <div style={{ fontSize: '0.78rem', color: '#94a3b8' }}>
                                {t.quitado !== '-' && `Quitado: ${t.quitado}`}{t.possuiDebitos !== '-' && ` | Débitos: ${t.possuiDebitos}`}
                              </div>
                            </div>
                            <span style={{ color: '#475569', fontSize: '0.88rem' }}>{isMobile ? `Placa: ${t.placa || '—'}` : (t.placa || '—')}</span>
                            <span style={{ color: '#475569', fontSize: '0.88rem' }}>{isMobile ? `Ano: ${t.anoFabricacao && t.anoModelo ? `${t.anoFabricacao}/${t.anoModelo}` : t.anoFabricacao || t.anoModelo || '—'}` : (t.anoFabricacao && t.anoModelo ? `${t.anoFabricacao}/${t.anoModelo}` : t.anoFabricacao || t.anoModelo || '—')}</span>
                            <input
                              type="text"
                              value={t.valorTroca}
                              onChange={(e) => handleTrocaValor(t.id, formatarMoeda(e.target.value))}
                              placeholder="R$ 0,00"
                              style={{ padding: '6px 8px', border: '1px solid #cbd5e1', borderRadius: '6px', fontSize: '0.85rem', width: '100%' }}
                            />
                            <div style={{ display: 'flex', gap: '6px', justifyContent: isMobile ? 'flex-start' : 'flex-end' }}>
                              <button
                                type="button"
                                onClick={() => abrirSubModalTrocaEditar(t)}
                                style={{ background: '#e0f2fe', border: 'none', borderRadius: '6px', padding: '6px 10px', cursor: 'pointer', color: '#0284c7', fontSize: '0.85rem' }}
                                title="Editar"
                              >✏️</button>
                              <button
                                type="button"
                                onClick={() => removerTroca(t.id)}
                                style={{ background: '#fee2e2', border: 'none', borderRadius: '6px', padding: '6px 10px', cursor: 'pointer', color: '#dc2626', fontSize: '0.85rem' }}
                                title="Remover"
                              >×</button>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </>
                )}

                {currentSection === 3 && (() => {
                  const totalProdutosAgregados = produtosAgregados.reduce((s, p) => s + parseMoeda(p.valor), 0);

                  return (
                    <>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: isMobile ? 'stretch' : 'center', gap: '12px', marginBottom: '1.25rem', flexDirection: isMobile ? 'column' : 'row' }}>
                        <div>
                          <h4 style={{ margin: '0 0 4px', color: '#1e293b' }}>🧾 Produtos agregados</h4>
                          <p style={{ margin: 0, color: '#64748b', fontSize: '0.85rem' }}>
                            Adicione taxas e serviços que entram no valor da negociação e impactam o acerto financeiro.
                          </p>
                        </div>
                        <button type="button" onClick={adicionarProdutoAgregado} className="btn-primary" style={{ padding: '8px 16px', fontSize: '0.85rem' }}>
                          + Adicionar produto
                        </button>
                      </div>

                      {produtosAgregados.length === 0 ? (
                        <div style={{ textAlign: 'center', padding: '2.5rem', border: '2px dashed #cbd5e1', borderRadius: '10px', color: '#64748b', background: '#f8fafc' }}>
                          Nenhum produto agregado adicionado. Essa etapa é opcional.
                        </div>
                      ) : (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                          {produtosAgregados.map((produto) => {
                            const preset = produtosAgregadosPadrao.find(p => p.tipo === produto.tipo) || produtosAgregadosPadrao[0];
                            const exigeVeiculo = preset.exigeVeiculo;
                            const veiculoValor = produto.veiculo_tipo && produto.veiculo_placa ? `${produto.veiculo_tipo}:${produto.veiculo_placa}` : '';

                            return (
                              <div key={produto.id} style={{ padding: '12px', border: `1px solid ${exigeVeiculo && !produto.veiculo_placa ? '#fcd34d' : '#e2e8f0'}`, borderRadius: '10px', background: exigeVeiculo && !produto.veiculo_placa ? '#fffbeb' : '#fff', display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '190px 1fr 150px 36px', gap: '8px', alignItems: 'start' }}>
                                <div className="field-group" style={{ margin: 0 }}>
                                  <label style={{ fontSize: '0.75rem', color: '#64748b', fontWeight: 700 }}>Produto</label>
                                  <select value={produto.tipo || 'transferencia_propriedade'} onChange={(e) => atualizarProdutoAgregado(produto.id, 'tipo', e.target.value)}
                                    style={{ padding: '8px', border: '1px solid #e2e8f0', borderRadius: '6px', fontSize: '0.85rem', width: '100%' }}>
                                    {produtosAgregadosPadrao.map(p => <option key={p.tipo} value={p.tipo}>{p.nome}</option>)}
                                  </select>
                                </div>

                                <div className="field-group" style={{ margin: 0 }}>
                                  <label style={{ fontSize: '0.75rem', color: '#64748b', fontWeight: 700 }}>
                                    {produto.tipo === 'outros' ? 'Nome personalizado' : 'Veículo vinculado'}
                                  </label>
                                  {produto.tipo === 'outros' ? (
                                    <input type="text" value={produto.nome || ''} onChange={(e) => atualizarProdutoAgregado(produto.id, 'nome', e.target.value)}
                                      placeholder="Ex.: Despachante, taxa extra..."
                                      style={{ padding: '8px', border: '1px solid #e2e8f0', borderRadius: '6px', fontSize: '0.85rem', width: '100%' }} />
                                  ) : (
                                    <select value={veiculoValor} onChange={(e) => atualizarProdutoAgregado(produto.id, 'veiculo', e.target.value)}
                                      style={{ padding: '8px', border: '1px solid #e2e8f0', borderRadius: '6px', fontSize: '0.85rem', width: '100%' }}>
                                      <option value="">{exigeVeiculo ? 'Selecione o veículo...' : 'Sem vínculo específico'}</option>
                                      {veiculosParaProdutosAgregados.map(v => (
                                        <option key={v.key} value={`${v.tipo}:${v.placa}`}>
                                          {v.tipo === 'saida' ? 'Saindo' : 'Entrando'} • {v.label}
                                        </option>
                                      ))}
                                    </select>
                                  )}
                                  {exigeVeiculo && !produto.veiculo_placa && (
                                    <span style={{ display: 'block', marginTop: '4px', fontSize: '0.72rem', color: '#b45309' }}>
                                      Vistoria precisa estar atrelada a um veículo.
                                    </span>
                                  )}
                                </div>

                                <div className="field-group" style={{ margin: 0 }}>
                                  <label style={{ fontSize: '0.75rem', color: '#64748b', fontWeight: 700 }}>Valor</label>
                                  <input type="text" value={produto.valor || ''} onChange={(e) => atualizarProdutoAgregado(produto.id, 'valor', formatarMoeda(e.target.value))}
                                    placeholder="R$ 0,00"
                                    style={{ padding: '8px', border: '1px solid #e2e8f0', borderRadius: '6px', fontSize: '0.85rem', width: '100%' }} />
                                </div>

                                <button type="button" onClick={() => removerProdutoAgregado(produto.id)}
                                  style={{ background: '#fee2e2', border: 'none', borderRadius: '6px', padding: '8px', cursor: 'pointer', color: '#dc2626', fontWeight: '700', width: isMobile ? '100%' : 'auto', marginTop: isMobile ? 0 : '20px' }}
                                  title="Remover produto">
                                  ×
                                </button>
                              </div>
                            );
                          })}

                          <div style={{ textAlign: 'right', fontSize: '0.9rem', color: '#1d4ed8', fontWeight: 700, marginTop: '4px' }}>
                            Total de produtos agregados: {exibirMoeda(totalProdutosAgregados)}
                          </div>
                        </div>
                      )}
                    </>
                  );
                })()}

                {currentSection === 4 && (() => {
                  const totalVendidos = veiculosVendidos.reduce((s, v) => s + parseMoeda(v.valorVenda), 0);
                  const totalTrocas = trocas.reduce((s, t) => s + parseMoeda(t.valorTroca) - parseMoeda(t.valorQuitacao) - parseMoeda(t.valorDebitos), 0);
                  const totalProdutosAgregados = produtosAgregados.reduce((s, p) => s + parseMoeda(p.valor), 0);
                  const saldo = totalVendidos + totalProdutosAgregados - totalTrocas;
                  const totalPago = pagamentos.reduce((s, p) => s + parseMoeda(p.valor), 0);
                  const totalRecebido = recebimentos.reduce((s, r) => s + obterValorRecebimentoAcerto(r), 0);

                  return (
                    <>
                      {/* Resumo do saldo */}
                      <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : `repeat(${trocas.length > 0 ? 4 : 3}, 1fr)`, gap: '0.75rem', marginBottom: '1.5rem' }}>
                        <div style={{ padding: '0.75rem 1rem', background: '#f8fafb', border: '1px solid #e2e8f0', borderRadius: '8px' }}>
                          <div style={{ fontSize: '0.72rem', color: '#64748b', fontWeight: '500', marginBottom: '2px', textTransform: 'uppercase', letterSpacing: '0.04em' }}>Veículos saindo</div>
                          <div style={{ fontSize: '1.05rem', fontWeight: '600', color: '#334155' }}>{exibirMoeda(totalVendidos)}</div>
                        </div>
                        {trocas.length > 0 && (
                          <div style={{ padding: '0.75rem 1rem', background: '#f8fafb', border: '1px solid #e2e8f0', borderRadius: '8px' }}>
                            <div style={{ fontSize: '0.72rem', color: '#64748b', fontWeight: '500', marginBottom: '2px', textTransform: 'uppercase', letterSpacing: '0.04em' }}>Veículos entrando (líquido)</div>
                            <div style={{ fontSize: '1.05rem', fontWeight: '600', color: '#334155' }}>{exibirMoeda(totalTrocas)}</div>
                          </div>
                        )}
                        <div style={{ padding: '0.75rem 1rem', background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: '8px' }}>
                          <div style={{ fontSize: '0.72rem', color: '#1d4ed8', fontWeight: '500', marginBottom: '2px', textTransform: 'uppercase', letterSpacing: '0.04em' }}>Produtos agregados</div>
                          <div style={{ fontSize: '1.05rem', fontWeight: '600', color: '#1d4ed8' }}>{exibirMoeda(totalProdutosAgregados)}</div>
                        </div>
                        <div style={{ padding: '0.75rem 1rem', background: '#f8fafb', border: `1px solid ${saldo >= 0 ? '#bbf7d0' : '#fecaca'}`, borderRadius: '8px' }}>
                          <div style={{ fontSize: '0.72rem', color: '#64748b', fontWeight: '500', marginBottom: '2px', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                            {saldo >= 0 ? 'Saldo a receber' : 'Saldo a pagar'}
                          </div>
                          <div style={{ fontSize: '1.05rem', fontWeight: '600', color: saldo >= 0 ? '#16a34a' : '#dc2626' }}>{exibirMoeda(Math.abs(saldo))}</div>
                        </div>
                      </div>

                      {/* RECEBIMENTOS — principal, sempre visível */}
                      <div style={{ marginBottom: '2rem' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
                          <h4 style={{ margin: 0, color: '#15803d' }}>💰 Recebimentos</h4>
                          <button type="button" className="btn-primary"
                            style={{ padding: '6px 14px', fontSize: '0.8rem' }}
                            onClick={() => setRecebimentos(prev => [...prev, { id: Date.now(), financiamento_chave: window.crypto.randomUUID(), tipo: 'PIX', valor: '', descricao: '' }])}>
                            + Adicionar
                          </button>
                        </div>
                        {recebimentos.length === 0 ? (
                          <div style={{ padding: '2rem', border: '2px dashed #86efac', borderRadius: '8px', textAlign: 'center', color: '#999', fontSize: '0.88rem' }}>
                            Nenhum recebimento adicionado
                          </div>
                        ) : (
                          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                                        {recebimentos.map(r => (
                              <div key={r.id} style={{ border: '1px solid #e2e8f0', borderRadius: '8px', padding: '8px', background: r.tipo === 'Financiamento' ? '#f0fdf4' : '#fff' }}>
                                <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : (r.tipo === 'Nota promissória' ? '160px 1fr 36px 36px' : '160px 1fr 36px'), gap: '6px', alignItems: 'center' }}>
                                  <select value={r.tipo} onChange={(e) => setRecebimentos(prev => prev.map(x => {
                                    if (x.id !== r.id) return x;

                                    const base = {
                                      ...x,
                                      tipo: e.target.value,
                                      banco: '',
                                      placa: veiculosVendidos[0]?.placa || '',
                                      veiculo: veiculosVendidos[0] ? `${veiculosVendidos[0].marca} ${veiculosVendidos[0].modelo}` : '',
                                      percentual_retorno: '',
                                      fin_status: 'Em formalização'
                                    };

                                    if (e.target.value === 'Cartão de crédito') {
                                      return {
                                        ...base,
                                        valor: '',
                                        liquido_cartao: '',
                                        parcelas: '',
                                        valor_parcela: ''
                                      };
                                    }

                                    return base;
                                  }))}
                                    style={{ padding: '8px', border: '1px solid #e2e8f0', borderRadius: '6px', fontSize: '0.85rem' }}>
                                    <option>PIX</option>
                                    <option>TED</option>
                                    <option>Financiamento</option>
                                    <option>Nota promissória</option>
                                    <option>Cartão de crédito</option>
                                    <option>Cartão de débito</option>
                                    <option>Espécie (dinheiro)</option>
                                  </select>
                                  <input
                                    type="text"
                                    value={r.tipo === 'Cartão de crédito' ? (r.liquido_cartao || '') : (r.valor || '')}
                                    placeholder={r.tipo === 'Cartão de crédito' ? 'Valor líquido (R$ 0,00)' : 'R$ 0,00'}
                                    onChange={(e) => setRecebimentos(prev => prev.map(x => {
                                      if (x.id !== r.id) return x;
                                      if (x.tipo === 'Cartão de crédito') {
                                        return { ...x, liquido_cartao: formatarMoeda(e.target.value) };
                                      }
                                      return { ...x, valor: formatarMoeda(e.target.value) };
                                    }))}
                                    style={{ padding: '8px', border: '1px solid #e2e8f0', borderRadius: '6px', fontSize: '0.85rem' }} />
                                  {r.tipo === 'Nota promissória' && <button type="button" onClick={() => abrirConfiguracaoPromissoria(r)} title="Configurar promissórias" aria-label="Configurar promissórias" style={{ background: '#dbeafe', border: 'none', borderRadius: '6px', padding: '8px', cursor: 'pointer', color: '#1d4ed8', fontWeight: 700, width: isMobile ? '100%' : '36px' }}>🖩</button>}
                                  <button type="button" onClick={() => setRecebimentos(prev => prev.filter(x => x.id !== r.id))}
                                    style={{ background: '#fee2e2', border: 'none', borderRadius: '6px', padding: '8px', cursor: 'pointer', color: '#dc2626', fontWeight: '700', width: isMobile ? '100%' : 'auto' }}>×</button>
                                </div>
                                {r.tipo === 'Cartão de crédito' && (
                                  <div style={{ marginTop: '8px', display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: '6px', background: '#faf5ff', padding: '8px', borderRadius: '6px' }}>
                                    <div className="field-group" style={{ margin: 0 }}>
                                      <label style={{ fontSize: '0.75rem', color: '#64748b', fontWeight: '600' }}>Parcelas</label>
                                      <input type="number" min="1" value={r.parcelas || ''} onChange={e => setRecebimentos(prev => prev.map(x => {
                                        if (x.id !== r.id) return x;
                                        const parcelas = e.target.value;
                                        const valorParcela = parseMoeda(x.valor_parcela || '');
                                        const qtdParcelas = parseInt(parcelas, 10);
                                        const totalBruto = valorParcela > 0 && qtdParcelas > 0 ? exibirMoeda(valorParcela * qtdParcelas) : '';
                                        return { ...x, parcelas, valor: totalBruto };
                                      }))}
                                        placeholder="Ex: 10" style={{ padding: '6px 8px', border: '1px solid #e2e8f0', borderRadius: '6px', fontSize: '0.82rem', width: '100%' }} />
                                    </div>
                                    <div className="field-group" style={{ margin: 0 }}>
                                      <label style={{ fontSize: '0.75rem', color: '#64748b', fontWeight: '600' }}>Valor de cada parcela</label>
                                      <input type="text"
                                        value={r.valor_parcela || ''}
                                        onChange={e => setRecebimentos(prev => prev.map(x => {
                                          if (x.id !== r.id) return x;
                                          const valor_parcela = formatarMoeda(e.target.value);
                                          const valorParcelaNumero = parseMoeda(valor_parcela);
                                          const qtdParcelas = parseInt(x.parcelas, 10);
                                          const totalBruto = valorParcelaNumero > 0 && qtdParcelas > 0 ? exibirMoeda(valorParcelaNumero * qtdParcelas) : '';
                                          return { ...x, valor_parcela, valor: totalBruto };
                                        }))}
                                        placeholder="R$ 0,00" style={{ padding: '6px 8px', border: '1px solid #e2e8f0', borderRadius: '6px', fontSize: '0.82rem', width: '100%' }} />
                                    </div>
                                    <div className="field-group" style={{ margin: 0, gridColumn: '1 / -1' }}>
                                      <label style={{ fontSize: '0.75rem', color: '#64748b', fontWeight: '600' }}>Total bruto calculado</label>
                                      <input type="text" readOnly
                                        value={r.valor || '—'}
                                        style={{ padding: '6px 8px', border: '1px solid #e2e8f0', borderRadius: '6px', fontSize: '0.82rem', width: '100%', background: '#f8fafc', color: '#475569' }} />
                                    </div>
                                  </div>
                                )}
                                {r.tipo === 'Nota promissória' && r.promissoria?.parcelas?.length > 0 && <div style={{ marginTop: 8, padding: '7px 10px', borderRadius: 6, background: '#eff6ff', color: '#1e40af', fontSize: '0.78rem' }}>📄 {r.promissoria.quantidade_parcelas} parcela(s) · Total: {exibirMoeda(r.promissoria.parcelas.reduce((s, p) => s + parseMoeda(p.valor), 0))}</div>}
                                {r.tipo === 'Financiamento' && (() => {
                                    const veiculo = veiculosVendidos.length === 1
                                      ? veiculosVendidos[0]
                                      : veiculosVendidos.find(v => v.placa === r.placa) || null;
                                    return (
                                  <div style={{ marginTop: '8px', display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: '6px' }}>
                                    {veiculosVendidos.length > 1 && (
                                      <div className="field-group" style={{ margin: 0, gridColumn: '1 / -1' }}>
                                        <label style={{ fontSize: '0.75rem', color: '#64748b', fontWeight: '600' }}>Veículo</label>
                                        <select value={r.placa || ''} onChange={e => {
                                          const v = veiculosVendidos.find(x => x.placa === e.target.value);
                                          setRecebimentos(prev => prev.map(x => x.id === r.id ? { ...x, placa: e.target.value, veiculo: v ? `${v.marca} ${v.modelo}` : '' } : x));
                                        }} style={{ padding: '6px 8px', border: '1px solid #e2e8f0', borderRadius: '6px', fontSize: '0.82rem', width: '100%' }}>
                                          <option value="">Selecione o veículo...</option>
                                          {veiculosVendidos.map(v => (
                                            <option key={v.id} value={v.placa}>{v.marca} {v.modelo} — {v.placa}</option>
                                          ))}
                                        </select>
                                      </div>
                                    )}
                                    {veiculosVendidos.length === 1 && (
                                      <div style={{ gridColumn: '1 / -1', fontSize: '0.78rem', color: '#475569', background: '#f8fafc', padding: '6px 10px', borderRadius: '6px', border: '1px solid #e2e8f0' }}>
                                        🚗 {veiculosVendidos[0].marca} {veiculosVendidos[0].modelo} — {veiculosVendidos[0].placa}
                                      </div>
                                    )}
                                    <div className="field-group" style={{ margin: 0 }}>
                                      <label style={{ fontSize: '0.75rem', color: '#64748b', fontWeight: '600' }}>Banco</label>
                                      <select value={r.banco || ''} onChange={e => setRecebimentos(prev => prev.map(x => x.id === r.id ? { ...x, banco: e.target.value } : x))}
                                        style={{ padding: '6px 8px', border: '1px solid #e2e8f0', borderRadius: '6px', fontSize: '0.82rem', width: '100%' }}>
                                        <option value="">Selecione...</option>
                                        {bancosLoja.map(b => <option key={b._id} value={b.nome}>{b.nome}</option>)}
                                      </select>
                                    </div>
                                    <div className="field-group" style={{ margin: 0 }}>
                                      <label style={{ fontSize: '0.75rem', color: '#64748b', fontWeight: '600' }}>% Retorno</label>
                                      <input type="number" step="0.1" value={r.percentual_retorno || ''} onChange={e => setRecebimentos(prev => prev.map(x => x.id === r.id ? { ...x, percentual_retorno: e.target.value } : x))}
                                        placeholder="Ex: 3.6" style={{ padding: '6px 8px', border: '1px solid #e2e8f0', borderRadius: '6px', fontSize: '0.82rem', width: '100%' }} />
                                    </div>
                                  </div>
                                    );
                                  })()}
                              </div>
                            ))}
                            <div style={{ textAlign: 'right', fontSize: '0.85rem', color: '#15803d', fontWeight: '600', marginTop: '4px' }}>
                              Total: {exibirMoeda(totalRecebido)}
                            </div>
                          </div>
                        )}
                      </div>

                      {promissoriaAberta && (
                        <div style={{ position: 'fixed', inset: 0, zIndex: 5000, background: 'rgba(15,23,42,.46)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
                          <div role="dialog" aria-modal="true" aria-labelledby="titulo-promissoria" style={{ width: 'min(680px, 100%)', maxHeight: '90vh', overflowY: 'auto', background: '#fff', borderRadius: 14, padding: 22, boxShadow: '0 20px 60px rgba(15,23,42,.25)' }}>
                            <style>{'[aria-labelledby="titulo-promissoria"] .btn-secondary { display: none; }'}</style>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 18 }}><div><h3 id="titulo-promissoria" style={{ margin: 0, color: '#1e293b' }}>Configurar nota promissória</h3><small style={{ color: '#64748b' }}>Defina a quantidade e as datas. O total permanece {exibirMoeda(promissoriaAberta.principal)}.</small></div><button type="button" onClick={() => setPromissoriaAberta(null)} style={{ border: 0, background: '#f1f5f9', borderRadius: 8, fontSize: 22, cursor: 'pointer' }}>×</button></div>
                            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: 12, marginBottom: 14 }}>
                              <label style={{ color: '#475569', fontSize: 13, fontWeight: 600 }}>Valor original
                                <input type="text" readOnly value={exibirMoeda(promissoriaAberta.principal)} style={{ display: 'block', width: '100%', marginTop: 5, padding: '10px 11px', border: '1px solid #cbd5e1', borderRadius: 7, background: '#f8fafc', color: '#475569' }} />
                              </label>
                              <label style={{ color: '#475569', fontSize: 13, fontWeight: 600 }}>Primeira parcela
                                <input type="date" value={promissoriaAberta.primeiro_vencimento} onChange={e => setPromissoriaAberta(atual => ({ ...atual, primeiro_vencimento: e.target.value }))} style={{ display: 'block', width: '100%', marginTop: 5, padding: '10px 11px', border: '1px solid #cbd5e1', borderRadius: 7 }} />
                              </label>
                              <label style={{ color: '#475569', fontSize: 13, fontWeight: 600 }}>Quantidade de parcelas
                                <input type="number" min="1" max="120" value={promissoriaAberta.quantidade_parcelas} onChange={e => setPromissoriaAberta(atual => ({ ...atual, quantidade_parcelas: e.target.value }))} style={{ display: 'block', width: '100%', marginTop: 5, padding: '10px 11px', border: '1px solid #cbd5e1', borderRadius: 7 }} />
                              </label>
                            </div>
                            <button type="button" className="btn-primary" onClick={() => setPromissoriaAberta(atual => recalcularPromissoria({ ...atual, parcelas: [] }))} style={{ width: '100%', marginBottom: 16, padding: '10px 14px' }}>Gerar parcelamento</button>
                            {promissoriaAberta.parcelas.length > 0 && <div style={{ border: '1px solid #e2e8f0', borderRadius: 9, overflow: 'hidden' }}><div style={{ display: 'grid', gridTemplateColumns: '45px 1fr 1fr', gap: 8, padding: '9px 12px', background: '#f8fafc', color: '#64748b', fontSize: 12, fontWeight: 700 }}><span>#</span><span>Vencimento individual</span><span>Valor calculado</span></div>{promissoriaAberta.parcelas.map((parcela, i) => <div key={parcela.numero} style={{ display: 'grid', gridTemplateColumns: '45px 1fr 1fr', gap: 8, alignItems: 'center', padding: '8px 12px', borderTop: '1px solid #f1f5f9' }}><strong>{parcela.numero}</strong><input type="date" value={parcela.vencimento} onChange={e => alterarVencimentoPromissoria(i, e.target.value)} style={{ width: '100%', padding: 8, border: '1px solid #cbd5e1', borderRadius: 6 }} /><span style={{ color: '#1d4ed8', fontWeight: 700 }}>{parcela.valor || 'R$ 0,00'}</span></div>)}</div>}
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 18, gap: 12, flexWrap: 'wrap' }}><strong style={{ color: '#1e40af' }}>{promissoriaAberta.parcelas.length > 0 ? `Total: ${exibirMoeda(promissoriaAberta.parcelas.reduce((s, p) => s + parseMoeda(p.valor), 0))}` : 'Preencha os campos e gere o parcelamento.'}</strong><button type="button" className="btn-primary" disabled={!promissoriaAberta.parcelas.length} onClick={salvarPromissoria}>Salvar configuração</button></div>
                          </div>
                        </div>
                      )}

                      {/* PAGAMENTOS — só aparece se houver algo a pagar */}
                      {((saldo - totalRecebido + totalPago) < 0 || pagamentos.length > 0) && (
                        <div style={{ marginBottom: '2rem', paddingTop: '1.5rem', borderTop: '1px solid #f1f5f9' }}>
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
                            <h4 style={{ margin: 0, color: '#dc2626' }}>💸 Pagamentos</h4>
                            <button type="button" className="btn-primary"
                              style={{ padding: '6px 14px', fontSize: '0.8rem', background: '#dc2626', borderColor: '#dc2626' }}
                              onClick={() => setPagamentos(prev => [...prev, { id: Date.now(), tipo: 'PIX', valor: '', descricao: '' }])}>
                              + Adicionar
                            </button>
                          </div>
                          {pagamentos.length === 0 ? (
                            <div style={{ padding: '2rem', border: '2px dashed #fca5a5', borderRadius: '8px', textAlign: 'center', color: '#999', fontSize: '0.88rem' }}>
                              Nenhum pagamento adicionado
                            </div>
                          ) : (
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                              {pagamentos.map(p => (
                                <div key={p.id} style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '110px 1fr 36px', gap: '6px', alignItems: 'center' }}>
                                  <select value={p.tipo} onChange={(e) => setPagamentos(prev => prev.map(x => x.id === p.id ? { ...x, tipo: e.target.value } : x))}
                                    style={{ padding: '8px', border: '1px solid #e2e8f0', borderRadius: '6px', fontSize: '0.85rem' }}>
                                    <option>PIX</option>
                                    <option>TED</option>
                                    <option>Espécie (dinheiro)</option>
                                  </select>
                                  <input type="text" value={p.valor} placeholder="R$ 0,00"
                                    onChange={(e) => setPagamentos(prev => prev.map(x => x.id === p.id ? { ...x, valor: formatarMoeda(e.target.value) } : x))}
                                    style={{ padding: '8px', border: '1px solid #e2e8f0', borderRadius: '6px', fontSize: '0.85rem' }} />
                                  <button type="button" onClick={() => setPagamentos(prev => prev.filter(x => x.id !== p.id))}
                                    style={{ background: '#fee2e2', border: 'none', borderRadius: '6px', padding: '8px', cursor: 'pointer', color: '#dc2626', fontWeight: '700', width: isMobile ? '100%' : 'auto' }}>×</button>
                                </div>
                              ))}
                              <div style={{ textAlign: 'right', fontSize: '0.85rem', color: '#dc2626', fontWeight: '600', marginTop: '4px' }}>
                                Total: {exibirMoeda(totalPago)}
                              </div>
                            </div>
                          )}
                        </div>
                      )}

                      {/* Balanço final pago/recebido */}
                      {(pagamentos.length > 0 || recebimentos.length > 0) && (
                        <div style={{ marginTop: '2rem', padding: '1rem 1.5rem', background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: '10px', display: 'flex', justifyContent: 'space-between', alignItems: isMobile ? 'flex-start' : 'center', flexDirection: isMobile ? 'column' : 'row', gap: isMobile ? '8px' : 0 }}>
                          <span style={{ color: '#64748b', fontSize: '0.9rem' }}>Saldo da negociação após acerto:</span>
                          <span style={{ fontSize: '1.2rem', fontWeight: '700', color: (saldo - totalRecebido + totalPago) >= 0 ? '#1d4ed8' : '#dc2626' }}>
                            {exibirMoeda(Math.abs(saldo - totalRecebido + totalPago))}
                            {' '}
                            <span style={{ fontSize: '0.78rem', fontWeight: '400' }}>
                              {(saldo - totalRecebido + totalPago) >= 0 ? 'a receber' : 'a pagar'}
                            </span>
                          </span>
                        </div>
                      )}
                    </>
                  );
                })()}

              {currentSection === 5 && (
                  <div className="nw-review">
                    {[
                      { title: 'Cliente e negociação', step: 0, lines: [formData.nome, `CPF: ${formData.cpf} · ${formData.telefone}`, `RG: ${formData.rg} · ${formData.profissao} · ${formData.estadoCivil}`, [formData.rua, formData.numero, formData.bairro, formData.cidadeEstado].filter(Boolean).join(', '), `${formData.vendedor} · ${formData.loja}`, `Origem: ${formData.origemVenda || 'Não informada'}`] },
                      { title: 'Veículos de venda', step: 1, lines: veiculosVendidos.length ? veiculosVendidos.map(v => `${v.marca} ${v.modelo} · ${v.placa} · ${exibirMoeda(parseMoeda(v.valorVenda))}`) : ['Nenhum veículo de venda'] },
                      { title: 'Veículos de entrada', step: 2, lines: trocas.length ? trocas.map(v => `${v.modelo} · ${v.placa} · ${exibirMoeda(parseMoeda(v.valorTroca))}`) : ['Sem veículo de entrada'] },
                      { title: 'Adicionais', step: 3, lines: produtosAgregados.length ? produtosAgregados.map(p => `${p.nome || p.tipo} · ${exibirMoeda(parseMoeda(p.valor))}`) : ['Sem adicionais'] },
                      { title: 'Acerto financeiro', step: 4, lines: [`Recebimentos: ${exibirMoeda(recebimentos.reduce((total, r) => total + obterValorRecebimentoAcerto(r), 0))}`, `Pagamentos: ${exibirMoeda(pagamentos.reduce((total, p) => total + parseMoeda(p.valor), 0))}`, `Saldo do acerto: ${exibirMoeda(veiculosVendidos.reduce((total, v) => total + parseMoeda(v.valorVenda), 0) + produtosAgregados.reduce((total, p) => total + parseMoeda(p.valor), 0) - trocas.reduce((total, t) => total + parseMoeda(t.valorTroca) - parseMoeda(t.valorQuitacao) - parseMoeda(t.valorDebitos), 0) - recebimentos.reduce((total, r) => total + obterValorRecebimentoAcerto(r), 0) + pagamentos.reduce((total, p) => total + parseMoeda(p.valor), 0))} (positivo a receber; negativo a pagar)`] }
                    ].map(bloco => <section key={bloco.title}><header><h3>{bloco.title}</h3><button type="button" className="nw-text-button" onClick={() => { setCurrentSection(bloco.step); setParteCliente(0); setErros({}); }}>Editar<span className="nw-sr-only"> {bloco.title}</span></button></header>{bloco.lines.map((line, i) => <p key={i}>{line}</p>)}</section>)}
                    <p className="nw-note">Ao concluir, a negociação fica pronta para seguir com o checklist.</p>
                  </div>
                )}
                </div>
                </div>
              </div>
              <div className="modal-actions nw-actions">
                <button type="button" className="nw-text-button" onClick={handleClose} disabled={carregando}>Salvar e sair</button>
                <div className="nw-actions-right">
                  {(currentSection > 0 || parteCliente > 0) && <button type="button" className="btn-secondary" onClick={goToPreviousSection} disabled={carregando}>Voltar</button>}
                  {currentSection < sections.length - 1 ? <button type="button" className="btn-primary" onClick={goToNextSection} disabled={proximoBloqueado()}>{currentSection === 4 ? 'Revisar negociação' : 'Continuar'} <span aria-hidden="true">→</span></button> : <button type="submit" className="btn-primary" disabled={carregando}>{carregando ? 'Concluindo…' : 'Concluir negociação'}</button>}
                </div>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Sub-modal: detalhes do veículo */}
      {subModalAberto && (
        <div
          className="modal-overlay-preencher"
          style={{ zIndex: 3000, padding: 0 }}
        >
          <div
            className="modal-preencher-container nw-vehicle"
            style={{ width: '100%', height: '100%', maxWidth: '100%', maxHeight: '100%', borderRadius: 0 }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="modal-preencher-header">
              <h2>{subModalEditId ? 'Editar Veículo' : 'Adicionar Veículo Vendido'}</h2>
              <button className="modal-close-btn" onClick={fecharSubModal}>×</button>
            </div>

            <div className="preencher-form-new" style={{ maxHeight: 'none', flex: 1 }}>
              <div className="form-fields">

                {/* toggle manual / estoque */}
                <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '1rem' }}>
                  <button
                    type="button"
                    onClick={() => handleSubChange('_isManual', !subModalForm._isManual)}
                    style={{ background: 'none', border: 'none', color: '#3498db', textDecoration: 'underline', cursor: 'pointer', fontSize: '0.9rem' }}
                  >
                    {subModalForm._isManual ? 'Selecionar veículo do estoque' : 'Consultar pela placa / preencher dados'}
                  </button>
                </div>

                {/* busca estoque */}
                {!subModalForm._isManual && (
                  <div className="field-row field-row-full">
                    <div className="field-group" style={{ position: 'relative' }}>
                      <label>Buscar no Estoque</label>
                      <input
                        type="text"
                        value={subModalForm._busca}
                        onChange={(e) => handleSubChange('_busca', e.target.value)}
                        onFocus={() => handleSubChange('_mostrarOpcoes', true)}
                        onBlur={() => setTimeout(() => handleSubChange('_mostrarOpcoes', false), 200)}
                        placeholder="Digite marca, modelo, placa ou ano..."
                        autoComplete="off"
                      />
                      {subModalForm._mostrarOpcoes && filtrarVeiculosEstoque(subModalForm._busca).length > 0 && (
                        <ul style={{
                          position: 'absolute', top: '100%', left: 0, right: 0,
                          background: 'white', border: '1px solid #ddd', borderRadius: '4px',
                          maxHeight: '200px', overflowY: 'auto', zIndex: 1200,
                          listStyle: 'none', padding: 0, margin: 0,
                          boxShadow: '0 4px 6px rgba(0,0,0,0.1)'
                        }}>
                          {filtrarVeiculosEstoque(subModalForm._busca).slice(0, 10).map(est => (
                            <li
                              key={est._id}
                              onMouseDown={() => handleSelecionarEstoqueSubModal(est)}
                              style={{ padding: '10px', cursor: 'pointer', borderBottom: '1px solid #eee' }}
                              onMouseOver={(e) => e.currentTarget.style.background = '#f5f5f5'}
                              onMouseOut={(e) => e.currentTarget.style.background = 'white'}
                            >
                              <div style={{ fontWeight: '600', display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                                <span>{est.marca} {est.modelo} - {est.ano_fabricacao && est.ano_modelo ? `${est.ano_fabricacao}/${est.ano_modelo}` : (est.ano_modelo || est.ano_fabricacao)}</span>
                                {est.tipo && est.tipo.includes('Repasse') && (
                                  <span style={{ background: '#ff9800', color: 'white', padding: '2px 8px', borderRadius: '12px', fontSize: '0.75rem' }}>{est.tipo}</span>
                                )}
                              </div>
                              <div style={{ fontSize: '0.85rem', color: '#666', marginTop: '4px' }}>Placa: {est.placa} | Cor: {est.cor}</div>
                            </li>
                          ))}
                        </ul>
                      )}
                    </div>
                  </div>
                )}

                <div className="field-row">
                  <div className="field-group">
                    <label>Placa <span className="required">*</span></label>
                    <div style={{ display: 'flex', gap: 8 }}>
                    <input type="text" value={subModalForm.placa} onChange={(e) => handleSubChange('placa', e.target.value)}
                      placeholder="ABC1D23" readOnly={!subModalForm._isManual}
                      style={!subModalForm._isManual ? { backgroundColor: '#f5f5f5' } : {}} />
                      <button type="button" aria-label="Consultar placa do veículo de venda"
                        onClick={() => buscarDadosPorPlaca('venda')}
                        disabled={Boolean(consultaPlacaStatus.venda?.carregando) || !subModalForm._isManual}
                        style={{ padding: '0 14px', border: 'none', borderRadius: 6, background: '#3498db', color: '#fff', cursor: 'pointer' }}>
                        {consultaPlacaStatus.venda?.carregando ? 'Buscando…' : 'Buscar dados'}
                      </button>
                    </div>
                    {consultaPlacaStatus.venda?.mensagem && <span role="status" style={{ display: 'block', marginTop: 8 }}>{consultaPlacaStatus.venda.mensagem}</span>}
                  </div>
                  <div className="field-group">
                    <label>Quilometragem <span className="required">*</span></label>
                    <input type="text" value={subModalForm.km} onChange={(e) => handleSubChange('km', e.target.value)}
                      placeholder="Ex: 50.000" />
                  </div>
                </div>

                <div className="field-row">
                  <div className="field-group">
                    <label>Marca <span className="required">*</span></label>
                    <input type="text" value={subModalForm.marca} onChange={(e) => handleSubChange('marca', e.target.value)}
                      placeholder="Ex: Volkswagen" readOnly={!subModalForm._isManual}
                      style={!subModalForm._isManual ? { backgroundColor: '#f5f5f5' } : {}} />
                  </div>
                  <div className="field-group">
                    <label>Modelo <span className="required">*</span></label>
                    <input type="text" value={subModalForm.modelo} onChange={(e) => handleSubChange('modelo', e.target.value)}
                      placeholder="Ex: Gol 1.0" readOnly={!subModalForm._isManual}
                      style={!subModalForm._isManual ? { backgroundColor: '#f5f5f5' } : {}} />
                  </div>
                </div>

                <div className="field-row">
                  <div className="field-group">
                    <label>Ano Fab/Modelo <span className="required">*</span></label>
                    <input type="text" value={subModalForm.ano} onChange={(e) => handleSubChange('ano', e.target.value)}
                      placeholder="Ex: 2020/2021" readOnly={!subModalForm._isManual}
                      style={!subModalForm._isManual ? { backgroundColor: '#f5f5f5' } : {}} />
                  </div>
                  <div className="field-group">
                    <label>Cor <span className="required">*</span></label>
                    <input type="text" value={subModalForm.cor} onChange={(e) => handleSubChange('cor', e.target.value)}
                      placeholder="Ex: Prata" readOnly={!subModalForm._isManual}
                      style={!subModalForm._isManual ? { backgroundColor: '#f5f5f5' } : {}} />
                  </div>
                </div>

                <div className="field-row" style={{ marginTop: '0.5rem', paddingTop: '1rem', borderTop: '1px solid #eee' }}>
                  <div className="field-group">
                    <label>Tem Manual? <span className="required">*</span></label>
                    <div style={{ display: 'flex', gap: '2rem', marginTop: '0.5rem' }}>
                      {['Sim', 'Não'].map(op => (
                        <label key={op} style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer' }}>
                          <input type="radio" name="sub_manual" value={op}
                            checked={subModalForm.temManual === op}
                            onChange={() => handleSubChange('temManual', op)} />
                          <span>{op}</span>
                        </label>
                      ))}
                    </div>
                  </div>
                  <div className="field-group">
                    <label>Tem Chave Reserva? <span className="required">*</span></label>
                    <div style={{ display: 'flex', gap: '2rem', marginTop: '0.5rem' }}>
                      {['Sim', 'Não'].map(op => (
                        <label key={op} style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer' }}>
                          <input type="radio" name="sub_chave" value={op}
                            checked={subModalForm.temChaveReserva === op}
                            onChange={() => handleSubChange('temChaveReserva', op)} />
                          <span>{op}</span>
                        </label>
                      ))}
                    </div>
                  </div>
                </div>

              </div>

              <div className="modal-actions" style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px' }}>
                <button type="button" className="btn-secondary" onClick={fecharSubModal}>Cancelar</button>
                <button
                  type="button"
                  className="btn-primary"
                  onClick={salvarSubModal}
                  disabled={!subModalValido()}
                  style={!subModalValido() ? { opacity: 0.4, cursor: 'not-allowed' } : {}}
                >
                  {subModalEditId ? 'Salvar alterações' : 'Adicionar veículo'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      {/* Sub-modal: veículo na troca */}
      {subModalTrocaAberto && (
        <div className="modal-overlay-preencher" style={{ zIndex: 3000, padding: 0 }}>
          <div className="modal-preencher-container nw-vehicle" style={{ width: '100%', height: '100%', maxWidth: '100%', maxHeight: '100%', borderRadius: 0 }} onClick={(e) => e.stopPropagation()}>
            <div className="modal-preencher-header">
              <h2>{subModalTrocaEditId ? 'Editar Veículo Entrando no Estoque' : 'Adicionar Veículo Entrando no Estoque'}</h2>
              <button className="modal-close-btn" onClick={fecharSubModalTroca}>×</button>
            </div>

            <div className="preencher-form-new" style={{ maxHeight: 'none', flex: 1 }}>
              <div className="form-fields">

                <div className="field-row">
                  <div className="field-group">
                    <label>Placa <span className="required">*</span></label>
                    <div style={{ display: 'flex', gap: 8 }}>
                    <input type="text" value={subModalTrocaForm.placa}
                      onChange={(e) => handleSubTrocaChange('placa', e.target.value)}
                      placeholder="ABC1D23" />
                      <button type="button" aria-label="Consultar placa do veículo de troca"
                        onClick={() => buscarDadosPorPlaca('troca')}
                        disabled={Boolean(consultaPlacaStatus.troca?.carregando)}
                        style={{ padding: '0 14px', border: 'none', borderRadius: 6, background: '#3498db', color: '#fff', cursor: 'pointer' }}>
                        {consultaPlacaStatus.troca?.carregando ? 'Buscando…' : 'Buscar dados'}
                      </button>
                    </div>
                    {consultaPlacaStatus.troca?.mensagem && <span role="status" style={{ display: 'block', marginTop: 8 }}>{consultaPlacaStatus.troca.mensagem}</span>}
                  </div>
                  <div className="field-group">
                    <label>Marca / modelo <span className="required">*</span></label>
                    <input type="text" value={subModalTrocaForm.modelo}
                      onChange={(e) => handleSubTrocaChange('modelo', e.target.value)}
                      placeholder="Ex: Ford Ka 1.0" />
                  </div>
                </div>

                <div className="field-row">
                  <div className="field-group">
                    <label>Cor</label>
                    <input type="text" value={subModalTrocaForm.cor}
                      onChange={(e) => handleSubTrocaChange('cor', e.target.value)}
                      placeholder="Ex: Prata" />
                  </div>
                  <div className="field-group">
                    <label>Quilometragem</label>
                    <input type="text" value={subModalTrocaForm.km}
                      onChange={(e) => handleSubTrocaChange('km', e.target.value)}
                      placeholder="Ex: 50.000" />
                  </div>
                </div>

                <div className="field-row">
                  <div className="field-group">
                    <label>Ano Fabricação <span className="required">*</span></label>
                    <input type="text" value={subModalTrocaForm.anoFabricacao}
                      onChange={(e) => handleSubTrocaChange('anoFabricacao', e.target.value)}
                      placeholder="Ex: 2020" />
                  </div>
                  <div className="field-group">
                    <label>Ano Modelo <span className="required">*</span></label>
                    <input type="text" value={subModalTrocaForm.anoModelo}
                      onChange={(e) => handleSubTrocaChange('anoModelo', e.target.value)}
                      placeholder="Ex: 2021" />
                  </div>
                </div>

                <div className="field-row">
                  <div className="field-group">
                    <label>Renavam</label>
                    <input type="text" value={subModalTrocaForm.renavam}
                      onChange={(e) => handleSubTrocaChange('renavam', e.target.value)}
                      placeholder="00000000000" />
                  </div>
                  <div className="field-group">
                    <label>Chassis</label>
                    <input type="text" value={subModalTrocaForm.chassis}
                      onChange={(e) => handleSubTrocaChange('chassis', e.target.value)}
                      placeholder="17 caracteres" />
                  </div>
                </div>

                <div className="field-row" style={{ paddingTop: '1rem', borderTop: '1px solid #eee' }}>
                  <div className="field-group">
                    <label>Manual presente? <span className="required">*</span></label>
                    <div style={{ display: 'flex', gap: '2rem', marginTop: '0.5rem' }}>
                      {['Sim', 'Não'].map(op => (
                        <label key={op} style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer' }}>
                          <input type="radio" name="troca_manual" value={op}
                            checked={subModalTrocaForm.temManual === op}
                            onChange={() => handleSubTrocaChange('temManual', op)} />
                          <span>{op}</span>
                        </label>
                      ))}
                    </div>
                  </div>
                  <div className="field-group">
                    <label>Chave reserva presente? <span className="required">*</span></label>
                    <div style={{ display: 'flex', gap: '2rem', marginTop: '0.5rem' }}>
                      {['Sim', 'Não'].map(op => (
                        <label key={op} style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer' }}>
                          <input type="radio" name="troca_chave" value={op}
                            checked={subModalTrocaForm.temChaveReserva === op}
                            onChange={() => handleSubTrocaChange('temChaveReserva', op)} />
                          <span>{op}</span>
                        </label>
                      ))}
                    </div>
                  </div>
                </div>

                <div className="field-row">
                  <div className="field-group">
                    <label>Quitação</label>
                    <input type="text" value={subModalTrocaForm.valorQuitacao}
                      onChange={(e) => handleSubTrocaChange('valorQuitacao', formatarMoeda(e.target.value))}
                      placeholder="R$ 0,00" />
                  </div>
                  <div className="field-group">
                    <label>Instituição financiadora da quitação</label>
                    <input type="text" value={subModalTrocaForm.instituicaoQuitacao || ''}
                      onChange={(e) => handleSubTrocaChange('instituicaoQuitacao', e.target.value)}
                      placeholder="Ex: Santander, BV, Itaú" />
                  </div>
                  <div className="field-group">
                    <label>Débitos</label>
                    <input type="text" value={subModalTrocaForm.valorDebitos}
                      onChange={(e) => handleSubTrocaChange('valorDebitos', formatarMoeda(e.target.value))}
                      placeholder="R$ 0,00" />
                  </div>
                </div>

              </div>

              <div className="modal-actions" style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px' }}>
                <button type="button" className="btn-secondary" onClick={fecharSubModalTroca}>Cancelar</button>
                <button
                  type="button"
                  className="btn-primary"
                  onClick={salvarSubModalTroca}
                  disabled={!subModalTrocaValido()}
                  style={!subModalTrocaValido() ? { opacity: 0.4, cursor: 'not-allowed' } : {}}
                >
                  {subModalTrocaEditId ? 'Salvar alterações' : 'Adicionar veículo'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {impressaoModalNeg && (
        <div className="modal-overlay-preencher" onClick={() => { if (!gerandoImpressao) setImpressaoModalNeg(null); }} style={{ zIndex: 3200 }}>
          <div className="modal-preencher-container" role="dialog" aria-modal="true" aria-labelledby="titulo-impressao-negociacao" onClick={e => e.stopPropagation()} style={{ width: 'min(520px, calc(100vw - 28px))', maxWidth: '520px', maxHeight: '90vh', overflowY: 'auto', borderRadius: '16px' }}>
            <div className="modal-preencher-header">
              <div><h2 id="titulo-impressao-negociacao">Imprimir negociação</h2><p style={{ margin: '4px 0 0', color: '#64748b', fontSize: '0.82rem' }}>Marque os documentos que deseja reunir em um único PDF.</p></div>
              <button className="modal-close-btn" disabled={gerandoImpressao} onClick={() => setImpressaoModalNeg(null)} aria-label="Fechar">×</button>
            </div>
            <div style={{ padding: '22px', display: 'grid', gap: '10px' }}>
              <div style={{ display: 'flex', gap: '12px' }}>
                <button type="button" disabled={gerandoImpressao} onClick={() => setDocumentosImpressao(DOCUMENTOS_IMPRESSAO.map(documento => documento.id))} className="btn-secondary">Marcar todos</button>
                <button type="button" disabled={gerandoImpressao || !documentosImpressao.length} onClick={() => setDocumentosImpressao([])} className="btn-secondary">Limpar seleção</button>
              </div>
              {DOCUMENTOS_IMPRESSAO.map(documento => (
                <label key={documento.id} style={{ display: 'flex', alignItems: 'center', gap: '12px', padding: '14px 15px', border: '1px solid #e2e8f0', borderRadius: '10px', background: documentosImpressao.includes(documento.id) ? '#eff6ff' : '#fff', color: '#1e293b', cursor: gerandoImpressao ? 'wait' : 'pointer', fontSize: '0.9rem', fontWeight: 600 }}>
                  <input type="checkbox" disabled={gerandoImpressao} checked={documentosImpressao.includes(documento.id)} onChange={e => {
                    const marcado = e.target.checked;
                    setDocumentosImpressao(atual => marcado ? [...atual, documento.id] : atual.filter(id => id !== documento.id));
                    setErroImpressao('');
                  }} style={{ width: '18px', height: '18px', flexShrink: 0, accentColor: '#2563eb' }} />
                  {documento.nome}
                </label>
              ))}
              {erroImpressao && <div role="alert" style={{ padding: '12px', borderRadius: '8px', background: '#fee2e2', color: '#991b1b', fontSize: '0.85rem' }}>{erroImpressao}</div>}
              <div style={{ color: '#64748b', fontSize: '0.8rem' }}>{documentosImpressao.length} documento(s) selecionado(s). A ordem do PDF segue a lista acima.</div>
              <button type="button" className="btn-primary" disabled={gerandoImpressao || !documentosImpressao.length} onClick={imprimirDocumentosSelecionados} style={{ width: '100%', padding: '13px', opacity: gerandoImpressao || !documentosImpressao.length ? 0.6 : 1 }}>
                {gerandoImpressao ? 'Gerando PDF...' : 'Gerar PDF dos selecionados'}
              </button>
            </div>
          </div>
        </div>
      )}

      {checklistModalNeg && (
        <ChecklistModal
          neg={checklistModalNeg}
          onChecklistUpdated={carregarNegociacoes}
          onClose={async () => {
            await carregarNegociacoes();
            setChecklistModalNeg(null);
          }}
        />
      )}
    </div>
  );
};

export default Negociacoes;
